/* ---- Reload-safety for pinned ScrollTriggers — EVERY PAGE ----
   The browser would otherwise restore the previous scroll position before the layout and the pins have
   settled, and ScrollTrigger would measure against that unsettled page → sections overlap (near
   home-s6, or on the platform-s1 deck at <=600). Taking over restoration and starting at the top gives
   every pin a clean layout to measure.

   Note what this does NOT do any more: it used to repeat the reset on `load`, which on a slow first
   visit fired long after the visitor had scrolled a third of the way down and threw them back to the
   top. The mis-measurement it was papering over is now fixed where it belongs — each pin re-measures
   itself once it exists (see createPin) — so forcing the viewport is no longer load-bearing.
   Safe site-wide because no link relies on the browser's own jump: cross-page deep links arrive as
   ?section= and do their own scrolling after the layout settles (module below). */
if ("scrollRestoration" in history) history.scrollRestoration = "manual";
window.scrollTo(0, 0);

/* ---- Fonts-ready gate, for ENTRANCE ANIMATIONS ONLY ----
   Every intro waits for the web font before it runs, so SplitText measures real glyph widths. It used
   to wait on document.fonts.ready, which Safari leaves pending long after the file is actually usable
   — hence "the first load crawls, a reload is fine" (by then Poppins is cached) and "only on Safari"
   (other engines settle that promise on time).

   So ask about the FACES instead: fonts.load() reports on the specific ones the intros measure
   against, and Safari answers that honestly. fonts.ready stays as a fallback, and a deadline caps the
   wait no matter what. Whichever signal lands first wins.

   Deliberately NOT used by anything that MEASURES the layout — createPin, the two card decks and the
   global ScrollTrigger.refresh still wait on the real promise. Measuring against fallback-font metrics
   is what made home-s6/home-s7 overlap the last time this gate went in everywhere; an intro firing on
   stale metrics only risks a different line wrap, and the refresh on `load` fixes its trigger anyway. */
window.rooyaFontsReady = function (cb) {
  var fired = false;
  var go = function () {
    if (fired) return;
    fired = true;
    // One frame before measuring. fonts.load() resolves when the face is DOWNLOADED, which is not yet
    // the same as the browser having relaid the text with it — split on the same tick and SplitText
    // can still measure fallback widths.
    requestAnimationFrame(cb);
  };

  if (!document.fonts) { go(); return; }

  // EVERY weight the theme declares, not a guessed subset. The hero paragraph is set in 300, so asking
  // about 400/600 alone reported "ready" while the face that paragraph actually renders in was still
  // downloading — SplitText then measured the fallback and froze its wrap. That was browser-agnostic,
  // which is why it showed up on Firefox too.
  if (document.fonts.load) {
    var weights = ["300", "400", "500", "600", "700"];
    Promise.all(weights.map(function (w) {
      return document.fonts.load(w + " 1em Poppins");
    })).then(function (sets) {
      // `every`, not `some`: an empty result means no matching face was found rather than "loaded", and
      // firing on a partial answer is exactly the bug above. A miss here just leaves it to the signals
      // below, which is safe.
      var loaded = sets.every(function (faces) { return faces && faces.length; });
      if (loaded) go();
    }, function () {});
  }

  if (document.fonts.ready) document.fonts.ready.then(go);

  // Backstop only, and deliberately generous. fonts.load() above is what actually cures the Safari
  // delay, so this timer exists for a genuine hang — nothing else. Set short it PRE-EMPTS the honest
  // signal on a first visit, SplitText then measures the fallback, and because type:"lines" freezes
  // the wrap into per-line elements the paragraph keeps those wrong breaks after the font swaps in.
  setTimeout(go, 3000);
};

/* Runs `cb` once a section's SECTIONS-driver intro has visually landed. Sections whose lower half is
   too far down the page to share the head's trigger get split onto a second trigger of their own; on
   a screen tall enough to fire both at once, that second half would otherwise race the first. This is
   the handshake between them.

   Not the timeline's onComplete: power4.out has everything settled well before the tween formally
   runs out, and waiting for the real end reads as dead air. So the callback is dropped INTO the
   intro a little short of its end — the same overlap the driver itself uses when handing over from
   copy to composition. A section with no intro (or a caller running before the driver has built one)
   just fires straight away, so nothing can hang waiting for a timeline that will never exist.
   Call this from inside rooyaFontsReady, which is when the driver publishes _introTl.

   `at` names an absolute position on the intro's timeline instead, for a second half that only has
   to clear the copy rather than follow all of it. */
/* Releases a wrapper that CSS held hidden through the first paint (see .has-js in header.php).
   Call it once the module has inline-hidden the pieces it is about to animate — from then on those
   pieces carry their own visibility and the wrapper can safely be shown. Call it straight away when
   bailing out instead, so nothing is ever left permanently invisible. */
window.rooyaRelease = function (el) { if (el) el.style.visibility = "visible"; };

/* Runs `fn` with the page parked at the very top, then puts the scroll straight back. Both hops and
   `fn` itself happen inside ONE task, so the browser never paints in between — nothing is seen to move.

   Why every layout measurement wants this: a ScrollTrigger start is a position in the DOCUMENT, and
   the document only looks like itself when nothing is pinned. Refresh (or rebuild a card deck) while
   the visitor is sitting inside a pinned range and the numbers are taken against a layout that is
   holding a section fixed — the deck then sets up a few hundred px off its real place and the cards
   end up overlapping the section above. Parking at 0 first releases every pin, which is the state the
   pins were tuned against and the only one where the start lands exactly right.

   This is the old "reset the scroll again on `load`" from the top of this file, minus its one real
   flaw: that version measured just as safely but never scrolled BACK, so a slow first visit yanked
   the reader to the top a third of the way down the page. */
window.rooyaAtTop = function (fn) {
  // Phones only. The mis-measurement this cures belongs to the two stacked card decks, and both of
  // those exist ONLY at <=600 — above it there is nothing to protect, so the hop (cheap, but it does
  // force a reflow and briefly lies to every scroll listener) simply doesn't run.
  if (!(window.matchMedia && window.matchMedia("(max-width: 600px)").matches)) { fn(); return; }

  var y = window.pageYOffset || document.documentElement.scrollTop || 0;
  if (y < 1) { fn(); return; } // already there — no hop, nothing to restore

  // html { scroll-behavior: smooth } (default.scss) would ANIMATE both hops, turning an invisible
  // measurement into a very visible round trip. Force it off for the duration, then hand it back.
  var root = document.documentElement;
  var prev = root.style.scrollBehavior;
  root.style.scrollBehavior = "auto";

  window.scrollTo(0, 0);
  try {
    fn();
  } finally {
    window.scrollTo(0, y);
    root.style.scrollBehavior = prev;
    // ScrollTrigger cached scrollY 0 while we were up there. Tell it where the page actually is, so
    // the pins re-engage at the right progress now rather than on the next scroll event.
    if (window.ScrollTrigger && ScrollTrigger.update) ScrollTrigger.update();
  }
};

/* Puts a ScrollTrigger.batch into visual top-to-bottom order before it is staggered. The array batch
   hands over is in the order ScrollTrigger happened to detect the elements, which is NOT the order
   they sit on the page: scroll into a grid quickly, or have some of it already on screen when the
   batch is built, and a lower row can be detected first and therefore lead the stagger. Sorting by
   the live bounding rect makes the cascade run down the page whatever the entry order was. Sort is
   stable, so items on the same line keep their DOM order — left to right. */
window.rooyaTopDown = function (batch) {
  return batch.slice().sort(function (a, b) {
    return a.getBoundingClientRect().top - b.getBoundingClientRect().top;
  });
};

window.rooyaAfterIntro = function (section, cb, at) {
  var tl = section && section._introTl;
  if (!tl) { cb(); return; }
  var when = (typeof at === "number") ? at : Math.max(0, tl.duration() - 0.6);
  if (tl.time() >= when) cb();
  else tl.call(cb, null, when);
};

/* ---- Cross-page deep links: /platform/?section=pf-security ----
   The footer links straight into sections of the inner pages. A real #hash cannot be used for that:
   the browser jumps to the target during parse, BEFORE images/fonts settle and before ScrollTrigger
   has built its pin spacers, so every pinned block (platform-s1 deck <=600, home-s6/home-s7) sets up
   against a mid-scroll layout and the sections end up overlapping — the exact bug the homepage
   reload-safety above exists to avoid.
   So the links carry ?section=<id> instead. The page always loads at the top, where the layout is
   clean and the pins initialise correctly, and we scroll only once `load` + fonts + a final
   ScrollTrigger.refresh() have settled the document height. The param deliberately STAYS in the URL:
   a reload just replays this same safe path, and the link stays shareable. */
(function () {
  if (!window.URLSearchParams) return;

  var id = new URLSearchParams(window.location.search).get("section");
  if (!id || !/^[\w-]+$/.test(id)) return; // ignore anything that isn't a plain id

  var el = document.getElementById(id);
  if (!el) return;

  // A target that only makes sense stacked (an office card on /contact/, 3 Nov 2026): above 900 the
  // cards sit side by side, so the whole section it names in data-section-wide is the better landing.
  var wide = el.getAttribute("data-section-wide");
  if (wide && window.matchMedia("(min-width: 901px)").matches && document.getElementById(wide)) {
    el = document.getElementById(wide);
  }

  // Hold the top until the layout is final — including on reload, which would otherwise restore
  // mid-page and re-create the very pin bug this indirection avoids.
  if ("scrollRestoration" in history) history.scrollRestoration = "manual";
  window.scrollTo(0, 0);

  // Where the element wants to land, read from its own scroll-margin-top so the offset below the
  // sticky navbar lives in ONE place — the CSS (see default.scss / industries.scss).
  function targetTop() {
    var margin = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
    return window.pageYOffset + el.getBoundingClientRect().top - margin;
  }

  /* Jump, with no animation whatever the CSS says. `html { scroll-behavior: smooth }` (default.scss)
     applies to programmatic scrolls too, so an ordinary scrollTo would animate — which is wrong both
     for the per-frame writes of the tween below and for the instant correction at the end. */
  function jumpTo(y) {
    var root = document.documentElement;
    var prev = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    window.scrollTo(0, y);
    root.style.scrollBehavior = prev;
  }

  /* The travel is animated HERE rather than handed to `behavior: "smooth"`.
     That property says what to do, never how long to take, and the engines disagree by a lot: Blink
     scales the duration with the distance and lands somewhere near a second on a long page, while
     WebKit uses its own much shorter curve. Same markup, same link — on Safari the page simply
     arrived, with no sense of having travelled. Owning the tween is the only way the movement reads
     the same everywhere, and it costs nothing else: this is a plain rAF loop over window.scrollTo. */
  function smoothTo(y) {
    var start = window.pageYOffset;
    var dist = y - start;
    if (Math.abs(dist) < 2) { correctOnSettle(); return; }

    // Long trips take longer, but not proportionally — a whole-page scroll would be tiresome at a
    // constant speed. Clamped at both ends so short hops are not abrupt and long ones do not drag.
    var dur = Math.min(1100, Math.max(450, Math.abs(dist) * 0.45));
    var t0 = null, cancelled = false;

    // Any real input wins immediately. A forced scroll that keeps going while the reader is trying
    // to steer is worse than one that stops short.
    var stop = function () { cancelled = true; };
    var opts = { passive: true };
    window.addEventListener("wheel", stop, opts);
    window.addEventListener("touchstart", stop, opts);
    window.addEventListener("keydown", stop, opts);
    var release = function () {
      window.removeEventListener("wheel", stop, opts);
      window.removeEventListener("touchstart", stop, opts);
      window.removeEventListener("keydown", stop, opts);
    };

    var ease = function (p) {  // inOutCubic — leaves and arrives gently, quick through the middle
      return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
    };

    var step = function (ts) {
      if (cancelled) { release(); return; }
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      jumpTo(start + dist * ease(p));
      if (p < 1) { requestAnimationFrame(step); return; }
      release();
      correctOnSettle();
    };
    requestAnimationFrame(step);
  }

  // The trip passes THROUGH the pinned sections, and a pin spacer that resizes on the way moves the
  // target out from under an offset already measured in absolute px. So re-measure once everything
  // has stopped and close whatever gap is left, instantly.
  function correctOnSettle() {
    var last = -1, idle = 0, tries = 0;
    var timer = setInterval(function () {
      var y = window.pageYOffset;
      idle = Math.abs(y - last) < 1 ? idle + 1 : 0;
      last = y;
      if (idle < 2 && ++tries < 40) return; // still moving, and under the ~4s ceiling

      clearInterval(timer);
      var want = targetTop();
      if (Math.abs(window.pageYOffset - want) > 2) jumpTo(want);
    }, 100);
  }

  window.addEventListener("load", function () {
    var fonts = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();

    fonts.then(function () {
      // 250ms clears the debounced rooyaRefreshST (200ms) so our refresh is the last one to run and
      // the spacers we measure against are the ones the page keeps.
      setTimeout(function () {
        if (window.ScrollTrigger && ScrollTrigger.refresh) ScrollTrigger.refresh();

        requestAnimationFrame(function () {
          smoothTo(targetTop());
        });
      }, 250);
    });
  });
})();

(function () {
  "use strict";

  /* ---- Sticky nav: always on screen, shadow once the page has moved ----
     The nav used to slide away on a downward scroll and come back on an upward one; the client wants
     it pinned in both directions, so the only thing scroll position still drives is the shadow. */
  var nav = document.querySelector(".nav");
  if (nav) {
    var ticking = false;
    var stuck = null;
    var onScroll = function () {
      var next = window.pageYOffset > 8;
      // only on the change, not once per scroll event as it used to be
      if (next !== stuck) {
        stuck = next;
        nav.classList.toggle("nav--stuck", next);
        // Deliberately no ScrollTrigger refresh here. The stuck bar gives back in margin exactly what
        // it takes off its height (see navbar.scss), so the document never changes length and there is
        // nothing to recompute — and refreshing mid-scroll is itself what disturbs the pinned sections.
      }
      ticking = false;
    };
    window.addEventListener("scroll", function () {
      if (!ticking) { window.requestAnimationFrame(onScroll); ticking = true; }
    }, { passive: true });
  }

  /* ---- Mobile menu (hamburger morphs into X, whole menu slides in) ----
     Same query as $nav-mobile in src/scss/_nav-bp.scss — keep the two identical: below 1200px, and on
     touch-only screens up to 1400px (tablets in landscape). */
  var navMobileMQ = window.matchMedia("screen and (max-width: 1199px), screen and (hover: none) and (pointer: coarse) and (max-width: 1400px)");
  var toggle = document.querySelector(".nav-toggle");
  var menu = document.querySelector(".nav-menu");
  if (toggle && menu) {
    // The open panel scrolls inside itself down to the window's bottom edge (navbar.scss), and the page
    // behind is locked while it is open. Re-measured on resize, so turning the device keeps it right.
    var fitMenu = function () {
      if (!menu.classList.contains("is-open")) return;
      var top = menu.getBoundingClientRect().top;
      menu.style.setProperty("--nav-menu-max-h", Math.max(200, Math.floor(window.innerHeight - top)) + "px");
    };
    var setMenu = function (open) {
      toggle.classList.toggle("is-open", open);
      menu.classList.toggle("is-open", open);
      toggle.setAttribute("aria-expanded", open);
      document.documentElement.classList.toggle("nav-menu-open", open);
      if (open) { menu.scrollTop = 0; fitMenu(); }
    };
    toggle.addEventListener("click", function () {
      setMenu(!menu.classList.contains("is-open"));
    });
    window.addEventListener("resize", fitMenu);
    // Crossing into the desktop layout (rotating a tablet, widening a window) closes the menu, so the
    // page doesn't stay locked behind a panel that is no longer shown.
    navMobileMQ.addEventListener("change", function (mq) {
      if (!mq.matches) setMenu(false);
    });
    // close after tapping any link/button (but keep it open for the EN/AR switch)
    menu.addEventListener("click", function (e) {
      // (…nor for a megamenu link, which only unfolds its section here — see "Megamenu" below)
      if (e.target.closest("a") && !e.target.closest(".lang-switch") && !e.target.closest("a[data-mega]")) {
        setMenu(false);
      }
    });
  }

  /* ---- Megamenu (desktop only) ----
     A nav link with data-mega owns the panel #mega-<slug> printed after the nav (header.php). Hover
     opens it with a short intent delay and closes it with a grace period, so the pointer can travel
     from the link down into the panel; keyboard focus opens it too, Escape closes it. On touch the
     first tap opens the panel and a second tap follows the link. The link always stays a real link —
     with JS off, or in the hamburger menu (navMobileMQ) where there are no panels, it simply navigates. */
  var megaLinks = Array.prototype.slice.call(document.querySelectorAll(".nav-links a[data-mega]"));
  // Desktop = not the hamburger layout (navMobileMQ, "Mobile menu" above).
  var megaDesktop = { get matches() { return !navMobileMQ.matches; } };
  if (megaLinks.length) {
    var megaOpen = null, megaOpenTimer = null, megaCloseTimer = null, megaTouch = false;

    var megaPanelFor = function (link) {
      return document.getElementById("mega-" + link.getAttribute("data-mega"));
    };

    /* Row highlight inside a panel: exactly one .mega__link carries .is-current — the row the pointer
       or focus last reached. It stays put while the pointer crosses gaps or leaves the list, which a
       :hover-driven highlight could not do (it flicked back to the first row and returned). */
    var megaLight = function (row) {
      if (!row || row.classList.contains("is-current")) return;
      var list = row.closest(".mega__links");
      var lit = list && list.querySelector(".mega__link.is-current");
      if (lit) lit.classList.remove("is-current");
      row.classList.add("is-current");
    };
    var megaResetLight = function (scope) {
      megaLight(scope && scope.querySelector(".mega__link"));
    };
    Array.prototype.forEach.call(document.querySelectorAll(".mega__links"), function (list) {
      var pick = function (e) { megaLight(e.target.closest && e.target.closest(".mega__link")); };
      list.addEventListener("mouseover", pick);
      list.addEventListener("focusin", pick);
    });

    // Caret sits under the centre of the link, measured against the panel box.
    // Also measures the room left below the panel, so a panel taller than the window scrolls inside
    // itself (--mega-max-h, megamenu.scss) instead of running off the bottom of the screen. Both are
    // set on .mega, the caret's own box since the panel itself became the scroll box.
    var megaPlaceCaret = function (link) {
      var panel = megaPanelFor(link);
      var box = panel && panel.querySelector(".mega__panel");
      if (!box) return;
      var l = link.getBoundingClientRect(), b = box.getBoundingClientRect();
      // Centre on the LABEL, not the whole link: the link also holds the chevron, and centring on
      // both put the caret visibly off the word towards the arrow.
      var label = link.firstChild;
      if (label && label.nodeType === 3 && document.createRange) {
        var range = document.createRange();
        range.selectNodeContents(label);
        var lr = range.getBoundingClientRect();
        if (lr.width) l = lr;
      }
      panel.style.setProperty("--caret-x", Math.round(l.left + l.width / 2 - b.left) + "px");
      panel.style.setProperty("--mega-max-h", Math.max(240, Math.floor(window.innerHeight - b.top - 16)) + "px");
    };

    var megaClose = function () {
      clearTimeout(megaOpenTimer);
      clearTimeout(megaCloseTimer);
      if (!megaOpen) return;
      var panel = megaPanelFor(megaOpen);
      if (panel) panel.classList.remove("is-open");
      megaOpen.setAttribute("aria-expanded", "false");
      megaOpen = null;
    };

    var megaShow = function (link) {
      clearTimeout(megaOpenTimer);
      clearTimeout(megaCloseTimer);
      if (megaOpen === link || !megaDesktop.matches) return;
      megaClose();
      var panel = megaPanelFor(link);
      if (!panel) return;
      megaPlaceCaret(link);
      megaResetLight(panel);
      panel.classList.add("is-open");
      link.setAttribute("aria-expanded", "true");
      megaOpen = link;
    };

    var megaHoldOpen = function () { clearTimeout(megaCloseTimer); };
    var megaCloseSoon = function () {
      clearTimeout(megaOpenTimer);
      clearTimeout(megaCloseTimer);
      megaCloseTimer = setTimeout(megaClose, 220);
    };

    megaLinks.forEach(function (link) {
      var panel = megaPanelFor(link);

      link.addEventListener("pointerdown", function (e) { megaTouch = e.pointerType !== "mouse"; });

      link.addEventListener("mouseenter", function () {
        clearTimeout(megaCloseTimer);
        clearTimeout(megaOpenTimer);
        // Already showing another panel: swap straight away, the reader is browsing the menu.
        megaOpenTimer = setTimeout(function () { megaShow(link); }, megaOpen ? 0 : 90);
      });
      link.addEventListener("mouseleave", megaCloseSoon);
      link.addEventListener("focus", function () { megaShow(link); });

      link.addEventListener("click", function (e) {
        // Hamburger menu: the link is an accordion header over the panel's left column (.mega-m,
        // printed right after it). Its "Overview" entry is the way through to the page itself.
        if (!megaDesktop.matches) {
          var acc = document.getElementById("mega-m-" + link.getAttribute("data-mega"));
          if (acc) {
            e.preventDefault();
            var open = !acc.classList.contains("is-open");
            if (open) {
              megaResetLight(acc);
              // One section at a time, so the list stays short enough to find your way around in.
              megaLinks.forEach(function (other) {
                var a = other !== link && document.getElementById("mega-m-" + other.getAttribute("data-mega"));
                if (a && a.classList.contains("is-open")) {
                  a.classList.remove("is-open");
                  other.setAttribute("aria-expanded", "false");
                }
              });
            }
            acc.classList.toggle("is-open", open);
            link.setAttribute("aria-expanded", open ? "true" : "false");
            // Bring the unfolded section's header to the top of the (scrolling) menu panel, so its
            // links are in view rather than below the fold — the "Careers unreachable" report.
            var scroller = link.closest(".nav-menu");
            if (open && scroller) {
              var to = scroller.scrollTop + link.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 8;
              if (scroller.scrollTo) scroller.scrollTo({ top: Math.max(0, to), behavior: "smooth" });
              else scroller.scrollTop = Math.max(0, to);
            }
          }
          return;
        }
        if (megaTouch && megaOpen !== link) {
          e.preventDefault();
          megaShow(link);
        }
        megaTouch = false;
      });

      if (panel) {
        panel.addEventListener("mouseenter", megaHoldOpen);
        panel.addEventListener("mouseleave", megaCloseSoon);
      }
    });

    // Focus leaving both the link and its panel closes it (tabbing on to the next nav item opens that one).
    document.addEventListener("focusin", function (e) {
      if (!megaOpen) return;
      var panel = megaPanelFor(megaOpen);
      if (e.target !== megaOpen && !(panel && panel.contains(e.target))) megaClose();
    });

    document.addEventListener("keydown", function (e) {
      if (megaOpen && (e.key === "Escape" || e.key === "Esc")) {
        var link = megaOpen;
        megaClose();
        link.focus({ preventScroll: true });
        megaClose(); // the focus() above re-opened it
      }
    });

    document.addEventListener("click", function (e) {
      if (!megaOpen) return;
      var panel = megaPanelFor(megaOpen);
      if (!megaOpen.contains(e.target) && !(panel && panel.contains(e.target))) megaClose();
    });

    /* While the pointer is over an open megamenu, the wheel never reaches the page (client request,
       19 Oct 2026). The page used to scroll away under the open menu — always when the panel fits the
       window (nothing to scroll, so the wheel goes straight to the page), and in Safari also past the
       end of a panel that does scroll, which ignores `overscroll-behavior: contain` there.
       A panel with a scrollbar still scrolls; only the part that would spill past its top or bottom
       is cancelled. Bound to .mega, not just the panel, so the strip and caret above it count too.
       A closed menu has pointer-events: none, so it never receives the wheel at all. */
    Array.prototype.forEach.call(document.querySelectorAll(".mega"), function (mega) {
      var box = mega.querySelector(".mega__panel");
      if (!box) return;
      mega.addEventListener("wheel", function (e) {
        var scrolls = box.scrollHeight > box.clientHeight + 1;
        if (!scrolls || !box.contains(e.target)) { e.preventDefault(); return; }
        var atTop = box.scrollTop <= 0;
        var atBottom = box.scrollTop + box.clientHeight >= box.scrollHeight - 1;
        if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) e.preventDefault();
      }, { passive: false });
    });

    // Crossing into desktop folds any mobile accordion, so its link doesn't arrive marked as open.
    navMobileMQ.addEventListener("change", function (mq) {
      if (mq.matches) return;
      megaLinks.forEach(function (link) {
        var acc = document.getElementById("mega-m-" + link.getAttribute("data-mega"));
        if (acc) acc.classList.remove("is-open");
        if (link !== megaOpen) link.setAttribute("aria-expanded", "false");
      });
    });

    window.addEventListener("resize", function () {
      if (!megaOpen) return;
      if (!megaDesktop.matches) megaClose();
      else megaPlaceCaret(megaOpen);
    });
  }

  /* ---- Language switch (EN/AR) ----
     No script. The switcher is two links and the server renders which one is on: header.php asks
     Polylang for the current language and prints `.is-active` on that option and `.is-ar` on the
     pill. The state is therefore already correct in the HTML of every page, in view-source and with
     JS off, and it changes only when a new page arrives.

     What used to be here was a click handler from before Polylang was wired up — it repainted the
     pill on click because nothing else could. Kept alongside real links it did active harm: the
     pill flipped to the target language while the old page was still on screen, so the control
     announced a switch the server had not made yet. It also read `data-lang`, an attribute the
     current markup does not emit, so the `is-ar` toggle always resolved false: clicking AR moved
     the thumb back under EN and painted the AR label white, i.e. invisible on the mint pill. */

  /* ---- Logo marquee (home-s1) — scrolls forever, and can be grabbed and thrown ----
     One set of logos is printed by pages/home.php; this clones it until the track covers the viewport
     plus one whole spare set, then walks a single offset and wraps it by exactly one set width. Because
     every set is identical, wrapping is invisible — there is no seam and no "jump back to the start".

     The transform is written here rather than left to a CSS animation because the same offset has to
     be driveable by three things at once: the constant drift, a drag, and the throw that follows it.
     A keyframe animation can only be paused, not steered.

     Hovering parks the drift, which is also what makes dragging usable: the strip is already still
     under the cursor by the time you press, so a grab does not have to fight it. */
  (function () {
    var vp = document.querySelector("[data-logo-marquee]");
    if (!vp) return;
    var track = vp.querySelector(".home-s1__track");
    var set = track && track.querySelector(".home-s1__set");
    if (!track || !set) return;

    var SPEED = 40;        // px/s the strip drifts on its own
    var FRICTION = 0.94;   // per-frame(60fps) decay applied to a throw
    var MAX_THROW = 3500;  // px/s — caps a flick so it can't blur into nothing
    var STILL = 90;        // ms of no movement before a release counts as a stop, not a throw

    var setW = 0, offset = 0, vel = 0;
    // +1 while the strip runs to the left, -1 once the page is RTL and it has to run to the right.
    // `offset` stays a plain distance travelled either way; this is the only thing that knows which
    // way that is, so the drift, the drag and the throw all pick it up from one place.
    var sign = 1;
    var dragging = false, lastX = 0, lastMove = 0, raf = null, prev = 0;
    var hover = false, rate = 1; // rate = how much of SPEED is currently in effect, 0..1

    var slow = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    /* Fill the track with copies of the printed set. The wrap subtracts ONE set width, so the track
       has to be at least a viewport plus one spare set wide — otherwise the strip runs out of logos
       before the wrap can bring them round and a gap crosses the screen. */
    function fill() {
      while (track.children.length > 1) track.removeChild(track.lastChild);

      // Read off the TRACK rather than off <html>: this is the direction the flex row is actually
      // laid out with, which is the thing the transform has to agree with. Re-read on every fill, so
      // a resize or a stylesheet change cannot leave it stale.
      sign = getComputedStyle(track).direction === "rtl" ? -1 : 1;

      setW = set.offsetWidth;
      if (!setW) return;

      var need = Math.ceil(vp.offsetWidth / setW) + 1;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < need; i++) {
        var clone = set.cloneNode(true);
        clone.setAttribute("aria-hidden", "true"); // the wrapper already carries the label
        frag.appendChild(clone);
      }
      track.appendChild(frag);
    }

    function draw() {
      if (setW) offset = ((offset % setW) + setW) % setW; // keep it inside one set, forever
      // In RTL the sets stack from the right and the spare copies hang off the LEFT edge, so the
      // strip has to travel right to pull them in. Translating left regardless — which is what this
      // did — dragged the whole strip away instead and opened a widening gap behind it, until the
      // wrap snapped it back a set width later.
      track.style.transform = "translate3d(" + (-offset * sign) + "px,0,0)";
    }

    function tick(now) {
      raf = requestAnimationFrame(tick);
      var dt = prev ? Math.min(0.05, (now - prev) / 1000) : 0; // cap it: a backgrounded tab hands
      prev = now;                                              // back a huge delta on its first frame

      // Hover parks the drift so a logo can be looked at — and eased, not cut: a strip that stops
      // dead under the cursor reads as a stutter rather than as an invitation. Dragging parks it too,
      // so letting go under the cursor leaves it already stopped instead of lurching back up to speed.
      var want = (hover || dragging || slow) ? 0 : 1;
      rate += (want - rate) * Math.min(1, dt * 8);

      if (!dragging) {
        offset += SPEED * rate * dt;
        // A throw still plays out while hovering — only the constant drift is what hover suspends.
        if (vel) {
          offset += vel * dt;
          vel *= Math.pow(FRICTION, dt * 60);
          if (Math.abs(vel) < 2) vel = 0;
        }
      }
      draw();
    }

    /* ---- hover: park the drift, mouse only ----
       Gated on pointerType because a touch fires pointerenter and then nothing until the next touch
       lands somewhere else — which would leave the strip parked for good after one tap. */
    vp.addEventListener("pointerenter", function (e) {
      if (e.pointerType && e.pointerType !== "mouse") return;
      hover = true;
    });
    vp.addEventListener("pointerleave", function (e) {
      if (e.pointerType && e.pointerType !== "mouse") return;
      hover = false;
    });

    /* ---- drag ---- */
    vp.addEventListener("pointerdown", function (e) {
      if (e.button != null && e.button !== 0) return; // left button / touch / pen only
      dragging = true;
      vel = 0;
      lastX = e.clientX;
      lastMove = 0;
      vp.classList.add("is-dragging");
      // Mouse only. preventDefault on a touch pointerdown cancels the browser's vertical pan, and
      // `touch-action: pan-y` in the CSS has already carved up the gesture correctly for touch.
      if (e.pointerType === "mouse") e.preventDefault();
      if (vp.setPointerCapture) { try { vp.setPointerCapture(e.pointerId); } catch (err) {} }
    });

    vp.addEventListener("pointermove", function (e) {
      if (!dragging) return;
      var now = performance.now();
      var dx = e.clientX - lastX;
      lastX = e.clientX;
      offset -= dx * sign; // the strip follows the pointer, whichever way it runs

      // Smoothed, so one stuttered frame can't decide the whole throw.
      var dt = lastMove ? Math.max(1, now - lastMove) / 1000 : 0;
      if (dt) {
        var inst = Math.max(-MAX_THROW, Math.min(MAX_THROW, -dx * sign / dt));
        vel = vel * 0.3 + inst * 0.7;
      }
      lastMove = now;
      draw();
    });

    var release = function (e) {
      if (!dragging) return;
      dragging = false;
      vp.classList.remove("is-dragging");
      // Held still before letting go? Then it was a stop, not a flick — don't fling it on from a
      // velocity that is by now several hundred ms out of date.
      if (!lastMove || performance.now() - lastMove > STILL) vel = 0;
      if (e && e.pointerId != null && vp.releasePointerCapture) {
        try { vp.releasePointerCapture(e.pointerId); } catch (err) {}
      }
    };
    vp.addEventListener("pointerup", release);
    vp.addEventListener("pointercancel", release);

    /* ---- lifecycle ---- */
    var start = function () { if (!raf) { prev = 0; raf = requestAnimationFrame(tick); } };
    var stop = function () { if (raf) { cancelAnimationFrame(raf); raf = null; } };

    var remeasure = function () {
      var was = setW ? offset / setW : 0; // hold the position proportionally across a resize
      fill();
      offset = was * setW;
      draw();
    };

    fill();
    draw();
    window.addEventListener("load", remeasure);   // logos decoded — widths are final
    window.addEventListener("resize", remeasure);

    // Only run while the strip is on screen; a rAF loop past the fold is pure waste.
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) start(); else stop();
      }).observe(vp);
    } else {
      start();
    }
  })();

  /* ---- Sliders (Swiper) ---- */
  if (typeof Swiper === "undefined") return;

  // Industries — autoplay carousel, centred (active) slide enlarged via CSS
  var indWrap = document.querySelector(".home-s6__swiper .swiper-wrapper");
  if (indWrap) { indWrap.innerHTML += indWrap.innerHTML; } // clone for smooth loop

  // The arc above the active circle is driven here (not CSS) so its entrance and exit
  // aren't mirror images: it rises + grows (with overshoot) coming in, and only fades
  // out in place going out — never dropping back onto the circle.
  var s6HasGsap = typeof gsap !== "undefined";
  var s6PrevArc = null;
  var S6_ARC_BRIGHT = "#4ddc87";                  // active arc colour (= --green-bright)
  var S6_ARC_DIM = "rgb(20,81,66)";               // ~ the dim green an inactive circle reads as
  function s6Arc(sw) {
    if (!s6HasGsap) return;
    var active = sw.slides[sw.activeIndex];
    var arc = active ? active.querySelector(".home-s6__arc") : null;
    if (s6PrevArc && s6PrevArc !== arc) {
      // exit, in lock-step with the circle's shrink (0.55s, same ease-out): shrink from centre
      // and slide DOWN so the arc's centre lands on the circle's centre — small enough to tuck
      // fully behind it (arc z-index 0 < circle 1) — while recolouring to the darker green.
      // The travel is measured live so the small arc ends up centred on (hidden by) the circle.
      var yTarget = 150;
      var prevCircle = s6PrevArc.parentNode && s6PrevArc.parentNode.querySelector(".home-s6__circle");
      if (prevCircle) {
        var ar = s6PrevArc.getBoundingClientRect();
        var cr = prevCircle.getBoundingClientRect();
        yTarget = (cr.top + cr.height / 2) - (ar.top + ar.height / 2);
      }
      gsap.to(s6PrevArc, {
        autoAlpha: 0, scale: 0.2, y: yTarget, duration: 0.55, ease: "power3.out",
        transformOrigin: "50% 50%", overwrite: true
      });
      var prevPath = s6PrevArc.querySelector("path");
      if (prevPath) gsap.to(prevPath, { fill: S6_ARC_DIM, duration: 0.55, ease: "power3.out", overwrite: true });
    }
    if (arc) {
      gsap.set(arc, { xPercent: -50, transformOrigin: "50% 100%" });
      var path = arc.querySelector("path");
      if (path) gsap.set(path, { fill: S6_ARC_BRIGHT }); // reset colour for a re-entering arc
      gsap.fromTo(arc,
        { autoAlpha: 0, y: 40, scale: 0.55 },
        { autoAlpha: 1, y: 0, scale: 1, duration: 0.6, ease: "back.out(1.6)", overwrite: true });
    }
    s6PrevArc = arc;
  }

  // Variable spacing: widen the two gaps either side of the active slide and tighten the inner ones,
  // while keeping the OUTERMOST visible slide in place — so the same number of slides stays visible
  // (the spacing is just redistributed, not the total width). Shift tapers to 0 at the outer edge.
  var S6_EXTRA = 34; // px added to each of the active slide's gaps (taken evenly from the inner gaps)
  function s6Spacing(sw) {
    var N = Math.floor((sw.params.slidesPerView || 1) / 2); // slides visible on each side of centre
    var a = sw.activeIndex;
    for (var i = 0; i < sw.slides.length; i++) {
      if (N < 2) { sw.slides[i].style.transform = ""; continue; } // 1-per-view (mobile): leave to Swiper
      var d = i - a, ad = Math.abs(d), tx = 0;
      if (ad >= 1 && ad < N) tx = (d > 0 ? 1 : -1) * S6_EXTRA * (N - ad) / (N - 1);

      // Slide ORDER runs right-to-left once Swiper is in RTL, but translateX does not — +x is the
      // right-hand edge either way. Unflipped, the widening landed on the wrong side of the active
      // slide, so the Arabic carousel squeezed the two gaps it was meant to open and opened the
      // inner ones it was meant to squeeze. rtlTranslate is the flag Swiper does its own translate
      // maths with, so reading it means this can never disagree with the layout it is adjusting.
      if (sw.rtlTranslate) tx = -tx;

      sw.slides[i].style.transform = "translateX(" + tx + "px)";
    }
  }

  var s6On = function (sw) { s6Arc(sw); s6Spacing(sw); };

  new Swiper(".home-s6__swiper", {
    centeredSlides: true,
    loop: true,
    loopAdditionalSlides: 3, // more buffer so the loop's slide↔clone swap happens off-screen (no circle blink)
    speed: 900,
    spaceBetween: 24,
    slidesPerView: 5, // numeric = reliable centering; breakpoints keep slides wide
    slideToClickedSlide: true, // click an inactive slide → it scrolls in and becomes the active one
    autoplay: { delay: 2200, disableOnInteraction: false },
    navigation: { prevEl: ".home-s6__prev", nextEl: ".home-s6__next" }, // shown only <=600 (see SCSS)
    breakpoints: {
      0:    { slidesPerView: 1 },
      600:  { slidesPerView: 3 },
      900:  { slidesPerView: 5 },
      1350: { slidesPerView: 6 },
      1800: { slidesPerView: 7 }
    },
    on: { init: s6On, slideChangeTransitionStart: s6On, breakpoint: s6Spacing }
  });

  // ---- Full-bleed peek slider (home-s7 "difference" + platform-s1 features) ----
  // Bleeds to both viewport edges (see SCSS); slidesOffsetBefore/After re-align the first/last card
  // to the container and give the last card a trailing gap; collapseEndSnap drops the stray end-snap
  // so the last card lands in a single click. Inset derived from layout (max-width 1720 + 40px pad).
  // The slider bleeds to the viewport edges via `calc(50% - 50vw)`. Both 50vw AND CSS @media count
  // the scrollbar (= window.innerWidth); only the .container fills the scrollbar-less content area
  // (= clientWidth). Use innerWidth for the bled part + breakpoints, clientWidth for the container —
  // otherwise the first card is off by half the scrollbar and triggers fire at the wrong width.
  var mq = function (q) { return window.matchMedia && window.matchMedia(q).matches; };
  function bleedInset() {
    if (mq("(max-width: 500px)")) return 0;          // below 500 the SCSS unsets the bleed → no inset
    var iw = window.innerWidth;                       // matches the CSS 50vw (includes scrollbar)
    var cw = document.documentElement.clientWidth;    // the container fills the content area (no scrollbar)
    var pad = mq("(max-width: 1100px)") ? 24 : 40;   // .container side padding (@media breakpoint)
    var containerContent = Math.min(cw, 1720) - pad * 2;
    return (iw - containerContent) / 2;              // bled edge → first card at the container content-left
  }
  function makeBleedSlider(swiperSel, prevSel, nextSel) {
    if (!document.querySelector(swiperSel)) return; // only inits on the page that has this slider
    var sw = new Swiper(swiperSel, {
      slidesPerView: 4.1,
      spaceBetween: 42,
      slidesOffsetBefore: bleedInset(),
      slidesOffsetAfter: bleedInset(),
      navigation: { prevEl: prevSel, nextEl: nextSel },
      breakpoints: {
        0:    { slidesPerView: 1, spaceBetween: 25 },
        500:  { slidesPerView: 2, spaceBetween: 25 },
        800:  { slidesPerView: 3, spaceBetween: 25 },
        1400: { slidesPerView: 4.1, spaceBetween: 42 }
      }
    });
    var collapseEndSnap = function () {
      var sg = sw.snapGrid;
      var cardW = (sw.slidesSizesGrid && sw.slidesSizesGrid[0]) || 300;
      while (sg && sg.length >= 2 && (sg[sg.length - 1] - sg[sg.length - 2]) < cardW * 0.6) {
        sg.splice(sg.length - 2, 1);
      }
    };
    var setOffsets = function () {
      var off = bleedInset();
      sw.params.slidesOffsetBefore = off;
      sw.params.slidesOffsetAfter = off;
      sw.update();
      collapseEndSnap();
      if (sw.navigation) sw.navigation.update();
    };
    sw.on("update", collapseEndSnap);
    requestAnimationFrame(setOffsets);
    sw.on("resize", setOffsets);
    sw.on("breakpoint", setOffsets);
    window.addEventListener("load", setOffsets);
    return sw;
  }
  // home-s7: horizontal peek-slider above 600, but a scroll-driven vertical stack at/below 600
  // (see the .home-s7 stacked styles + the "drive away" scroll effect). Init/destroy on the breakpoint.
  (function () {
    if (!document.querySelector(".home-s7__swiper")) return;
    var mq = window.matchMedia("(min-width: 601px)");
    var sw = null;
    var sync = function () {
      if (mq.matches && !sw) {
        sw = makeBleedSlider(".home-s7__swiper", ".home-s7__prev", ".home-s7__next");
      } else if (!mq.matches && sw) {
        sw.destroy(true, true); // strip Swiper's inline styles so the cards stack in normal flow
        sw = null;
      }
    };
    sync();
    if (mq.addEventListener) mq.addEventListener("change", sync); else mq.addListener(sync);
  })();
  // platform-s1 "everything you need": same as home-s7 — horizontal peek-slider above 600, scroll-driven
  // vertical card DECK at/below 600. Init/destroy Swiper on the breakpoint so the cards stack in flow.
  (function () {
    if (!document.querySelector(".platform-s1__swiper")) return;
    var mq = window.matchMedia("(min-width: 601px)");
    var sw = null;
    var sync = function () {
      if (mq.matches && !sw) {
        sw = makeBleedSlider(".platform-s1__swiper", ".platform-s1__prev", ".platform-s1__next");
      } else if (!mq.matches && sw) {
        sw.destroy(true, true);
        sw = null;
      }
    };
    sync();
    if (mq.addEventListener) mq.addEventListener("change", sync); else mq.addListener(sync);
  })();

  // platform-s3 behaviour slider — one image per slide, chevron arrows on the sides + dots below
  (function () {
    if (typeof Swiper === "undefined") return;
    if (!document.querySelector(".behaviour-swiper")) return;
    var bsw = new Swiper(".behaviour-swiper", {
      slidesPerView: 1,
      spaceBetween: 20,
      speed: 500,
      // Endless in both directions, so neither arrow is ever a dead end. Swiper 11's loop REORDERS
      // the real slides rather than inserting duplicates of them (the pre-9 behaviour), which matters
      // here: the hover-to-play module further down binds to `.behaviour-card`, and clones would have
      // given it a second copy of every card — each with its own <video> pulling the same clip off
      // the CDN a second time. Nothing to guard against on 11; the five cards stay five elements.
      loop: true,
      watchOverflow: true,   // lock the slider if the slides ever all fit (keeps bounds sane)
      navigation: { prevEl: ".behaviour-prev", nextEl: ".behaviour-next" },
      pagination: { el: ".behaviour-dots", clickable: true },
      on: {
        // Hand the card that just arrived over to the clip player further down this file. Fired at
        // the END of the transition on purpose: the outgoing card keeps playing while it slides away,
        // which reads better than every card reverting to its still the moment you touch an arrow.
        // The player publishes this hook when it sets up, well after this init runs — hence the guard.
        slideChangeTransitionEnd: function () {
          if (window.rooyaBehaviourSync) window.rooyaBehaviourSync();
        }
      }
    });

    // The clip player drives the carousel forward when a card finishes, so give it a way to do that
    // without reaching for the instance. Safe in loop mode: slideNext() from the last card wraps.
    window.rooyaBehaviourNext = function () { bsw.slideNext(); };
    // If init runs before the slider has its final width, the slide metrics come out wrong and the
    // drag is not clamped to them. Recompute once the layout has settled — NOT via observer/
    // observeParents, which re-fire on every image load and can reset the drag bounds mid-interaction
    // (that let the slider be dragged past the ends). This used to also exist to get the prev arrow
    // disabled on slide 0; looping means there is no such state left to correct.
    var fix = function () { bsw.update(); if (bsw.navigation) bsw.navigation.update(); };
    window.addEventListener("load", fix);
    requestAnimationFrame(fix);
  })();

  // "See your fleet" (platform-s2) — pills act as tabs, swapping only the dashboard image
  (function () {
    var stage = document.querySelector(".platform-s2__stage");
    if (!stage) return;
    var img = stage.querySelector(".platform-s2__dash img");
    var pills = stage.querySelectorAll(".platform-s2__pills li");
    if (!img || !pills.length) return;

    var FADE = 150; // must match the CSS opacity transition on .platform-s2__dash img (out + in)
    var swap = function (src) {
      if (!src) return;
      var pre = new Image();
      pre.src = src;                            // start decoding the new image right away
      img.classList.add("is-fading");           // fade the current image OUT (CSS opacity transition)
      // wait for the fade-out to actually play before swapping, otherwise the browser coalesces the
      // add+remove into one frame and nothing animates
      setTimeout(function () {
        var apply = function () {
          img.src = src;                        // swap while hidden (already decoded → no flicker)
          requestAnimationFrame(function () { img.classList.remove("is-fading"); }); // fade back IN
        };
        if (pre.complete) apply();
        else pre.onload = pre.onerror = apply;
      }, FADE);
    };

    for (var i = 0; i < pills.length; i++) {
      pills[i].addEventListener("click", function () {
        if (this.classList.contains("is-active")) return;
        for (var j = 0; j < pills.length; j++) pills[j].classList.remove("is-active");
        this.classList.add("is-active");
        swap(this.getAttribute("data-img"));
      });
    }
  })();

  /* ---- Impact stats (home-s8) — count-up fallback only. When GSAP + ScrollTrigger are present
     (and motion isn't reduced) the section-intro timeline drives the counters instead: it fades
     each card up, then starts its count. This branch only covers the no-GSAP / reduced-motion case. ---- */
  var impact = document.querySelector(".home-s8");
  var s8HasIntro = typeof gsap !== "undefined" && window.ScrollTrigger &&
    !(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  if (impact && !s8HasIntro && "IntersectionObserver" in window) {
    var stats = impact.querySelectorAll(".home-s8__stat");

    // freeze every number at 0% and stash its target + the bar's target width
    stats.forEach(function (s) {
      var num = s.querySelector(".num");
      var bar = s.querySelector(".bar span");
      num.setAttribute("data-target", parseInt(num.textContent, 10) || 0);
      num.textContent = "0%";
      bar.setAttribute("data-target", bar.style.width || "0%");
      bar.style.width = "0%";
    });

    var animateStat = function (s) {
      var num = s.querySelector(".num");
      var bar = s.querySelector(".bar span");
      var target = +num.getAttribute("data-target");
      bar.style.width = bar.getAttribute("data-target");
      var start = null, dur = 1100;
      var step = function (ts) {
        if (!start) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        num.textContent = Math.round(eased * target) + "%";
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        stats.forEach(function (s, i) { setTimeout(function () { animateStat(s); }, i * 260); });
      });
    }, { threshold: 0.3 });
    io.observe(impact.querySelector(".home-s8__grid") || impact);
  }
})();

/* ---- S2 connector arrows — one per line, riding the SVG paths, looping & staggered ---- */
(function () {
  var layer = document.querySelector(".home-s2__lines");
  if (!layer) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  // two arrow layers sharing the cluster geometry: FRONT above the images, BACK below.
  // A rider is moved to BACK only while it sits over the phone, so it slips behind it
  // (the baked-in green circle is excluded, so arrows stay visible over it).
  var arrowFront = document.createElement("div");
  arrowFront.className = "home-s2__arrows";
  var arrowBack = document.createElement("div");
  arrowBack.className = "home-s2__arrows--back";
  layer.parentNode.appendChild(arrowBack);
  layer.parentNode.appendChild(arrowFront);

  var phoneImg = document.querySelector(".home-s2__q--apps .image img");
  var PHONE = { x0: 0.50, x1: 0.86, y0: 0.00, y1: 0.80 }; // phone-device box as fractions of the image

  // arrow badge (ids stripped so 5 copies don't clash); sized by CSS .line-arrow
  var ARROW =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 31.111 31.114">' +
    '<g transform="translate(-187.766 -1497.795)">' +
    '<path d="M47.25,32.7A14.555,14.555,0,1,1,32.693,18.145,14.556,14.556,0,0,1,47.25,32.7" transform="translate(170.627 1480.65)" fill="#00995d"/>' +
    '<path d="M47.25,32.7A14.555,14.555,0,1,1,32.693,18.145,14.556,14.556,0,0,1,47.25,32.7Z" transform="translate(170.627 1480.65)" fill="none" stroke="#fff" stroke-miterlimit="10" stroke-width="2"/>' +
    '<path d="M32.965,26.882,21.888,21.557l2.654,5.095-2.928,5Z" transform="translate(176.879 1486.79)" fill="#fff"/>' +
    '</g></svg>';

  var DURATION = 40000; // ms per full cycle, SAME for every line — keeps the arrows evenly
                        // staggered (no drift/bunching) and sets the overall pace
  var VISIBLE = 0.6;    // fraction of the cycle an arrow is travelling (rest = hidden).
                        // With 5 riders staggered by 1/5 this keeps 3 on screen at all times.
  var HALF = 15.5;      // half the arrow size, to centre it on the path point
  var reverseDone = false; // one-time: flip the top-right line so its arrow runs right -> left

  var riders = [];
  layer.querySelectorAll(".line").forEach(function (span, i) {
    var geo = span.querySelector("path, line");
    if (!geo || !geo.getTotalLength) return;
    var len = geo.getTotalLength();
    var el = document.createElement("div");
    el.className = "line-arrow";
    el.innerHTML = ARROW;
    arrowFront.appendChild(el);
    riders.push({
      geo: geo,
      svg: geo.ownerSVGElement,
      el: el,
      len: len,
      dur: DURATION,
      phase: i / 5,   // stagger the starts so the arrows never move in unison
      forward: null,  // resolved on first visible frame: does rising length go rightward?
      ox: null,       // this line's <svg> offset inside the layer, in unscaled local px
      oy: null,
      pt: geo.ownerSVGElement.createSVGPoint()
    });
  });
  if (!riders.length) return;

  // Each line's <svg> offset within the layer, in the layer's own unscaled px. Cached rather than
  // read per frame: these are local coordinates, so the cluster's scale does not affect them and only
  // a real reflow can. (Reading five rects every frame would also be five forced layouts.)
  // Is this layer drawn mirrored? rtl.scss flips company-s2's line layer whole, badges included, so
  // that the riders stay on their paths. The placement below is all local-space and comes along for
  // free — but the offsets measured HERE are read off the screen, and a mirror turns screen-left
  // into local-right. Measured from the wrong edge, a badge is laid onto a path that is no longer
  // under it, which is what left one of them hanging in open space.
  function mirrored() {
    var t = getComputedStyle(layer).transform;
    return t.indexOf("matrix(-") === 0;
  }

  function measureOffsets() {
    if (!layer.offsetWidth) return;
    var rect = layer.getBoundingClientRect();
    var s = rect.width / layer.offsetWidth || 1;
    var mir = mirrored();
    riders.forEach(function (r) {
      var sr = r.svg.getBoundingClientRect();
      // Distance from the layer's LEADING screen edge to the line's own leading edge — which is the
      // right-hand one once the layer is flipped. Vertical is untouched: nothing mirrors on y.
      r.ox = mir ? (rect.right - sr.right) / s : (sr.left - rect.left) / s;
      r.oy = (sr.top - rect.top) / s;
    });
  }

  window.addEventListener("resize", measureOffsets);
  window.addEventListener("load", measureOffsets);

  // Point at a "virtual" length: inside [0,len] it's the real path point; beyond either
  // end it extrapolates straight along the tangent, giving the arrow a runway to slide
  // fully off-screen (used for the reversed rider, whose path ends at the viewport edge).
  function ptv(geo, vl, len) {
    if (vl < 0) {
      var p0 = geo.getPointAtLength(0), p1 = geo.getPointAtLength(Math.min(1, len));
      var dx = p0.x - p1.x, dy = p0.y - p1.y, d = Math.hypot(dx, dy) || 1;
      return { x: p0.x + dx / d * (-vl), y: p0.y + dy / d * (-vl) };
    }
    if (vl > len) {
      var pn = geo.getPointAtLength(len), pm = geo.getPointAtLength(Math.max(0, len - 1));
      var ex = pn.x - pm.x, ey = pn.y - pm.y, e = Math.hypot(ex, ey) || 1;
      return { x: pn.x + ex / e * (vl - len), y: pn.y + ey / e * (vl - len) };
    }
    return geo.getPointAtLength(vl);
  }

  var t0 = null;
  function tick(ts) {
    if (t0 === null) t0 = ts;
    // skip while the layer is hidden (mobile) — getScreenCTM() would be null there
    if (layer.offsetWidth) {
      var rect = layer.getBoundingClientRect();
      var s = rect.width / layer.offsetWidth; // the stage is CSS-scaled; convert screen px -> local px
      var elapsed = ts - t0;

      // phone hit-box in layer-local (unscaled) coords, recomputed each frame
      var phone = null;
      if (phoneImg) {
        var ir = phoneImg.getBoundingClientRect();
        var il = (ir.left - rect.left) / s, it = (ir.top - rect.top) / s;
        var iw = ir.width / s, ih = ir.height / s;
        phone = {
          x0: il + PHONE.x0 * iw, x1: il + PHONE.x1 * iw,
          y0: it + PHONE.y0 * ih, y1: it + PHONE.y1 * ih
        };
      }

      riders.forEach(function (r) {
        // getCTM (path user units -> its own <svg> viewport), NOT getScreenCTM. WebKit does not fold
        // the CSS transform of HTML ancestors into getScreenCTM, so on Safari it returned points in a
        // space the `rect`/`s` conversion below did not match: the arrows landed hundreds of px off
        // the stage — the ones seen floating in mid-air. getCTM stops at the SVG viewport, which every
        // engine agrees on, and the <svg>'s own offset in the layer is added separately.
        var ctm = r.geo.getCTM();
        if (!ctm) return;

        if (r.ox === null) { measureOffsets(); if (r.ox === null) return; }

        // resolve travel sense once so every arrow runs left -> right,
        // whichever way this particular path's data happens to be authored
        if (r.forward === null) {
          var s0 = r.geo.getPointAtLength(0);
          var s1 = r.geo.getPointAtLength(r.len);
          r.pt.x = s0.x; r.pt.y = s0.y;
          var x0 = r.pt.matrixTransform(ctm).x;
          r.pt.x = s1.x; r.pt.y = s1.y;
          var x1 = r.pt.matrixTransform(ctm).x;
          r.forward = x1 >= x0; // rising length moves rightward (uniform scale keeps the order)
        }

        var t = (elapsed / r.dur + r.phase) % 1;
        if (t >= VISIBLE) { r.el.style.opacity = 0; return; } // resting — hidden this part of the cycle
        var prog = t / VISIBLE;                                // 0..1 travel progress across the line

        // Every rider gets a straight runway past both ends: it extrapolates along the
        // tangent so the arrow slides fully off-screen to appear/disappear — no arrow ever
        // pops in or out mid-view, wherever its line happens to terminate.
        var EXT = 140; // arc px of off-screen lead-in/out beyond each endpoint
        var span = r.len + 2 * EXT;
        var vl = r.forward ? (-EXT + prog * span) : (r.len + EXT - prog * span);
        var a = ptv(r.geo, vl, r.len);
        var b = ptv(r.geo, vl + (r.forward ? 1 : -1), r.len); // point in the direction of travel
        r.pt.x = a.x; r.pt.y = a.y;
        var pa = r.pt.matrixTransform(ctm);
        r.pt.x = b.x; r.pt.y = b.y;
        var pb = r.pt.matrixTransform(ctm);
        var ang = Math.atan2(pb.y - pa.y, pb.x - pa.x) * 180 / Math.PI; // scale is uniform → unchanged
        // pa is already in the layer's unscaled px once the line's own offset is added — no screen
        // round-trip, so nothing here depends on how an engine treats ancestor CSS transforms.
        var lx = r.ox + pa.x;
        var ly = r.oy + pa.y;

        // no fade: keep the arrow fully opaque until it (plus its glow) is completely
        // past a stage edge — where the browser already clips it — so the cut is unseen.
        // EDGE covers the badge radius + box-shadow spread so it never blinks out on-screen.
        var EDGE = 60;
        r.el.style.opacity = (lx < -EDGE || lx > layer.offsetWidth + EDGE) ? 0 : 1;

        // slip behind the phone only while the arrow is actually over the device
        var target = (phone && lx > phone.x0 && lx < phone.x1 && ly > phone.y0 && ly < phone.y1)
          ? arrowBack : arrowFront;
        if (r.el.parentNode !== target) target.appendChild(r.el);

        r.el.style.transform = "translate(" + (lx - HALF) + "px," + (ly - HALF) + "px) rotate(" + ang + "deg)";
      });

      // Once every rider's travel sense is known, flip the top-right line so its
      // arrow runs the opposite way (right -> left). We pick the line that reaches
      // furthest into the top-right corner (max screen x, min screen y).
      if (!reverseDone && riders.every(function (r) { return r.forward !== null; })) {
        var bestIdx = -1, bestScore = -Infinity;
        riders.forEach(function (r, idx) {
          var ctm = r.geo.getCTM();          // same local-space math as the loop above
          if (!ctm || r.ox === null) return;
          for (var q = 0; q <= 4; q++) {
            var p = r.geo.getPointAtLength(r.len * q / 4);
            r.pt.x = p.x; r.pt.y = p.y;
            var sp = r.pt.matrixTransform(ctm);
            var score = (r.ox + sp.x) - (r.oy + sp.y); // top-right = high
            if (score > bestScore) { bestScore = score; bestIdx = idx; }
          }
        });
        if (bestIdx >= 0) { riders[bestIdx].forward = !riders[bestIdx].forward; }
        reverseDone = true;
      }
    }
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
})();

/* ---- S2 connector arrows (stacked tablet/mobile) — ONE left-to-right pass per line ----
   Mobile (<=600) rides the .mline paths, tablet (601-900) the wider .tline paths. Each line's arrow
   makes a SINGLE left->right pass, kicked off the moment that line scrolls into the viewport (per-line
   IntersectionObserver). No looping, no "max on screen" cap — every arrow can be visible at once.
   getScreenCTM is transform-agnostic, so the baked rotate(90) on each path is handled. */
(function () {
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  var ARROW =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 31.111 31.114">' +
    '<g transform="translate(-187.766 -1497.795)">' +
    '<path d="M47.25,32.7A14.555,14.555,0,1,1,32.693,18.145,14.556,14.556,0,0,1,47.25,32.7" transform="translate(170.627 1480.65)" fill="#00995d"/>' +
    '<path d="M47.25,32.7A14.555,14.555,0,1,1,32.693,18.145,14.556,14.556,0,0,1,47.25,32.7Z" transform="translate(170.627 1480.65)" fill="none" stroke="#fff" stroke-miterlimit="10" stroke-width="2"/>' +
    '<path d="M32.965,26.882,21.888,21.557l2.654,5.095-2.928,5Z" transform="translate(176.879 1486.79)" fill="#fff"/>' +
    '</g></svg>';

  // PASS = ms for one left->right sweep across the whole span (len + 2*EXT). Matches the desktop
  // tempo: desktop travels its span over VISIBLE*DURATION = 0.6 * 40000 = 24000ms.
  var PASS = 18000, HALF = 15.5, EXT = 140, EDGE = 60;

  function ptv(geo, vl, len) {
    if (vl < 0) {
      var p0 = geo.getPointAtLength(0), p1 = geo.getPointAtLength(Math.min(1, len));
      var dx = p0.x - p1.x, dy = p0.y - p1.y, d = Math.hypot(dx, dy) || 1;
      return { x: p0.x + dx / d * (-vl), y: p0.y + dy / d * (-vl) };
    }
    if (vl > len) {
      var pn = geo.getPointAtLength(len), pm = geo.getPointAtLength(Math.max(0, len - 1));
      var ex = pn.x - pm.x, ey = pn.y - pm.y, e = Math.hypot(ex, ey) || 1;
      return { x: pn.x + ex / e * (vl - len), y: pn.y + ey / e * (vl - len) };
    }
    return geo.getPointAtLength(vl);
  }

  // build a self-contained arrow system for one line container (.home-s2__mlines / .home-s2__tlines)
  function setup(layer, lineSel) {
    if (!layer) return;

    var arrowLayer = document.createElement("div");
    arrowLayer.className = "home-s2__marrows"; // inset:0 of the same column as the lines
    layer.parentNode.appendChild(arrowLayer);

    var riders = [];
    layer.querySelectorAll(lineSel).forEach(function (span, i) {
      var geo = span.querySelector("path, line");
      if (!geo || !geo.getTotalLength) return;
      var el = document.createElement("div");
      el.className = "line-arrow";
      el.innerHTML = ARROW;
      arrowLayer.appendChild(el);
      var r = {
        span: span, geo: geo, el: el, len: geo.getTotalLength(),
        computed: false, sStart: 0, sEnd: 0, pt: geo.ownerSVGElement.createSVGPoint(),
        first: i === 0, // only the FIRST line's arrow runs the opposite way (right→left)
        t0: null, done: false // t0 set when the line enters view; done after its single pass
      };
      span._rider = r;
      riders.push(r);
    });
    if (!riders.length) return;

    // Only run the per-frame loop while home-s2 is on (or near) screen. It reads getBoundingClientRect /
    // getScreenCTM every frame — a forced synchronous layout — and left running while you're far below
    // (at the deck / footer) that constant layout thrash desyncs ScrollTrigger's scroll math on Firefox
    // (pins land wrong, entrances fire early). Pausing it off-screen removes that interference entirely.
    var inView = false, running = false;
    var pump = function () { if (!running) { running = true; requestAnimationFrame(frame); } };
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        if (inView) pump(); else riders.forEach(function (r) { r.el.style.opacity = 0; });
      }, { rootMargin: "300px 0px" }).observe(layer);
    } else { inView = true; }

    function frame(ts) {
      if (!inView) { running = false; return; } // paused while off-screen — no layout reads at all
      if (layer.offsetWidth) {
        var rect = layer.getBoundingClientRect();
        var s = rect.width / layer.offsetWidth; // recover the CSS scale of the stacked column

        riders.forEach(function (r) {
          try {
            if (r.done) { r.el.style.opacity = 0; return; } // finished its single pass
            var ctm = r.geo.getScreenCTM();
            if (!ctm) { r.el.style.opacity = 0; return; }

            if (!r.computed) {
              // Follow the WHOLE path from its TOPMOST endpoint (smallest screen y), with ±EXT runway
              // so the arrow slides off the ends. Each connector begins at a top corner (line 1 top-
              // left, line 2 top-right, …) and threads down its route — so the arrow starts where the
              // line visually begins and flows along it.
              var e0 = r.geo.getPointAtLength(0), e1 = r.geo.getPointAtLength(r.len);
              r.pt.x = e0.x; r.pt.y = e0.y; var se0 = r.pt.matrixTransform(ctm);
              r.pt.x = e1.x; r.pt.y = e1.y; var se1 = r.pt.matrixTransform(ctm);
              var from0 = se0.y <= se1.y;
              r.sStart = from0 ? -EXT : (r.len + EXT);
              r.sEnd = from0 ? (r.len + EXT) : -EXT;
              r.computed = true;
            }

            // Start the pass once this line's START point scrolls up into view.
            if (r.t0 === null) {
              var sp0 = ptv(r.geo, r.sStart, r.len);
              r.pt.x = sp0.x; r.pt.y = sp0.y; var startY = r.pt.matrixTransform(ctm).y;
              if (startY < window.innerHeight * 1.05) r.t0 = ts;
              else { r.el.style.opacity = 0; return; }
            }

            var p = (ts - r.t0) / PASS;
            if (p >= 1) { r.done = true; r.el.style.opacity = 0; return; } // one pass only
            var vl = r.sStart + (r.sEnd - r.sStart) * p; // travel from start to end position
            var step = r.sEnd >= r.sStart ? 1 : -1;      // tangent direction of travel (for the angle)
            var a = ptv(r.geo, vl, r.len);
            var b = ptv(r.geo, vl + step, r.len);
            r.pt.x = a.x; r.pt.y = a.y; var pa = r.pt.matrixTransform(ctm);
            r.pt.x = b.x; r.pt.y = b.y; var pb = r.pt.matrixTransform(ctm);
            var ang = Math.atan2(pb.y - pa.y, pb.x - pa.x) * 180 / Math.PI;
            var lx = (pa.x - rect.left) / s;
            var ly = (pa.y - rect.top) / s;
            // Visibility is gated against the VIEWPORT (screen x), not the narrow centred column — the
            // line SVGs bulge into the wide side margins, so an arrow out there is still on screen and
            // must stay visible. (lx/ly place the arrow in column-local space; pa.x is its screen x.)
            r.el.style.opacity = (pa.x < -EDGE || pa.x > window.innerWidth + EDGE) ? 0 : 1;
            r.el.style.transform = "translate(" + (lx - HALF) + "px," + (ly - HALF) + "px) rotate(" + ang + "deg)";
          } catch (e) { /* one bad rider must never kill the loop */ }
        });
      } else {
        riders.forEach(function (r) { r.el.style.opacity = 0; }); // hidden set (other breakpoint)
      }
      if (inView) requestAnimationFrame(frame); else running = false;
    }
    if (inView) pump();
  }

  setup(document.querySelector(".home-s2__mlines"), ".mline"); // mobile (<=600)
  setup(document.querySelector(".home-s2__tlines"), ".tline"); // tablet (601-900)
})();

/* Shared, DEBOUNCED ScrollTrigger refresh. home-s2 reserves its height via JS after load/images,
   which shifts every section below it. Refreshing on each intermediate (too-short) height makes the
   downstream pins compute too high; debouncing means we refresh only ONCE the height has settled, so
   the deck + footer pins land against the final layout. */
window.rooyaRefreshST = (function () {
  var t;
  return function () {
    if (!(window.ScrollTrigger && ScrollTrigger.refresh)) return;
    clearTimeout(t);
    t = setTimeout(function () {
      // from the top, always: this fires late (images/fonts/height reservation all settle after the
      // visitor has started scrolling), so by now the page is usually mid-pin. See rooyaAtTop.
      window.rooyaAtTop(function () { ScrollTrigger.refresh(); });
    }, 200);
  };
})();

/* ---- S2 diagram — scale the fixed 1920 stage to fill any viewport (option A) ---- */
(function () {
  var vp = document.querySelector(".home-s2__viewport");
  var cluster = vp && vp.querySelector(".home-s2__cluster");
  if (!vp || !cluster) return;

  var DESIGN = 1920; // width the diagram is designed/tuned at

  // set the reserved height, and when it actually changes tell ScrollTrigger to recompute — this
  // height is set AFTER first layout/images, which shifts every section below; without a refresh the
  // pins downstream (home-s7 deck, footer reveal) keep their stale positions (visible on Firefox).
  var lastH = null;
  function setH(h) {
    if (h === lastH) return;
    lastH = h;
    vp.style.height = h;
    if (window.rooyaRefreshST) window.rooyaRefreshST(); // debounced — recompute pins once height settles
  }

  function fit() {
    // stack point must fire with the CSS @media (max-width: 900px) — matchMedia mirrors it exactly
    if (window.matchMedia && window.matchMedia("(max-width: 900px)").matches) {
      cluster.style.transform = "";
      setH("");
      return;
    }
    var vw = document.documentElement.clientWidth; // the diagram fills the content area (no scrollbar)
    var s = vw / DESIGN;
    cluster.style.transform = "scale(" + s + ")";
    // offsetHeight is the unscaled (1920-layout) height; reserve the scaled height
    setH((cluster.offsetHeight * s) + "px");
  }

  fit();
  window.addEventListener("resize", fit);
  window.addEventListener("load", fit); // re-fit once images have set the height
})();

/* ---- S2 diagram — stacked tablet/mobile layout (<=900) ----
   No JS scaler here: below 900 the stacked column (.home-s2__inner) flows NATURALLY via CSS (relative,
   width:100% capped at the 431 design), so there's no JS-reserved height changing after load — which
   (together with the .image height-reservation fix) is what kept the downstream pins from drifting.
   The 1920 desktop scaler above still clears its transform/height at <=900, leaving the flow intact. */

/* ---- Platform masthead — scale the fixed 1040 design stage to fit the media column (like S2) ---- */
(function () {
  var media = document.querySelector(".platform-hero__media");
  var stage = media && media.querySelector(".platform-hero__stage");
  if (!media || !stage) return;

  var DESIGN = 1040; // stage width = the media-column width at the 1920 design

  var mm = function (q) { return window.matchMedia && window.matchMedia(q).matches; };
  var lastW = -1;
  function fit() {
    // measure the ACTUAL media-column width rather than deriving it from vw + assumed padding — this
    // is layout-accurate and scrollbar-proof, so it can't disagree with the CSS breakpoints
    var avail = media.clientWidth;
    if (!avail) return;
    lastW = avail;
    var stacked = mm("(max-width: 900px)"); // hero stacks — media is full width
    var s = avail / DESIGN;
    // the 1400 boost is a desktop tweak only (fire exactly with the SCSS @media via matchMedia)
    if (!stacked && mm("(max-width: 1400px)")) s *= 1.18;
    s = Math.min(1, s);
    stage.style.transform = s >= 1 ? "" : "scale(" + s + ")";
    // transform doesn't shrink the layout box, so when scaled down reserve the REAL (scaled) height.
    // Use offsetHeight * s (deterministic) rather than getBoundingClientRect — the latter depends on the
    // transform having been applied at measure time and can come back UNSCALED, over-reserving and
    // leaving a big empty gap below the composition on mobile.
    media.style.height = (s < 1) ? Math.ceil(stage.offsetHeight * s) + "px" : "";
  }

  // run after first layout too — covers cached loads where 'load' has already fired before this runs
  requestAnimationFrame(fit);
  window.addEventListener("resize", fit);
  window.addEventListener("load", fit);

  // fires reliably once the column has a real measured width (images/fonts settled); guard against the
  // feedback loop from our own height write by only re-fitting when the WIDTH actually changed
  if ("ResizeObserver" in window) {
    new ResizeObserver(function () {
      if (media.clientWidth !== lastW) fit();
    }).observe(media);
  }
})();

/* ---- Industries masthead — scale the fixed 1040 design stage to fit the media column ----
   Same idea as the platform masthead, but anchored top-LEFT: the fleet deliberately runs past the
   column's right edge (the section clips it at the viewport), so shrinking has to pull that overhang
   in proportionally instead of pushing the composition around. */
(function () {
  var media = document.querySelector(".ind-hero__media");
  var stage = media && media.querySelector(".ind-hero__stage");
  if (!media || !stage) return;

  var DESIGN = 1040; // stage width = the media-column width at the 1920 design
  var lastW = -1;

  function fit() {
    var avail = media.clientWidth;
    if (!avail) return;
    lastW = avail;
    var s = Math.min(1, avail / DESIGN);
    stage.style.transform = s >= 1 ? "" : "scale(" + s + ")";
    // transform doesn't shrink the layout box → reserve the REAL (scaled) height, or the copy centres
    // against the unscaled 700px and the section keeps a big empty gap below the fleet
    media.style.height = (s < 1) ? Math.ceil(stage.offsetHeight * s) + "px" : "";
  }

  requestAnimationFrame(fit);
  window.addEventListener("resize", fit);
  window.addEventListener("load", fit);
  if ("ResizeObserver" in window) {
    new ResizeObserver(function () { if (media.clientWidth !== lastW) fit(); }).observe(media);
  }
})();

/* ---- Solutions masthead — scale the fixed 960 design stage to fit the media column ----
   Same scaler as the other two mastheads. 960 is the composition's own bounding box (not the column
   width), so at 1920 it lands unscaled and simply sits flush with the container's right edge. */
(function () {
  var media = document.querySelector(".solutions-hero__media");
  var stage = media && media.querySelector(".solutions-hero__stage");
  if (!media || !stage) return;

  var DESIGN = 960;
  var lastW = -1;

  function fit() {
    var avail = media.clientWidth;
    if (!avail) return;
    lastW = avail;
    var s = Math.min(1, avail / DESIGN);
    stage.style.transform = s >= 1 ? "" : "scale(" + s + ")";
    // transform leaves the layout box unscaled → reserve the REAL height, or the copy centres against
    // the full 600 and the section keeps a big empty gap under the composition
    media.style.height = (s < 1) ? Math.ceil(stage.offsetHeight * s) + "px" : "";
  }

  requestAnimationFrame(fit);
  window.addEventListener("resize", fit);
  window.addEventListener("load", fit);
  if ("ResizeObserver" in window) {
    new ResizeObserver(function () { if (media.clientWidth !== lastW) fit(); }).observe(media);
  }
})();

/* ---- Solutions masthead — "Trip playback" swaps the event card over to its clip ----
   The card keeps its header and grows downward into a callout; closing it rewinds the clip so the
   next open starts from the top. Muted loop, no controls — the same treatment as the other clips. */
(function () {
  var card = document.querySelector(".solutions-hero__event");
  if (!card) return;
  var openBtn = card.querySelector(".solutions-hero__event-btn");
  var closeBtn = card.querySelector(".solutions-hero__event-close");
  var video = card.querySelector(".solutions-hero__event-video");
  if (!openBtn || !closeBtn || !video) return;

  var clip = card.querySelector(".solutions-hero__event-clip");
  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var canTween = typeof gsap !== "undefined" && !reduce;

  // The card is pinned by its bottom edge, so growing it moves only its top — it unfolds upwards,
  // past the fatigue badge, and nothing below it shifts. `height:auto` can't be tweened, so both
  // ends are measured around the class flip and the tween runs between those two numbers.
  var setState = function (open) {
    var from = card.offsetHeight;                 // may be mid-tween if you click again quickly
    if (canTween) gsap.killTweensOf(card);
    card.classList.toggle("is-open", open);       // CSS swaps the button for the clip + close control
    if (!canTween) return;
    gsap.set(card, { clearProps: "height" });     // measure the NATURAL height, not the one we set
    var to = card.offsetHeight;
    gsap.fromTo(card, { height: from }, {
      height: to,
      duration: 0.42,
      ease: "power3.out",
      onComplete: function () { gsap.set(card, { clearProps: "height" }); }
    });
    // the clip is only display:block from this frame on, so it would otherwise pop in at full strength
    if (open && clip) gsap.fromTo(clip, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, delay: 0.1 });
  };

  openBtn.addEventListener("click", function () {
    setState(true);
    video.muted = true;
    video.loop = true;
    var p = video.play();
    if (p && p.catch) p.catch(function () {}); // autoplay can still be refused; the frame just sits
  });

  closeBtn.addEventListener("click", function () {
    setState(false);
    video.pause();
    try { video.currentTime = 0; } catch (e) {}
  });
})();

/* ---- Platform S4 media — scale the fixed-design composition to fit the column below 1600 (like masthead) ---- */
(function () {
  var media = document.querySelector(".platform-s4__media");
  var stage = media && media.querySelector(".platform-s4__stage");
  if (!media || !stage) return;

  // Two frozen design sizes (must match the __stage widths in SCSS): 730 for the desktop 901–1600 range,
  // 760 for mobile ≤760. In between (761–900) and above 1600 the stage is fluid → no scaling.
  var mm = function (q) { return window.matchMedia && window.matchMedia(q).matches; };
  var lastW = -1;
  function fit() {
    var DESIGN;
    if (mm("(max-width: 1600px)") && mm("(min-width: 901px)")) DESIGN = 730;
    else if (mm("(max-width: 760px)")) DESIGN = 760;
    else { stage.style.transform = ""; media.style.height = ""; lastW = -1; return; }
    var avail = media.clientWidth;
    if (!avail) return;
    lastW = avail;
    var s = Math.min(1, avail / DESIGN);
    stage.style.transform = s >= 1 ? "" : "scale(" + s + ")";
    // transform doesn't shrink the layout box → reserve the REAL (scaled) height (offsetHeight * s, not
    // getBoundingClientRect which can read back unscaled and over-reserve).
    media.style.height = (s < 1) ? Math.ceil(stage.offsetHeight * s) + "px" : "";
  }

  requestAnimationFrame(fit);
  window.addEventListener("resize", fit);
  window.addEventListener("load", fit);
  if ("ResizeObserver" in window) {
    new ResizeObserver(function () { if (media.clientWidth !== lastW) fit(); }).observe(media);
  }
})();

/* ---- Machine translation: notice it, and keep SplitText out of its way ----
   A translator walks text nodes. A heading SplitText has rebuilt as one element per character gives
   it a hundred one-letter nodes, which it translates separately and glues back together as nonsense
   ("Ever rything, in one plaf ace"). The only real fix is not to split translated text at all.

   Two signals, because browsers do not agree. Chromium and the Google widget stamp the root element
   with translated-ltr/translated-rtl. Firefox advertises nothing at all, so the fallback is the one
   thing every translator has to do: rewrite the text. That is watched only on the elements handed to
   SplitText, where nothing in this theme ever writes — the animated counters assign textContent,
   which replaces the node and reports as childList, not as the characterData change watched here. */
(function () {
  var STAMP = /(^|\s)translated-(ltr|rtl)(\s|$)/;
  var translated = STAMP.test(document.documentElement.className);
  var undos = [];
  var textWatch = null;

  var flush = function () {
    if (translated) return;
    translated = true;
    if (textWatch) { textWatch.disconnect(); textWatch = null; }
    undos.splice(0).forEach(function (fn) { fn(); });
  };

  window.rooyaTranslated = function () { return translated; };

  // Hands the split back the moment a translator touches it, instead of waiting for the intro to
  // finish — otherwise the visitor watches mangled text play all the way through its animation.
  window.rooyaSplitGuard = function (roots, undo) {
    if (translated || !undo) { if (translated && undo) undo(); return; }
    if (!textWatch) return;
    undos.push(undo);
    roots.forEach(function (el) {
      if (el && el.nodeType === 1) textWatch.observe(el, { characterData: true, subtree: true });
    });
  };

  if (translated || !window.MutationObserver) return;

  new MutationObserver(function () {
    if (STAMP.test(document.documentElement.className)) flush();
  }).observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

  textWatch = new MutationObserver(flush);
})();

/* ---- Text whose script does not match the page direction ----
   The Arabic pages are rendered with <html dir="rtl">, but their copy is only translated a piece at
   a time, so English sentences sit inside an RTL document for as long as that takes. The bidi
   algorithm then does what it is supposed to do and puts each sentence's trailing full stop at the
   paragraph's end, which in RTL is the LEFT: ".Everything, in one place".

   The fix is to tell the browser what each block actually is. A block holding no Arabic at all is
   English, so it is given dir="ltr" and reads correctly; a block holding any Arabic is left alone
   and keeps the page's RTL. That is why the test is "contains Arabic" rather than dir="auto", which
   decides from the FIRST strong character and would call a sentence opening with the brand name —
   "Rooya يقدم..." — an English one and lay the whole Arabic sentence out backwards.

   It also undoes itself: as each string is translated the block starts containing Arabic and stops
   being touched. Nothing has to be removed later.

   Alignment is pinned before dir is changed. text-align: start/end resolve against the element's own
   direction, so flipping dir on a heading that is right-aligned only by virtue of `start` would
   swing it across the page. Reading the computed value first and writing it back keeps every block
   exactly where the design put it. */
(function () {
  var root = document.documentElement;
  var rtl  = root.dir === "rtl" ||
             (window.getComputedStyle && getComputedStyle(root).direction === "rtl");

  // Arabic block, Supplement, Extended-A, and the presentation forms.
  var ARABIC = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;

  window.rooyaRTL      = function () { return rtl; };
  window.rooyaIsArabic = function (el) { return !!el && ARABIC.test(el.textContent || ""); };

  if (!rtl) return;

  var SKIP = /^(SCRIPT|STYLE|NOSCRIPT)$/;

  // A flex or grid container orders its CHILDREN, not merely its words, so flipping direction on one
  // can move an icon to the wrong side of its label — .login would put the avatar back on the wrong
  // side of the word. That is what this guard is for.
  //
  // But it only bites when there is something to reorder. A flex box whose only children are text
  // and <br> is a text block that happens to be centred with flex, and this theme has several: the
  // industries labels are `display: flex` purely to bottom-align one or two lines, and skipping them
  // left "Heavy Plant<br>& Machinery" with its ampersand thrown to the far end of the second line.
  var LAYOUT = /^(flex|grid|inline-flex|inline-grid)$/;

  var reorderable = function (el) {
    for (var i = 0; i < el.children.length; i++) {
      if (el.children[i].tagName !== "BR") return true;
    }
    return false;
  };

  // Does this element have a generated marker? A ::before or ::after is placed against the element's
  // INLINE-START edge, so `dir` decides which side of the words it sits on — the same way it decides
  // where an inline padding goes. An element with one is therefore never "just text": flipping its
  // direction moves furniture.
  //
  // This is why the tick lists broke. `.solutions-check li` is an ordinary block, so the flex guard
  // below never looked at it; it holds nothing but English words, so it was given dir="ltr" and its
  // tick and its padding both went back to the left — while the text-align pinned a line earlier
  // still held the words against the right. Ticks down one margin, words down the other.
  //
  // Skipping these costs nothing in return: a list item is a fragment, not a sentence, so there is
  // no trailing punctuation for the bidi algorithm to misplace.
  var marked = function (el, cs) {
    // A real list item has a ::marker, which is a marker box like any other and sits on the
    // inline-start side — but it is NOT a ::before, so checking only for those missed every <ul> in
    // the theme. The FAQ answers were the visible case: bullets stranded down the left margin with
    // their text against the right.
    if (cs.display.indexOf("list-item") !== -1) return true;

    var before = getComputedStyle(el, "::before").content;
    var after  = getComputedStyle(el, "::after").content;

    return (before && before !== "none") || (after && after !== "none");
  };

  // Does this element hold text OF ITS OWN, rather than only inheriting it from descendants?
  var ownText = function (el) {
    for (var i = 0; i < el.childNodes.length; i++) {
      var n = el.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue && n.nodeValue.trim()) return true;
    }
    return false;
  };

  // Every element that carries its own text, rather than a fixed list of tags. The theme sets plenty
  // of copy in divs — the industries carousel labels among them — and a tag list misses exactly
  // those: the first label rendered as "Heavy Plant / Machinery &", because the ampersand is a
  // neutral character and took the paragraph's RTL direction to the far end of its line.
  var mark = function () {
    var all = document.body ? document.body.getElementsByTagName("*") : [];

    Array.prototype.forEach.call(all, function (el) {
      if (!(el instanceof HTMLElement)) return;              // leaves SVG internals alone
      if (SKIP.test(el.tagName)) return;
      if (el.getAttribute("dir")) return;                    // author said so; leave it
      if (el.classList.contains("rooya-ltr-text")) return;   // already handled
      if (!ownText(el)) return;

      var cs = getComputedStyle(el);
      if (marked(el, cs)) return;
      if (LAYOUT.test(cs.display) && reorderable(el)) return;

      // A POSITIONED box is placed by its own direction too. `inset-inline-start` on an absolutely
      // positioned element resolves against that element's own writing direction, so handing it
      // dir="ltr" does not merely reorder its words — it moves the box to the other corner.
      //
      // That is what kept the "KSA Team" pill in the photo's left corner on the Arabic page after
      // its inset had already been made logical: the CSS was right, and this pass was quietly
      // overruling it. The same would have happened to every other floating label on the site —
      // the hero telemetry cards, the event callout, the status pills.
      //
      // Nothing is lost by leaving them alone. These are short labels, a few words with no trailing
      // punctuation, so there is no bidi ordering for this pass to improve in the first place.
      if (cs.position !== "static") return;

      // Arabic text is marked RTL explicitly. Nearly everywhere that is exactly what it already
      // inherits and the attribute changes nothing. It earns its place inside a region deliberately
      // pinned to `direction: ltr` to protect its geometry — the home-s2 stage — where a translated
      // label would otherwise inherit LTR and come out backwards.
      if (ARABIC.test(el.textContent)) {
        el.setAttribute("dir", "rtl");
        return;
      }

      // start/end are resolved against the direction THIS element actually sits in, not against the
      // document's. Those two used to be the same thing; they stopped being it the moment a region
      // could be pinned to LTR inside an RTL page, and reading `start` as "right" in there would
      // have thrown the stage's own labels to the wrong edge.
      var align = cs.textAlign;
      if (align === "start") align = cs.direction === "rtl" ? "right" : "left";
      else if (align === "end") align = cs.direction === "rtl" ? "left" : "right";

      el.style.textAlign = align;

      // A CLASS carrying `unicode-bidi: plaintext`, not dir="ltr". Both fix the thing this pass is
      // for — the trailing full stop of an English sentence being carried to the far end of an RTL
      // line — but `dir` does it by changing the element's `direction`, and `direction` drives the
      // layout as well as the letters: logical padding, logical insets, marker side, flex order.
      //
      // That is what kept biting. The FAQ answers are the plainest case: their `padding-inline-end`
      // clears the icon column, the icon sits on the left once mirrored, and dir="ltr" on the
      // paragraph turned "inline-end" back into "right" — so the gap opened on the side with
      // nothing in it. Same mechanism had already moved the KSA Team pill and the tick markers.
      //
      // plaintext sets only the bidi paragraph direction, from the content. `direction` is left
      // alone, so everything positional stays as the page laid it out, and the words still read
      // correctly. It is also self-retiring: once the copy is Arabic, plaintext resolves RTL.
      el.classList.add("rooya-ltr-text");
    });
  };

  // Runs NOW rather than on DOMContentLoaded. This script is enqueued in the footer, so the body it
  // walks is already parsed — and waiting loses a race it cannot afford.
  //
  // The intros start from rooyaFontsReady(), which resolves off document.fonts.load() plus one frame.
  // On a warm cache that lands well BEFORE DOMContentLoaded, so the masthead was splitting its
  // paragraph into lines before this pass had run. SplitText copies the parent's computed text-align
  // onto every line element it creates, and at that moment the computed value was still `start`.
  // Each line was therefore stamped `text-align: start` — and when this pass set dir="ltr" on the
  // paragraph a moment later, `start` on those lines resolved to LEFT. The copy sat left-aligned for
  // the whole intro and snapped back to the right the instant revert() removed the lines. That is the
  // "it changes when the animation ends" report: nothing was animating the alignment, the two states
  // simply disagreed about which edge `start` meant.
  mark();

  // Safety net in case this ever moves out of the footer. mark() skips whatever it has already done.
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", mark);
})();

/* ---- One gate for every per-character intro ----
   The entrance animations rebuild a heading as one element per character. Two conditions have to
   hold before that is safe, and every intro in this file asks the same question, so it is asked in
   one place. Direction is NOT one of them any more: the pass above gives an English heading on an
   Arabic page its own dir, so its characters are laid out in the order they were written and the
   intro plays exactly as it does in English. */
(function () {
  window.rooyaCanSplit = function () {
    return typeof SplitText !== "undefined" && !window.rooyaTranslated();
  };
})();

/* ---- Characters, or whole words where characters would not survive it ----
   Arabic is cursive. Its letters change shape according to their neighbours and join into a word, and
   that shaping is done per run of text — an element boundary ends a run. Give every letter its own
   inline-block box and each is shaped alone, so the browser draws the isolated form of all of them
   and the word comes apart into disconnected stumps. No amount of animation gives the joins back.

   So an Arabic heading is animated by WORD instead. A word boundary is where Arabic stops joining
   anyway, so nothing is broken, and the stagger still reads. Everything else is split by character
   as before. The stand-in returned for the word case carries the same shape a SplitText does —
   `.chars` to animate, `.revert()` to undo — so callers do not branch. */
window.rooyaSplitChars = function (words) {
  var root = words.elements && words.elements[0];

  // A SPLIT heading is the one place that still needs `direction`, not just `unicode-bidi`.
  //
  // SplitText rebuilds the heading as a row of inline-block boxes, and the bidi algorithm reads an
  // inline-block as a single neutral object rather than as the letter inside it. Neutrals carry no
  // direction of their own, so `plaintext` — which decides from the content — finds nothing to
  // decide on and falls back to the element's `direction`. On an Arabic page that is RTL, and the
  // English headline is laid out backwards for exactly as long as the intro runs.
  //
  // Naming the direction outright settles it. Safe to do here and nowhere else: split roots are
  // headings, which have no marker and no logical padding, and the direction pass has already
  // pinned their text-align to a physical value, so this cannot move them on the page.
  if (root && window.rooyaRTL && window.rooyaRTL() && !window.rooyaIsArabic(root)) {
    root.style.direction = "ltr";
  }

  if (window.rooyaIsArabic(root)) {
    // The stagger travels with the split, because it has to change with it. A heading holds roughly
    // ten times more characters than words, so the per-piece delay that reads as a sweep across
    // letters passes across five words in a twentieth of a second — no stagger at all, just a flat
    // fade. Widening it keeps the entrance deliberate at either granularity.
    return { chars: words.words, stagger: 0.06, revert: function () {} };
  }

  var chars = new SplitText(words.words, { type: "chars", charsClass: "s1-char" });
  chars.stagger = 0.01;

  return chars;
};

/* ---- Put split headings back together once their intro has played ----
   SplitText rebuilds a heading as one element per character. The entrance animations need that for
   about a second; everything afterwards is worse for it. Browser translation walks text nodes, so it
   reads each fragment as its own phrase and reassembles the results into nonsense — a Danish visitor
   got "Built af ar round your r fleet, not af a fixed templaf ate" — and skips some headings
   entirely. Ctrl+F stops matching a split heading, and copying one gives you gapped text.

   Every intro here is toggleActions "play none none none": it plays once and never reverses, so
   nothing needs the split elements after the timeline ends. Reverting hands the markup back as an
   ordinary text node. Translators watch the DOM, so a page translated mid-animation re-translates
   the restored text by itself — no need to detect that translation happened, which is just as well,
   because the browsers that do it do not agree on how to advertise it. */
window.rooyaUnsplit = function (tl, splits) {
  var list = [].concat(splits).filter(Boolean);
  if (!tl || !list.length) return tl;

  var undo = function () {
    list.forEach(function (sp) { if (sp && sp.revert) sp.revert(); });
    list = [];
  };

  // Chained rather than assigned: several of these timelines already carry an onComplete of their
  // own, and eventCallback replaces whatever was there.
  var prev = tl.eventCallback("onComplete");
  tl.eventCallback("onComplete", function () {
    if (prev) prev.apply(this, arguments);
    undo();
  });

  // Second trigger: a translator arriving mid-animation does not get to wait its turn.
  if (window.rooyaSplitGuard) {
    var roots = [];
    list.forEach(function (sp) {
      if (sp && sp.elements) Array.prototype.push.apply(roots, [].slice.call(sp.elements));
    });
    window.rooyaSplitGuard(roots, undo);
  }

  return tl;
};

/* ---- Masthead hero — GSAP intro (h1 chars, sub lines, ticks, CTAs) ---- */
(function () {
  var section = document.querySelector(".masthead-home");
  if (!section) return;
  // CSS holds this hidden through the first paint; nothing below may return without releasing it
  var wrap = section.querySelector(".container");
  var copy = section.querySelector(".masthead-home__hero-copy");

  // Respect reduced motion — leave everything as authored, no animation.
  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!copy || typeof gsap === "undefined" || reduce) { rooyaRelease(wrap); return; }

  if (window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
  // Not on a translated page: a split heading is what the translator turns into nonsense, and the
  // plain-fade branch below is a perfectly good intro.
  var hasSplit = window.rooyaCanSplit();
  if (hasSplit) gsap.registerPlugin(SplitText);

  // Hide the copy immediately (before fonts settle) so nothing flashes in unanimated.
  gsap.set(copy, { autoAlpha: 0 });
  rooyaRelease(wrap);   // the copy carries its own visibility now

  function run() {
    var h1 = copy.querySelector("h1");
    var p = copy.querySelector(".masthead-home__hero-sub");
    var ticks = copy.querySelectorAll(".masthead-home__tick");
    var btns = copy.querySelectorAll(".masthead-home__cta .btn");

    gsap.set(copy, { autoAlpha: 1 });      // reveal container; children start hidden via .from below
    gsap.set(btns, { transition: "none" }); // the .btn CSS transition fights GSAP's tween — silence it

    var tl = gsap.timeline({
      defaults: { ease: "power4.out" },
      // restore the buttons' hover transition once the intro is done
      onComplete: function () { gsap.set(btns, { clearProps: "transition" }); }
    });

    if (hasSplit) {
      // h1 -> words -> chars (chars rise in); sub -> lines (masked, slide up)
      var words = new SplitText(h1, { type: "words", wordsClass: "s1-word" });
      var chars = rooyaSplitChars(words);
      var lines = new SplitText(p, { type: "lines", linesClass: "s1-line" });
      gsap.set(words.words, { display: "inline-block" });
      gsap.set(lines.lines, { overflow: "hidden" });

      tl.from(chars.chars, { autoAlpha: 0, y: 50, duration: 1.1, stagger: chars.stagger }, 0)
        .from(lines.lines, { yPercent: 120, autoAlpha: 0, duration: 1, stagger: 0.08 }, 0.45);

      rooyaUnsplit(tl, [words, chars, lines]);
    } else {
      // graceful fallback if SplitText didn't load
      tl.from(h1, { autoAlpha: 0, y: 40, duration: 1.1 }, 0)
        .from(p, { autoAlpha: 0, y: 30, duration: 1 }, 0.45);
    }

    tl.from(ticks, { autoAlpha: 0, y: 24, duration: 0.7, stagger: 0.12 }, 0.7)
      .from(btns, { autoAlpha: 0, y: 40, duration: 0.8, stagger: 0.12 }, 0.9);
  }

  // Wait for fonts so line-splitting measures correctly (mirrors the reference).
  rooyaFontsReady(run);
})();

/* ---- Section intros on scroll (h2 chars, paragraph lines, cards, CTA) ---- */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  gsap.registerPlugin(ScrollTrigger);
  // Not on a translated page: a split heading is what the translator turns into nonsense, and the
  // plain-fade branch below is a perfectly good intro.
  var hasSplit = window.rooyaCanSplit();
  if (hasSplit) gsap.registerPlugin(SplitText);

  // Each entry: a section root + which parts (relative to it) to animate in sequence. The heading
  // (h2 chars) + paragraph (masked lines) always play the standard intro; extra groups fade up.
  //   head     — override the head container (default .section-head) that holds the h2 + p; may also
  //              name the section root itself
  //   pre      — small lead-in above the heading (eyebrow/subtitle): goes FIRST and pushes the rest back
  //   headStill — leave the h2 + paragraph alone and animate only the rest (cards, btns, fade…). The
  //              heading keeps its slot in the timing, so what follows it lands where it always did
  //   cards    — fade up together (stagger)
  //   cardsAt  — timeline position for `cards` (default 0.65), to run them closer under the heading
  //   extra    — small elements that fade up just after the text (e.g. slider nav arrows)
  //   extraAt  — timeline position for `extra` (default 0.7), for holding it back off the paragraph
  //   lines    — further paragraphs that get the head paragraph's masked-line treatment, not a plain
  //              fade — for copy blocks that run to more than one paragraph
  //   btns     — buttons fade up
  //   counters — home-s8 stat cards: fade up one by one, each starts its count-up on its own finish
  //   fade     — trailing block(s) that just fade up last (slider / preview / disclaimer)
  //   hide     — extra selectors to keep invisible until the intro runs (for use with `build`)
  //   build    — fn(tl, root, at) appending a section-specific sequence, starting at position `at`
  //   mediaAt  — where `build` starts on the timeline, i.e. how long the composition waits for the copy
  //   mediaFirst — below 900 play `build` FIRST and let the copy follow it (see the note in run())
  //   start    — ScrollTrigger start (default "top 75%") ; fadeAt — timeline position for `fade`
  //   scoreAt  — timeline position at which a [data-score-defer] ring starts counting (default 0.75)
  var SECTIONS = [
    { root: ".home-s2", btns: ".section-head .btn" },
    // Two entries for one section, because the platform page carries its own copy of it and wants the
    // heading left alone. The roots are mutually exclusive, so exactly one matches per page.
    { root: ".home-s5:not(.home-s5--platform)", cards: ".home-s5__card", btns: ".center-cta .btn" },
    { root: ".home-s5--platform", cards: ".home-s5__card", headStill: true },
    { root: ".home-s7", head: ".home-s7__head .text-container", extra: ".home-s7__nav",
      fade: ".home-s7__swiper", start: "top 62%", fadeAt: 0.85 },
    { root: ".home-s8", counters: ".home-s8__stat", fade: ".home-s8__disclaimer",
      start: "top 68%", fadeAt: 1.2 },
    { root: ".home-s9", head: ".home-s9__text", btns: ".home-s9__text .btn",
      fade: ".home-s9__preview", start: "top 75%", fadeAt: 0.95 },
    // platform page — "everything you need": head text + nav in, then the slider/deck fades up (same
    // pattern as home-s7). Works on desktop (slider) and mobile (deck).
    { root: ".platform-s1", head: ".platform-s1__head-text", extra: ".platform-s1__nav",
      fade: ".platform-s1__swiper", start: "top 75%", fadeAt: 0.85 },
    // platform-s3 is deliberately NOT here. Every key this driver offers animates copy — pre, the
    // split heading and paragraph, the checklist — and that section's text is meant to sit still now.
    // All that was left worth keeping was the slider fading up, which is too small a job to hold a
    // config entry open for: it runs on its own a few hundred lines down, next to the slider's other
    // modules. Re-adding a text intro here means putting the entry back, not editing that module.
    // platform-s4 is not here either, for the same reason as platform-s3: every key this driver
    // offers drives copy. Its composition is not a fade but a four-piece assembly with a deferred
    // counter, so it moved out whole — see the platform-s4 module further down.
    // platform-s5 has moved out too, alongside s3 and s4 — its copy is static now and its
    // composition runs from its own module further down.
    // platform-s6 has moved out as well — copy static, diagram on its own module further down.
    // platform-s7 has moved out too. What is left on this page is s1, whose head text and slider
    // still run through the driver.
    // industries page — "tailored solutions": same shape as home-s5 (heading, card grid, centred CTA).
    // The head here is heading-only, hence the optional paragraph below.
    { root: ".ind-s1", head: ".ind-s1__head", cards: ".ind-s1__card", btns: ".ind-s1__cta .btn",
      btnsOwn: true },
    // solutions page — "targeted fleet solutions": the same shape as ind-s1, minus the CTA. Heading
    // only in the head, then the card grid staggers up.
    { root: ".solutions-cbox", head: ".solutions-cbox__head", cards: ".solutions-cbox__card" },
    // solutions page — "spot the warning signs earlier": eyebrow, heading and the opening paragraph
    // get the standard treatment; the SECOND paragraph, the "Where this helps" title and the four
    // feature rows follow as one cascade (querySelectorAll hands them back in document order, so the
    // stagger runs down the column). Then the artwork assembles on the right.
    // solutions-s1 has moved out, the same way the platform sections did: its copy column — subtitle,
    // heading, both paragraphs, the "Where this helps" title and the four tiles — is static now, and
    // the photo composition runs from its own module further down.
    // solutions page — "make fleet risk easier to govern". The centred head is deliberately still
    // (headStill, and no `pre` — the subtitle above it does not move either); what does animate is the
    // "Built for safety oversight" title, then the five tiles, then the closing note.
    //
    // The positions are pulled well forward of what they were. They used to be spaced around a head
    // intro that ran for the first second — with that gone, the old 0.7 / 1.05 / 2 left the section
    // sitting visibly empty while the timeline played to itself.
    { root: ".solutions-s2", head: ".solutions-s2__head", headStill: true,
      extra: ".solutions-s2__oversight-title", extraAt: 0.3,
      fade: ".solutions-s2__note", fadeAt: 1.5,
      mediaAt: 0.6,
      hide: ".solutions-s2__col",
      build: function (tl, root, at) {
        var cols = root.querySelectorAll(".solutions-s2__col");
        if (cols.length) tl.fromTo(cols,
          { autoAlpha: 0, y: 44 },
          { autoAlpha: 1, y: 0, duration: 0.7, ease: "power3.out", stagger: 0.12 }, at);
      } },
    // solutions-s3 has moved out too — its copy column is static and the photo fades up from its own
    // module further down. Every key it used here (pre, head, extra) drove text.
    // solutions-s5 has moved out as well: subtitle, heading, paragraph, the "Optimisation in
    // practice" label and the four rows are all static now, and the map composition runs from its own
    // module further down.
    // company page — "technology backed by people who stay close". This was TWO entries, because the
    // head used to be a centred block with a 117px gap under it: on one trigger either the head fired
    // far too early for the photo, or the photo animated below the fold. The head now sits inside the
    // copy column, so both halves are one block on screen and one trigger is the honest answer — and
    // the h2 that entry animated separately is reached through `head` here.
    { root: ".company-s1__grid", head: ".company-s1__copy", pre: ".company-s1__subtitle",
      lines: ".company-s1__body", start: "top 80%",
      mediaAt: 0.7,
      // The whole slider is the media unit, not the <img> inside it. Hiding the images and revealing
      // one of them would strand the second slide: `hide` matches every image, `build` only ever
      // reached the first, so slide two would have stayed invisible for good once you clicked next.
      hide: ".company-s1__swiper, .company-s1__card, .company-s1__card li, .company-s1__arrow, .company-s1__dots",
      build: function (tl, root, at) {
        var photo  = root.querySelector(".company-s1__swiper");
        var card   = root.querySelector(".company-s1__card");
        var items  = root.querySelectorAll(".company-s1__card li");
        var chrome = root.querySelectorAll(".company-s1__arrow, .company-s1__dots");

        if (photo) tl.fromTo(photo,
          { autoAlpha: 0, y: 44 },
          { autoAlpha: 1, y: 0, duration: 0.85, ease: "power3.out" }, at);

        // Controls last. They belong to the photo rather than to the section, so arriving with it —
        // or worse, sitting there before it, which is what they did — reads as chrome that has come
        // loose from the thing it operates.
        if (chrome.length) tl.fromTo(chrome,
          { autoAlpha: 0 },
          { autoAlpha: 1, duration: 0.4, ease: "power1.out" }, at + 0.55);

        // the callout overhangs the photo's bottom-left corner, so it arrives from that side
        if (card) tl.fromTo(card,
          { autoAlpha: 0, x: -44, y: 20 },
          { autoAlpha: 1, x: 0, y: 0, duration: 0.65, ease: "power3.out" }, at + 0.3);

        // the card lands carrying only its title and the four points tick in after it, so the list
        // reads as being filled in rather than arriving pre-written
        if (items.length) tl.fromTo(items,
          { autoAlpha: 0, y: 14 },
          { autoAlpha: 1, y: 0, duration: 0.45, ease: "power3.out", stagger: 0.09 }, at + 0.55);
      } },
    // company page — "built where accountability matters": copy on the right, one photo on the left.
    // The "Why that matters" label and its three rows follow the paragraph as one cascade. The photo
    // goes through `build` rather than `fade` because it is first in the DOM and therefore stacks on
    // top below 900 — which is what mediaFirst is for, and mediaFirst needs a build to reorder.
    // company-s2 has moved out, like the platform and solutions sections: subtitle, heading,
    // paragraph, the label and the checklist under it are all static now. The photo and the connector
    // lines run from their own module further down.
    // company-s3's upper half was text only — centred head, subtitle, and the two intro columns
    // revealed line by line — so removing its intro left nothing for an entry to hold. The office
    // cards in the lower half were never here: they have always run from their own module.
    // company-s4 held its intro copy only, and that copy is static now, so the entry went with it.
    // The nine-card leadership grid was never here — it has always run from its own module.
    // company-s5 (FAQ) has no entrance at all any more — head, subtitle and the question rows are all
    // static. Its accordion is untouched by this: the disclosure lives in its own module and tweens
    // the panel HEIGHT, never the row's opacity or transform, so opening a question still animates.
    // hardware page — the intro band under the masthead: one centred paragraph and a button, no
    // heading at all. With no h2 above it the paragraph leads instead of waiting out its slot.
    { root: ".hardware-s1", head: ".hardware-s1__inner", btns: ".hardware-s1__inner .btn" }
  ];

  var list = function (root, sel) { return sel ? [].slice.call(root.querySelectorAll(sel)) : []; };

  SECTIONS.forEach(function (cfg) {
    var root = document.querySelector(cfg.root);
    if (!root) return;
    // querySelector only ever looks at DESCENDANTS, so a `head` selector naming the section itself
    // would come back null and the section would silently never animate. The matches() check first
    // makes `head: <the root>` mean what it reads like, rather than quietly doing nothing.
    var head = cfg.head
      ? ((root.matches && root.matches(cfg.head)) ? root : root.querySelector(cfg.head))
      : root.querySelector(".section-head");
    var h2 = head && head.querySelector("h2");
    var p = head && head.querySelector("p"); // optional — some heads are heading-only (e.g. ind-s1)
    // …and the mirror case: a block split off from its heading onto its own trigger has a paragraph
    // but no h2. Either one alone is enough to animate; neither means there is nothing to do here.
    if (!h2 && !p) return;
    var pre      = list(root, cfg.pre);
    var cards    = list(root, cfg.cards);
    var extra    = list(root, cfg.extra);
    var btns     = list(root, cfg.btns);
    var counters = list(root, cfg.counters);
    var fade     = list(root, cfg.fade);
    var lines    = list(root, cfg.lines);

    // home-s8 counters: freeze at 0% and stash targets so each can count up once it has faded in
    counters.forEach(function (s) {
      var num = s.querySelector(".num");
      var bar = s.querySelector(".bar span");
      if (!num || !bar) return;
      num.setAttribute("data-target", parseInt(num.textContent, 10) || 0);
      num.textContent = "0%";
      bar.setAttribute("data-target", bar.style.width || "0%");
      bar.style.width = "0%";
    });
    var countUp = function (s) {
      var num = s.querySelector(".num");
      var bar = s.querySelector(".bar span");
      if (!num || !bar) return;
      var target = +num.getAttribute("data-target");
      bar.style.width = bar.getAttribute("data-target");
      var start = null, dur = 1100;
      var step = function (ts) {
        if (!start) start = ts;
        var pr = Math.min((ts - start) / dur, 1);
        num.textContent = Math.round((1 - Math.pow(1 - pr, 3)) * target) + "%";
        if (pr < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    // hide every animated piece until the intro plays (nothing pops in before its scroll trigger).
    // A headStill section leaves its h2 and paragraph OUT of this: they are never tweened, so nothing
    // would ever hand their visibility back and they would stay hidden until fonts-ready at best.
    var hideAll = (cfg.headStill ? [] : [h2, p].filter(Boolean))
      .concat(pre, cards, extra, btns, counters, fade, lines, list(root, cfg.hide));
    gsap.set(hideAll, { autoAlpha: 0 });

    var run = function () {
      gsap.set(hideAll, { autoAlpha: 1 });                     // reveal containers; pieces re-hidden via .from
      if (btns.length) gsap.set(btns, { transition: "none" }); // silence .btn CSS transition vs GSAP

      // Below 900 every split section stacks with its media block ABOVE the copy, so a copy-first
      // intro animates the half the reader has already scrolled past while the picture sits there
      // waiting. `mediaFirst` sections flip the running order there: the composition plays from the
      // top of the timeline and the copy follows it. Read once, at load — a resize across the
      // breakpoint keeps whichever order the page started with.
      var mediaFirst = typeof cfg.build === "function" && cfg.mediaFirst &&
        window.matchMedia && window.matchMedia("(max-width: 900px)").matches;

      var tl = gsap.timeline({
        defaults: { ease: "power4.out" },
        scrollTrigger: {
          // the head is the copy block, which on mobile sits BELOW the media — waiting for it there
          // would start the timeline with the composition already half way up the screen
          trigger: mediaFirst ? root : head,
          start: cfg.start || "top 75%", toggleActions: "play none none none"
        },
        onComplete: function () {
          if (btns.length && !cfg.btnsOwn) gsap.set(btns, { clearProps: "transition" });
        }
      });

      // published so a block that was split onto its own trigger can wait for this half to finish
      // before playing its own — see the company-s3 offices module
      root._introTl = tl;

      // `mt` is where the composition starts, `tBase` where the copy does. Only one of them leads.
      var mt = 0, tBase = 0;
      if (mediaFirst) {
        cfg.build(tl, root, 0);
        tBase = Math.max(0, tl.duration() - 0.5);   // the copy comes in under the composition's tail
      }

      // A `pre` lead-in plays on its own first, and every position below shifts by `t` so the rest of
      // the sequence keeps its internal spacing. Sections without `pre` add nothing beyond `tBase`.
      if (pre.length) tl.from(pre, { autoAlpha: 0, y: 18, duration: 0.55, stagger: 0.1 }, tBase);
      var t = tBase + (pre.length ? 0.3 : 0);
      if (!mediaFirst) mt = (cfg.mediaAt || 0) + t;

      // with no heading above it there is nothing for the paragraph to wait for, so it leads
      var pAt = h2 ? 0.45 + t : t;
      // `lines` normally queues behind the head's own paragraph; with no paragraph up there it takes
      // that slot itself instead of waiting out an empty one
      var linesAt = p ? pAt + 0.4 : pAt;

      // masked lines: the wrapper clips while the line slides up out of it
      var lineSplits = [];
      var maskLines = function (el, dur, at) {
        var split = new SplitText(el, { type: "lines", linesClass: "s1-line" });
        lineSplits.push(split);
        gsap.set(split.lines, { overflow: "hidden" });
        tl.from(split.lines, { yPercent: 120, autoAlpha: 0, duration: dur, stagger: 0.08 }, at);
      };

      // headStill: the heading and paragraph are not touched at all. Their SLOTS are still spent —
      // `t`, `pAt` and `linesAt` above are unchanged — so everything queued behind them lands exactly
      // where it did before, and turning this on does not quietly re-time the rest of the section.
      var headMoves = !cfg.headStill;
      if (hasSplit) {
        if (h2 && headMoves) {
          var words = new SplitText(h2, { type: "words", wordsClass: "s1-word" });
          var chars = rooyaSplitChars(words);
          gsap.set(words.words, { display: "inline-block" });
          tl.from(chars.chars, { autoAlpha: 0, y: 50, duration: 1.1, stagger: chars.stagger }, t);
        }
        if (p && headMoves) maskLines(p, 1, pAt);
        lines.forEach(function (el, i) { maskLines(el, 0.9, linesAt + i * 0.3); });

        // `words`/`chars` are var-scoped to this closure, so they read as undefined when the heading
        // was left still — which rooyaUnsplit filters out.
        rooyaUnsplit(tl, lineSplits.concat([words, chars]));
      } else {
        // graceful fallback if SplitText didn't load — same order, plain fades
        if (h2 && headMoves) tl.from(h2, { autoAlpha: 0, y: 40, duration: 1.1 }, t);
        if (p && headMoves) tl.from(p, { autoAlpha: 0, y: 30, duration: 1 }, pAt);
        lines.forEach(function (el, i) {
          tl.from(el, { autoAlpha: 0, y: 24, duration: 0.9 }, linesAt + i * 0.3);
        });
      }

      // NB: this tween owns the cards' transform for good — GSAP leaves an inline `transform` behind
      // and neutralises the standalone `translate`/`rotate`/`scale` properties too. A card that also
      // wants a CSS hover lift has to move on something else entirely (see .ind-s1__card, which uses
      // `top`), or be wrapped so the hover sits on a different element.
      if (cards.length) tl.from(cards, { autoAlpha: 0, y: 44, duration: 0.7, stagger: 0.12 },
        (cfg.cardsAt != null ? cfg.cardsAt : 0.65) + t);
      if (extra.length) tl.from(extra, { autoAlpha: 0, y: 26, duration: 0.6, stagger: 0.1 },
        (cfg.extraAt != null ? cfg.extraAt : 0.7) + t);
      // btnsOwn: the card grid is taller than the viewport, so a CTA tied to the head's trigger would
      // fade up far below the fold and read as "not animated" — give it its own trigger instead
      if (btns.length && cfg.btnsOwn) {
        gsap.fromTo(btns, { autoAlpha: 0, y: 40 }, {
          autoAlpha: 1, y: 0, duration: 0.8, ease: "power4.out", stagger: 0.12,
          scrollTrigger: { trigger: btns[0], start: "top 92%", toggleActions: "play none none none" },
          onComplete: function () { gsap.set(btns, { clearProps: "transition" }); }
        });
      } else if (btns.length) {
        tl.from(btns, { autoAlpha: 0, y: 40, duration: 0.8, stagger: 0.12 }, (cards.length ? 1.2 : 0.7) + t);
      }

      // counters fade up one by one; each begins counting only once its own fade-up finishes
      counters.forEach(function (s, i) {
        tl.fromTo(s, { autoAlpha: 0, y: 40 },
          { autoAlpha: 1, y: 0, duration: 0.6, onComplete: function () { countUp(s); } }, 0.65 + i * 0.18 + t);
      });

      // trailing block (slider / preview / disclaimer) — plain fade up, last
      if (fade.length) tl.fromTo(fade, { autoAlpha: 0, y: 50 },
        { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.12 }, (cfg.fadeAt != null ? cfg.fadeAt : 0.9) + t);

      // section-specific sequence, appended to the same timeline so it stays in step with the copy
      // (already appended above when it leads)
      if (typeof cfg.build === "function" && !mediaFirst) cfg.build(tl, root, mt);

      // a driver-score ring inside this section counts up on the intro's schedule, not on its own
      // scroll observer (see the [data-score-defer] branch in the rings module). It starts while the
      // last block is still settling — waiting for the timeline to actually END read as a long pause.
      var deferred = [].slice.call(root.querySelectorAll("[data-score-defer]"));
      if (deferred.length) {
        tl.call(function () {
          deferred.forEach(function (r) { if (r._runScore) r._runScore(); });
        }, null, (cfg.scoreAt != null ? cfg.scoreAt : 0.75) +
                 (typeof cfg.build === "function" ? mt : t));
      }
    };

    rooyaFontsReady(run);
  });
})();

/* ---- Industries (home-s6) — mobile (<=600) text + slider entrance ----
   Desktop gets the expanding-circle reveal below; on mobile that reveal no-ops, so this gives the
   section its own intro: heading char-by-char, paragraph masked lines, then the slider + nav fade up. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  gsap.registerPlugin(ScrollTrigger);
  // Not on a translated page: a split heading is what the translator turns into nonsense, and the
  // plain-fade branch below is a perfectly good intro.
  var hasSplit = window.rooyaCanSplit();
  if (hasSplit) gsap.registerPlugin(SplitText);

  var root = document.querySelector(".home-s6");
  if (!root) return;
  var head = root.querySelector(".section-head");
  var h2 = head && head.querySelector("h2");
  var p = head && head.querySelector("p");
  var swiper = root.querySelector(".home-s6__swiper");
  var nav = root.querySelector(".home-s6__nav");
  if (!h2 || !p) return;

  gsap.matchMedia().add("(max-width: 600px)", function () {
    var splits = [];
    var hideAll = [h2, p, swiper, nav].filter(Boolean);
    gsap.set(hideAll, { autoAlpha: 0 }); // hidden until the intro plays (no flash before the trigger)

    var run = function () {
      gsap.set(hideAll, { autoAlpha: 1 }); // reveal containers; the pieces re-hide via .from below

      var tl = gsap.timeline({
        defaults: { ease: "power4.out" },
        scrollTrigger: { trigger: head, start: "top 80%", toggleActions: "play none none none" }
      });

      if (hasSplit) {
        var words = new SplitText(h2, { type: "words", wordsClass: "s1-word" });
        var chars = rooyaSplitChars(words);
        var lines = new SplitText(p, { type: "lines", linesClass: "s1-line" });
        splits.push(words, chars, lines);
        gsap.set(words.words, { display: "inline-block" });
        gsap.set(lines.lines, { overflow: "hidden" });
        tl.from(chars.chars, { autoAlpha: 0, y: 40, duration: 1.0, stagger: chars.stagger }, 0)
          .from(lines.lines, { yPercent: 120, autoAlpha: 0, duration: 0.9, stagger: 0.08 }, 0.4);

        rooyaUnsplit(tl, splits);
      } else {
        tl.from(h2, { autoAlpha: 0, y: 40, duration: 1.0 }, 0)
          .from(p, { autoAlpha: 0, y: 30, duration: 0.9 }, 0.4);
      }

      if (swiper) tl.fromTo(swiper, { autoAlpha: 0, y: 50 }, { autoAlpha: 1, y: 0, duration: 0.9 }, 0.7);
      if (nav) tl.fromTo(nav, { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.6 }, 1.0);
    };

    rooyaFontsReady(run);

    // leaving the breakpoint: undo the splits + inline props so desktop is untouched
    return function () {
      splits.forEach(function (s) { if (s && s.revert) s.revert(); });
      gsap.set(hideAll, { clearProps: "all" });
    };
  });
})();

/* ---- Industries (home-s6) — expanding-circle reveal on scroll (self-contained) ---- */
(function () {
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  var reveal = document.querySelector(".home-s6__reveal");
  if (!reveal) return;
  var s5btn = document.querySelector(".home-s5 .center-cta .btn"); // goes white while green is behind it

  // Every active slide parks at the same horizontal centre (centeredSlides), so measure that
  // resting centre ONCE (at rest) and reuse it — measuring live would make the clip lurch right
  // while a slide animates in.
  // The resting centre, taken from the CAROUSEL rather than from the active circle inside it.
  // `centeredSlides` parks the active slide on the carousel's own centre by definition, so this is the
  // same number — without the race that measuring the circle carried: autoplay moves the
  // .swiper-slide-active class at the START of a 900ms transition, so a measurement taken during one
  // read a circle still travelling toward the centre and cached it for the rest of the visit. That is
  // why the expansion sometimes started off to the side. The carousel's own box never moves.
  var pinnedCx = null;
  function measureCx() {
    var track = reveal.querySelector(".home-s6__swiper");
    if (!track) return;
    var t = track.getBoundingClientRect();
    if (!t.width) return;                      // not laid out yet — update() retries every frame
    pinnedCx = t.left + t.width / 2;
  }

  // A white copy of home-s5's content, laid exactly over the original and clipped to the SAME green
  // circle — so its text reads pure white ONLY where the dark-green shape is behind it (dark elsewhere).
  var s5 = document.querySelector(".home-s5");
  var s5white = null;
  (function () {
    var c = s5 && s5.querySelector(".container");
    if (!c) return;
    s5white = document.createElement("div");
    s5white.className = "home-s5__ondark";
    s5white.setAttribute("aria-hidden", "true");
    var clone = c.cloneNode(true);
    // The section-intro (GSAP) hides s5's cards with inline opacity/visibility/transform BEFORE this
    // clone is taken, so the snapshot would be invisible. Strip those inline styles so the white copy
    // always shows its final (visible) state — the clip-path is what limits where it's revealed.
    var hidden = clone.querySelectorAll("[style]");
    for (var hi = 0; hi < hidden.length; hi++) {
      hidden[hi].style.opacity = "";
      hidden[hi].style.visibility = "";
      hidden[hi].style.transform = "";
    }
    s5white.appendChild(clone);
    s5.appendChild(s5white);
  })();

  // Centre-anchored clip circle driven directly from the section's screen position — no
  // GSAP/ScrollTrigger, so it can't get out of sync. We work in a real pixel RADIUS and
  // convert to rx%/ry% using the box's actual size, so it stays perfectly round throughout
  // (a plain % ellipse would go wide-and-flat because width ≠ height).
  var ticking = false;

  // Symmetric reveal: as the section's centre rises past PEAK the circle grows from nothing to
  // a fully-covered plateau (FLAT band, uniform green), then SHRINKS back to nothing as you
  // keep scrolling down — grow → hold → shrink. (Needs lots of bottom padding on the reveal
  // so there's scroll room for the shrink before the next section appears.)
  var PEAK = 0.5;        // carousel position (fraction of viewport) at which the circle is fully open
  var FLAT = 0.1;        // once the carousel reaches PEAK+FLAT it stays fully open (no shrink)
  var GROW_RAMP = 0.42;  // grow distance — bigger = the circle starts expanding earlier

  function update() {
    ticking = false;
    // below 600 the expanding-circle reveal is off — the section just shows fully (no clip)
    if (window.matchMedia && window.matchMedia("(max-width: 600px)").matches) {
      reveal.style.clipPath = "none";
      if (s5white) s5white.style.clipPath = "inset(0 0 100% 0)"; // keep the white overlay hidden
      return;
    }
    var r = reveal.getBoundingClientRect();
    var focusEl = reveal.querySelector(".home-s6__swiper");
    var fr = focusEl ? focusEl.getBoundingClientRect() : r;
    var focus = fr.top + fr.height / 2;             // the carousel centre (screen Y) = the focal point
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var c = focus / vh;
    // grow as the section enters; once it reaches the plateau it STAYS fully open — no shrink
    // when you keep scrolling down past the section
    var p = c <= (PEAK + FLAT) ? 1 : 1 - (c - PEAK - FLAT) / GROW_RAMP;
    p = p < 0 ? 0 : (p > 1 ? 1 : p);

    var R = Math.sqrt(vw * vw + vh * vh) * 0.6 * p; // real px radius (from 0), covers the viewport
    // Centre the expanding shape on the ACTIVE circle both ways, so it's concentric on any screen.
    //  • horizontal: the carousel's centre, cached in pinnedCx ;  • vertical: read live — the circle
    //    only moves vertically on scroll (autoplay is horizontal), which is what we want to follow.
    var cw = document.documentElement.clientWidth || vw; // content-area width (for the circle centre)
    if (pinnedCx == null) measureCx();
    var acEl = reveal.querySelector(".swiper-slide-active .home-s6__circle");
    var acr = acEl ? acEl.getBoundingClientRect() : null;
    // Horizontally: the carousel's own centre, which is where centeredSlides parks the active slide —
    // never read off the active circle, or autoplay sliding a new one in lurches the shape sideways.
    // Vertically: read live, because autoplay is horizontal so y only moves on scroll, which we follow.
    var cx, cy;
    if (window.matchMedia && window.matchMedia("(max-width: 900px)").matches) {
      cx = cw / 2; // = the active circle's resting centre on mobile (stable, no slide-change jump)
      cy = acr ? (acr.top + acr.height / 2) : (vh * 0.897);
    } else {
      cx = (pinnedCx != null) ? pinnedCx : (focusEl ? fr.left + fr.width / 2 : cw / 2);
      cy = acr ? (acr.top + acr.height / 2) : (vh * 0.897);
    }
    if (p >= 1) {
      reveal.style.clipPath = "none";              // fully open (uniform green)
    } else {
      var anchorX = (cx - r.left) / r.width * 100; // % within the reveal box that lands on screen centre
      var anchorY = (cy - r.top) / r.height * 100;
      reveal.style.clipPath =
        "ellipse(" + (R / r.width * 100) + "% " + (R / r.height * 100) + "% at " + anchorX + "% " + anchorY + "%)";
    }

    // flip the home-s5 CTA to white while the dark-green shape is actually behind it
    if (s5btn) {
      var b = s5btn.getBoundingClientRect();
      var bx = b.left + b.width / 2, by = b.top + b.height / 2;
      var onDark = by >= r.top && by <= r.bottom && bx >= r.left && bx <= r.right &&
        (p >= 1 || ((bx - cx) * (bx - cx) + (by - cy) * (by - cy) <= R * R));
      s5btn.classList.toggle("is-on-dark", onDark);
    }

    // reveal the white text copy through the exact same circle (bounded to the green area, which
    // starts at the reveal box top r.top — above that there's no green, so no white).
    if (s5white) {
      var ow = s5white.getBoundingClientRect();
      if (ow.width && ow.height) {
        if (p >= 1) {
          var topOff = Math.max(0, r.top - ow.top);           // full green below the box top
          s5white.style.clipPath = "inset(" + topOff + "px 0 0 0)";
        } else {
          var axO = (cx - ow.left) / ow.width * 100;
          var ayO = (cy - ow.top) / ow.height * 100;
          s5white.style.clipPath =
            "ellipse(" + (R / ow.width * 100) + "% " + (R / ow.height * 100) + "% at " + axO + "% " + ayO + "%)";
        }
      }
    }
  }
  function onScroll() {
    if (!ticking) { requestAnimationFrame(update); ticking = true; }
  }

  // Drop the cached centre first: measureCx now refuses an unsettled reading, so keeping the old value
  // here would survive a resize that actually moved the carousel.
  function remeasure() { pinnedCx = null; measureCx(); update(); }
  measureCx();
  update();
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", remeasure);
  window.addEventListener("load", remeasure);
})();

/* ---- home-s6 → home-s7 transition: home-s7 slides UP over a pinned home-s6 ---- */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  if (window.matchMedia && window.matchMedia("(max-width: 900px)").matches) return;

  // Safari only: no pin at all. ScrollTrigger pins by position:fixed here, and WebKit rasterises the
  // clipped home-s6__reveal subtree inside a fixed ancestor ONCE at a lower resolution and then scales
  // that bitmap — so the carousel and headings go soft for exactly as long as the pin is held, and are
  // sharp everywhere above it. Both ways of keeping the pin and fixing the raster (pinType:"transform",
  // compositing hints on the reveal) changed how the section scrolls, which is worse than the blur.
  // Skipping the pin costs Safari only the hold: home-s7 still rides up over home-s6 on normal scroll,
  // because it already carries the higher z-index, the opaque background and min-height:100vh.
  // UA sniffing is deliberate — there is no feature test for "rasterises fixed layers badly".
  var ua = navigator.userAgent;
  if (/safari/i.test(ua) && !/chrome|chromium|crios|fxios|edg|opr|android/i.test(ua)) return;

  gsap.registerPlugin(ScrollTrigger);
  var s6 = document.querySelector(".hs-reveal .home-s6");
  var s7 = document.querySelector(".hs-reveal .home-s7");
  var swiper = document.querySelector(".home-s6__swiper"); // the reveal's focal point
  if (!s6 || !s7 || !swiper) return;

  // Hold home-s6 in place while home-s7 (higher z-index + opaque bg) rides UP over it — the "cover".
  // CRITICAL: trigger off the SWIPER's position, not home-s6's bottom. home-s6's height is mostly fixed
  // px (swiper 420 + margins) plus vh padding, so "bottom bottom" reaches the viewport bottom BEFORE the
  // circle finishes expanding on tall screens → it pins mid-expansion and the circle freezes wrong (the
  // bug). Pinning when the carousel is CENTRED means the reveal is always fully open first, at any
  // viewport height — so the pin and the expanding circle stop fighting.
  var created = false;
  function createPin() {
    if (created) return;
    created = true;
    ScrollTrigger.create({
      trigger: swiper,
      start: "center center", // carousel centred = circle already fully open (reveal hits p=1 by c≈0.5)
      end: function () { return "+=" + (window.innerHeight || document.documentElement.clientHeight); },
      pin: s6,
      pinSpacing: false,
      anticipatePin: 1,
      invalidateOnRefresh: true
    });

    // Re-measure now that the pin exists and the spacer has taken over home-s6's negative margin.
    // Both card decks and the s2 scaler already end this way; this pin was the only one that didn't.
    if (window.rooyaRefreshST) window.rooyaRefreshST();
  }

  // Build it NOW, while the page is still at the top — the block at the top of this file has just reset
  // the scroll, so this is the only moment guaranteed to be there.
  //
  // This used to wait for document.fonts.ready, which measurement showed to be the actual bug rather
  // than the safeguard it looked like. Created at scrollY 0 the start lands exactly where it should
  // (4647 of 4647 measured). On a first visit the font is uncached, so fonts.ready resolved long after
  // the visitor had already scrolled — and a pin created on a scrolled page measured its start ~540px
  // early, engaged while home-s5 still overlapped home-s6, and froze that overlap. A reload looked fine
  // only because the cached font made this run instantly, back at scrollY 0.
  //
  // The start does still need final font metrics, and it still gets them: "Recalculate every
  // ScrollTrigger" refreshes on both fonts.ready and `load`, and invalidateOnRefresh above makes those
  // passes recompute start/end from scratch. So the font is accounted for without the pin's existence
  // depending on when it arrives.
  createPin();
})();

/* ---- home-s7 deck (<=600) — stacked cards; scrolling flings the top one to the top-right ---- */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  gsap.registerPlugin(ScrollTrigger);
  // On real phones the browser chrome (address bar) shows/hides while scrolling, changing innerHeight
  // and forcing ScrollTrigger to refresh MID-PIN — which makes the pinned scrub jump/desync and can
  // drop out early. Telling it to ignore those viewport-height changes keeps the pin stable. (No effect
  // in desktop simulators, which never resize the chrome — hence "works in the sim, breaks on device".)
  if (ScrollTrigger.config) ScrollTrigger.config({ ignoreMobileResize: true });
  var deck = document.querySelector(".home-s7__swiper");
  if (!deck) return;
  var cards = gsap.utils.toArray(".home-s7__card");
  if (cards.length < 2) return;

  var mm = gsap.matchMedia();
  mm.add("(max-width: 600px)", function () {
    var n = cards.length;
    // negative = pull each card behind up a touch so its media ends ABOVE the front card's rounded
    // bottom corners (which would otherwise reveal a sliver of the image); title has 31px margin so
    // it's never clipped
    var GAP = -20;
    var tl = null;

    // (Re)build the whole deck from FRESH measurements. Called again after fonts + images settle: on a
    // first visit the web font isn't loaded yet, so measuring now would use fallback-font metrics
    // (shorter text) → the cards behind would sit too high and overlap the front card's copy once the
    // real font swaps in. Re-measuring on fonts.ready / load fixes that.
    function build() {
      if (tl) { if (tl.scrollTrigger) tl.scrollTrigger.kill(); tl.kill(); tl = null; }

      // reset to NATURAL FLOW before measuring — on a rebuild the cards are still position:absolute
      // (stacked at top:0) from the last build, where offsetHeight no longer reflects their real
      // laid-out height; measuring that gives too-small spacing and the cards behind overlap.
      gsap.set(cards, { clearProps: "position,top,left,width,transform,opacity,visibility,zIndex" });
      deck.style.height = "";
      void deck.offsetHeight; // force reflow so the measurements below are from the clean flow layout

      // per-card text-area height (the card minus its media) — offsets are cumulative & per-card, so
      // every card behind reveals exactly its own title + copy (its media stays hidden), evenly spaced
      var textH = cards.map(function (c) {
        var m = c.querySelector(".home-s7__media");
        return c.offsetHeight - (m ? m.offsetHeight : 0);
      });
      var cardH = cards.map(function (c) { return c.offsetHeight; });

      // y of card j while `s` is the front card (cards below s have been flung away)
      var offsetAt = function (s, j) {
        var o = 0;
        for (var k = s; k < j; k++) o += textH[k] + GAP;
        return o;
      };

      // the deck box is only ONE card tall (the tallest) — the cascade below it overflows during the
      // pin and gets flung away, so once it's done the last card fills the box with no empty gap
      var deckH = Math.max.apply(null, cardH);
      deck.style.height = deckH + "px";

      cards.forEach(function (c, i) {
        gsap.set(c, {
          position: "absolute", top: 0, left: 0, width: "100%",
          transformOrigin: "top center",
          zIndex: n - i, y: offsetAt(0, i), autoAlpha: 1
        });
      });

      // pin the deck and scrub: each segment flings the front card off + promotes the rest forward
      tl = gsap.timeline({
        scrollTrigger: {
          trigger: deck,
          start: "top 92px",
          end: "+=" + ((n - 1) * 480),
          pin: true,
          scrub: true,
          // this pin sits ABOVE home-s8/s9 and inserts a big spacer. Give it a higher refresh priority
          // so ScrollTrigger recomputes it FIRST — otherwise the sections below can measure their start
          // while this spacer isn't in place yet and end up too high (they fire "before you arrive").
          refreshPriority: 10
        }
      });
      for (var i = 0; i < n - 1; i++) {
        (function (i) {
          tl.to(cards[i], {                           // fling the front card to the top-right
            xPercent: 120, yPercent: -120, rotation: 20, autoAlpha: 0,
            ease: "power2.in", duration: 1
          }, i);
          for (var j = i + 1; j < n; j++) {           // promote the rest one level forward
            tl.to(cards[j], { y: offsetAt(i + 1, j), ease: "power2.out", duration: 1 }, i);
          }
        })(i);
      }

      // This pin inserts a big pin-spacer that pushes home-s8/s9 DOWN. Any trigger created before the
      // spacer existed now sits too high and fires way early ("before you get there"). Recompute all
      // triggers so the sections below account for the spacer. (mobile-only: no deck/pin on desktop.)
      if (window.rooyaRefreshST) window.rooyaRefreshST();
    }


    // Every LATER rebuild is measured from the top of the page. `build` resets the cards to natural
    // flow and re-creates the pin from scratch, and the fonts/`load` passes that call it land long
    // after the visitor has started scrolling — measured mid-pin, the deck sets up in the wrong place
    // and the cards spill over the section above. See rooyaAtTop.
    var rebuild = function () { window.rooyaAtTop(build); };

    rebuild();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(rebuild); // real font swapped in
    window.addEventListener("load", rebuild);                                       // images/video sized

    return function () { // revert to the normal flow when we leave the query
      window.removeEventListener("load", rebuild);
      if (tl) { if (tl.scrollTrigger) tl.scrollTrigger.kill(); tl.kill(); }
      deck.style.height = "";
      cards.forEach(function (c) {
        gsap.set(c, { clearProps: "position,top,left,width,zIndex,transform,opacity,visibility,scale" });
      });
    };
  });
})();

/* ---- platform-s1 deck (<=600) — same stacked-card behaviour as the home-s7 deck ---- */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  gsap.registerPlugin(ScrollTrigger);
  if (ScrollTrigger.config) ScrollTrigger.config({ ignoreMobileResize: true }); // stable pin on mobile chrome
  var deck = document.querySelector(".platform-s1__swiper");
  if (!deck) return;
  var cards = gsap.utils.toArray(".platform-s1__card");
  if (cards.length < 2) return;

  var mm = gsap.matchMedia();
  mm.add("(max-width: 600px)", function () {
    var n = cards.length;
    var GAP = -20;
    var tl = null;

    function build() {
      if (tl) { if (tl.scrollTrigger) tl.scrollTrigger.kill(); tl.kill(); tl = null; }

      gsap.set(cards, { clearProps: "position,top,left,width,transform,opacity,visibility,zIndex" });
      deck.style.height = "";
      void deck.offsetHeight; // force reflow so the measurements below use the clean flow layout

      var textH = cards.map(function (c) {
        var m = c.querySelector(".platform-s1__media");
        return c.offsetHeight - (m ? m.offsetHeight : 0);
      });
      var cardH = cards.map(function (c) { return c.offsetHeight; });

      var offsetAt = function (s, j) {
        var o = 0;
        for (var k = s; k < j; k++) o += textH[k] + GAP;
        return o;
      };

      var deckH = Math.max.apply(null, cardH);
      deck.style.height = deckH + "px";

      cards.forEach(function (c, i) {
        gsap.set(c, {
          position: "absolute", top: 0, left: 0, width: "100%",
          transformOrigin: "top center",
          zIndex: n - i, y: offsetAt(0, i), autoAlpha: 1
        });
      });

      tl = gsap.timeline({
        scrollTrigger: {
          trigger: deck,
          start: "top 92px",
          end: "+=" + ((n - 1) * 480),
          pin: true,
          scrub: true,
          refreshPriority: 10 // pin sits above the sections below — refresh it first so they measure right
        }
      });
      for (var i = 0; i < n - 1; i++) {
        (function (i) {
          tl.to(cards[i], {
            xPercent: 120, yPercent: -120, rotation: 20, autoAlpha: 0,
            ease: "power2.in", duration: 1
          }, i);
          for (var j = i + 1; j < n; j++) {
            tl.to(cards[j], { y: offsetAt(i + 1, j), ease: "power2.out", duration: 1 }, i);
          }
        })(i);
      }

      if (window.rooyaRefreshST) window.rooyaRefreshST();
    }


    // Every LATER rebuild is measured from the top of the page. `build` resets the cards to natural
    // flow and re-creates the pin from scratch, and the fonts/`load` passes that call it land long
    // after the visitor has started scrolling — measured mid-pin, the deck sets up in the wrong place
    // and the cards spill over the section above. See rooyaAtTop.
    var rebuild = function () { window.rooyaAtTop(build); };

    rebuild();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(rebuild);
    window.addEventListener("load", rebuild);

    return function () {
      window.removeEventListener("load", rebuild);
      if (tl) { if (tl.scrollTrigger) tl.scrollTrigger.kill(); tl.kill(); }
      deck.style.height = "";
      cards.forEach(function (c) {
        gsap.set(c, { clearProps: "position,top,left,width,zIndex,transform,opacity,visibility,scale" });
      });
    };
  });
})();

/* ---- Behaviours (home-s3) — plays itself above 900, click-to-play at/below it ----
   Above 900 the entrance timeline below "clicks" the play button and starts the clip once its intro
   finishes, and this block only covers the no-animation path (reduced motion / GSAP missing) by
   autoplaying on scroll-in. At/below 900 nothing starts it on its own: the visitor taps the button.

   The breakpoint is published as rooyaS3ClickToPlay so the entrance timeline reads the same answer
   rather than repeating the query — and it is read live, so a resize across 900 is honoured. */
window.rooyaS3ClickToPlay = function () {
  return !!(window.matchMedia && window.matchMedia("(max-width: 900px)").matches);
};

(function () {
  var wrap = document.querySelector(".home-s3__video");
  if (!wrap) return;
  var video = wrap.querySelector(".home-s3__player");
  if (!video) return;
  var btn = wrap.querySelector(".home-s3__play");

  var startVideo = function () {
    wrap.classList.add("is-playing"); // CSS hides the cover/play, reveals the video
    video.muted = true;
    video.loop = true;
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  };
  wrap._startVideo = startVideo; // the entrance timeline calls this after the play-button press

  // The button is inert above 900 (CSS pointer-events: none), so this listener can be bound
  // unconditionally — only the breakpoint where CSS lets clicks through can ever reach it.
  if (btn) btn.addEventListener("click", function () {
    if (window.rooyaS3ClickToPlay()) startVideo();
  });

  if (window.rooyaS3ClickToPlay()) return; // nothing auto-starts it here

  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var noGsap = typeof gsap === "undefined" || !window.ScrollTrigger;
  if (!reduce && !noGsap) return; // the animated path handles it

  if (!("IntersectionObserver" in window)) { startVideo(); return; }
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) { startVideo(); io.disconnect(); }
    });
  }, { threshold: 0.4 });
  io.observe(wrap);
})();

/* ---- Behaviours (home-s3) — text intro, then media (scale up), then tag (fade left) ---- */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  gsap.registerPlugin(ScrollTrigger);
  // Not on a translated page: a split heading is what the translator turns into nonsense, and the
  // plain-fade branch below is a perfectly good intro.
  var hasSplit = window.rooyaCanSplit();
  if (hasSplit) gsap.registerPlugin(SplitText);

  var root = document.querySelector(".home-s3");
  if (!root) return;
  var h2 = root.querySelector(".text-container h2");
  var p = root.querySelector(".text-container p");
  if (!h2 || !p) return;
  var media = [
    root.querySelector(".home-s3__video"),
    root.querySelector(".home-s3__event"),        // desktop event card (hidden ≤600)
    root.querySelector(".home-s3__event-mobile"), // mobile event card (hidden >600)
    root.querySelector(".home-s3__icons")
  ].filter(Boolean);
  var tag = root.querySelector(".home-s3__tag");

  gsap.set([h2, p], { autoAlpha: 0 }); // hide the text until the intro builds (no flash)

  var run = function () {
    gsap.set([h2, p], { autoAlpha: 1 });

    var tl = gsap.timeline({
      defaults: { ease: "power4.out" },
      scrollTrigger: { trigger: root, start: "top 75%", toggleActions: "play none none none" }
    });

    // 1) text — h2 char-by-char, paragraph masked lines
    if (hasSplit) {
      var words = new SplitText(h2, { type: "words", wordsClass: "s1-word" });
      var chars = rooyaSplitChars(words);
      var lines = new SplitText(p, { type: "lines", linesClass: "s1-line" });
      gsap.set(words.words, { display: "inline-block" });
      gsap.set(lines.lines, { overflow: "hidden" });
      tl.from(chars.chars, { autoAlpha: 0, y: 50, duration: 1.1, stagger: chars.stagger }, 0)
        .from(lines.lines, { yPercent: 120, autoAlpha: 0, duration: 1, stagger: 0.08 }, 0.45);

      rooyaUnsplit(tl, [words, chars, lines]);
    } else {
      tl.from(h2, { autoAlpha: 0, y: 40, duration: 1.1 }, 0)
        .from(p, { autoAlpha: 0, y: 30, duration: 1 }, 0.45);
    }

    // 2) video, event card, icons — scale up in sequence
    if (media.length) {
      tl.from(media, {
        autoAlpha: 0, scale: 0.8, transformOrigin: "50% 50%",
        duration: 0.7, stagger: 0.15, ease: "back.out(1.4)"
      }, 0.75);
    }

    // 3) the tag last — fade in from the left
    if (tag) {
      tl.from(tag, { autoAlpha: 0, x: -30, duration: 0.6 }, "-=0.15");
    }

    // 4) "click" the play button, then start the video (no manual clicking)
    var wrap = root.querySelector(".home-s3__video");
    var btn = root.querySelector(".home-s3__play");
    // Skipped at/below 900, where the clip is click-to-play: pressing the button by itself and then
    // starting the video would defeat the point.
    if (wrap && btn && !(window.rooyaS3ClickToPlay && window.rooyaS3ClickToPlay())) {
      tl.add(function () { btn.classList.add("home-s3__play--tap"); }, "+=0.05") // press down
        .add(function () { btn.classList.remove("home-s3__play--tap"); }, "+=0.16") // spring back
        .add(function () { if (wrap._startVideo) wrap._startVideo(); }, "+=0.26"); // reveal + play
    }
  };

  rooyaFontsReady(run);
})();

/* ---- Hardware (home-s4) — packshots scale + slight rotate in, circle scales in last ---- */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  gsap.registerPlugin(ScrollTrigger);

  var root = document.querySelector(".home-s4");
  if (!root) return;
  var shot1 = root.querySelector(".home-s4__shot--1");
  var shot2 = root.querySelector(".home-s4__shot--2");
  var circle = root.querySelector(".home-s4__circle");
  var mobile = root.querySelector(".home-s4__mobile"); // shown instead of the layered shots below 900px
  if (!shot1 && !shot2 && !circle && !mobile) return;

  var tl = gsap.timeline({
    scrollTrigger: { trigger: root, start: "top 75%", toggleActions: "play none none none" }
  });

  // packshot 1 first, then packshot 2 (both scale in with a slight rotation), circle last
  if (shot1) tl.from(shot1, { autoAlpha: 0, scale: 0.6, rotation: -10, transformOrigin: "50% 50%", duration: 0.8, ease: "back.out(1.5)" }, 0);
  if (shot2) tl.from(shot2, { autoAlpha: 0, scale: 0.6, rotation: 9,  transformOrigin: "50% 50%", duration: 0.8, ease: "back.out(1.5)" }, 0.35);
  if (circle) tl.from(circle, { autoAlpha: 0, scale: 0.4, transformOrigin: "50% 50%", duration: 0.7, ease: "back.out(1.4)" }, 0.7);
  // the flat mobile image (the only thing visible below 900px) scales/fades in on its own
  if (mobile) tl.from(mobile, { autoAlpha: 0, scale: 0.82, y: 24, transformOrigin: "50% 50%", duration: 0.85, ease: "back.out(1.4)" }, 0);
})();

/* ---- Recalculate every ScrollTrigger start/end once late images & fonts settle the layout ----
   Triggers created before images decode use stale (too-high) start positions and fire early — most
   visibly on macOS, where image-decode timing differs from Windows. Refreshing after fonts.ready and
   window `load` (fires once ALL images are in) pins the start points to the final layout. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger);
  var refresh = function () { window.rooyaAtTop(function () { ScrollTrigger.refresh(); }); };
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
  window.addEventListener("load", refresh);
})();

/* ---- The Rooya difference (home-s7) — cloud-masked video: plays on HOVER above 600, click on mobile ---- */
(function () {
  var media = document.querySelector(".home-s7__media--cloud");
  if (!media) return;
  var btn = media.querySelector(".home-s7__cloud-play");
  var video = media.querySelector(".home-s7__cloud-video");
  if (!video) return;

  // `playing` is the first point at which there is a frame to show, so that — not the hover — is what
  // retires the still underneath. If playback is refused (autoplay policy, a failed fetch) it never
  // fires and the still simply stays, which is the right fallback.
  video.addEventListener("playing", function () { media.classList.add("is-ready"); });

  var play = function () {
    media.classList.add("is-playing"); // reveals the video layer and hides the play button
    video.muted = true;                // muted loop, no controls — just plays in the cloud shape
    video.loop = true;
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  };
  var reset = function () {
    media.classList.remove("is-playing"); // back to the cover + play button
    media.classList.remove("is-ready");   // next hover shows the still again until frames are back
    video.pause();
    try { video.currentTime = 0; } catch (e) {}
  };

  // above 600 hovering the tile auto-plays it (and grows the cloud via CSS :hover); leaving reverts to
  // the default state. On mobile (no hover) it stays click-to-play.
  var isDesktop = function () { return !(window.matchMedia && window.matchMedia("(max-width: 600px)").matches); };
  media.addEventListener("mouseenter", function () { if (isDesktop()) play(); });
  media.addEventListener("mouseleave", function () { if (isDesktop()) reset(); });
  if (btn) btn.addEventListener("click", function () { if (!isDesktop()) play(); });
})();

/* ---- Platform · Cloud video evidence (platform-s1 card 3) — click-to-play the video ---- */
(function () {
  var media = document.querySelector(".platform-s1__media--cloud");
  if (!media) return;
  var btn = media.querySelector(".platform-s1__play");
  var video = media.querySelector(".platform-s1__video");
  if (!btn || !video) return;

  btn.addEventListener("click", function () {
    media.classList.add("is-playing"); // CSS reveals the video, hides the still + play button
    video.muted = true;
    video.loop = true;
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  });
})();

/* ---- Platform · trip evidence (platform-s5) — click-to-play the dash-cam clip ---- */
(function () {
  var wrap = document.querySelector(".platform-s5__video");
  if (!wrap) return;
  var btn = wrap.querySelector(".platform-s5__play");
  var video = wrap.querySelector(".platform-s5__video-clip");
  if (!btn || !video) return;

  btn.addEventListener("click", function () {
    wrap.classList.add("is-playing"); // CSS fades the clip in over the cover and drops the button
    video.muted = true;
    video.loop = true;
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  });
})();

/* ---- platform-s7 composition — the photo, the device tipping up behind it, then the badges ----
   Lifted out of the SECTIONS driver with its sequence intact; the copy beside it no longer animates. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  var root = document.querySelector(".platform-s7");
  if (!root) return;
  gsap.registerPlugin(ScrollTrigger);

  var media = root.querySelector(".platform-split__media");
  if (!media) return;

  var LEAD = 0.3;   // an empty first beat — see the note on the s4 module below

  var tl = gsap.timeline({
    defaults: { ease: "power4.out" },
    scrollTrigger: { trigger: media, start: "top 80%", toggleActions: "play none none none" }
  });

  var q = function (sel) { return root.querySelector(sel); };
  var man    = q(".platform-s7__man");
  var tablet = q(".platform-s7__tablet");
  var icon   = q(".platform-s7__icon");
  var arrow  = q(".platform-s7__arrow");

  if (man) tl.fromTo(man,
    { autoAlpha: 0, y: 50 },
    { autoAlpha: 1, y: 0, duration: 0.85 }, LEAD);

  // The device is a pre-rotated export, so it can be swung rather than just slid: pivoting on
  // the bottom of its box — a point buried behind the photo and the arc — starts it a few
  // degrees short of its final lean and tips it up into place, as if it were being raised from
  // behind the man. Only the top half is ever visible, which is the half that travels furthest.
  if (tablet) tl.fromTo(tablet,
    { autoAlpha: 0, y: 46, rotation: -5, scale: 0.95, transformOrigin: "50% 100%" },
    { autoAlpha: 1, y: 0, rotation: 0, scale: 1, duration: 0.8 }, LEAD + 0.30);

  // The record card travels right to left: it starts tucked behind the photo (it sits a layer
  // under it) and slides out to its place on the left. The travel is in xPercent, not pixels,
  // so it stays a fixed share of the card's own width and reads the same at every column width.
  // Nothing scales here: the movement is the whole point, and a pop would fight it.
  if (icon) tl.fromTo(icon,
    { autoAlpha: 0, xPercent: 28 },
    { autoAlpha: 1, xPercent: 0, duration: 0.7 }, LEAD + 0.55);

  // and the badge pops last, well after the card has landed
  if (arrow) tl.fromTo(arrow,
    { autoAlpha: 0, scale: 0.5 },
    { autoAlpha: 1, scale: 1, duration: 0.5, ease: "back.out(1.8)" }, LEAD + 1.05);
})();

/* ---- platform-s6 diagram — the tablet, then the connectors wiring the cards in ----
   Lifted out of the SECTIONS driver with its sequence intact; the subtitle, heading and paragraph
   beside it no longer animate. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  var root = document.querySelector(".platform-s6");
  if (!root) return;
  gsap.registerPlugin(ScrollTrigger);

  var media = root.querySelector(".platform-split__media");
  if (!media) return;

  var LEAD = 0.3;   // an empty first beat — see the note on the s4 module below

  var tl = gsap.timeline({
    defaults: { ease: "power4.out" },
    scrollTrigger: { trigger: media, start: "top 80%", toggleActions: "play none none none" }
  });

  var q = function (sel) { return root.querySelector(sel); };
  var tablet = q(".platform-s6__tablet");

  // A connector is drawn by sliding its own stroke into view: dasharray = the full path length,
  // and the offset runs from that length down to 0, growing the stroke from the path's START.
  // So the paths in platform.php are authored TABLET-FIRST — the direction the line travels.
  //
  // They used to be authored card-first and drawn with a NEGATIVE offset, which grows a stroke
  // from the far end instead. Chrome and Firefox do that; WebKit does not. A dasharray of one
  // value L repeats as dash-L/gap-L, so the pattern is 2L long, and WebKit normalises a negative
  // offset into that period: -L comes back as +L, which hides the line just the same but grows
  // it from the WRONG END. On Safari the elbows drew downwards from their card instead of out
  // of the device. Reversing the path data is what fixes it for every engine at once — there is
  // no offset value that means "from the end" everywhere.
  //
  // Lengths are measured from the geometry rather than hard-coded, so a re-exported SVG keeps
  // working.
  //
  // All three run at the same ~420 units/sec, so the middle line's long buried stretch takes
  // the time it should: it is drawn behind the device for most of the tween and only surfaces
  // in the gap under the card, which is all the design leaves visible of it.
  var draw = function (sel, pos, dur) {
    var wrap = q(sel);
    var line = wrap && wrap.querySelector("path, line");
    if (!line) return;
    var len = (typeof line.getTotalLength === "function" && line.getTotalLength()) || 160;

    // The WRAPPER is hidden here, not just the stroke — belt and braces, and it has earned its keep.
    // The dash offset below is what is meant to hold the connector back until its turn, but when that
    // failed to stick the line simply sat there fully drawn, in an otherwise empty stage because the
    // PNGs beside it are lazy. That was the bug on this page. Gating the wrapper as well makes the
    // hide independent of the dash maths. (.has-js in default.scss covers the earlier window still —
    // from first paint until this module runs.)
    gsap.set(wrap, { autoAlpha: 0 });
    // Applied straight away rather than left to the tween's immediateRender, for the same reason.
    gsap.set(line, { strokeDasharray: len, strokeDashoffset: len });

    tl.set(wrap, { autoAlpha: 1 }, pos);
    tl.fromTo(line, { strokeDashoffset: len },
      { strokeDashoffset: 0, duration: dur, ease: "power1.inOut" }, pos);
  };

  // the cards land with the same back-eased pop as the s5 ring, so the two sections' set pieces
  // arrive with one voice
  var pop = function (sel, pos) {
    var el = q(sel);
    if (!el) return;
    tl.fromTo(el, { scale: 0.62, autoAlpha: 0 },
      { scale: 1, autoAlpha: 1, duration: 0.45, ease: "back.out(1.5)" }, pos);
  };

  if (tablet) tl.fromTo(tablet,
    { autoAlpha: 0, y: 44 },
    { autoAlpha: 1, y: 0, duration: 0.65 }, LEAD);

  // the middle run is 92 units against the elbows' 153, hence the shorter duration. Each card
  // pops the moment its line arrives, so the connector reads as the thing that placed it.
  draw(".platform-s6__link--mid",   LEAD + 0.38, 0.22);
  pop (".platform-s6__sq--2",       LEAD + 0.58);

  draw(".platform-s6__link--left",  LEAD + 0.78, 0.36);
  pop (".platform-s6__sq--1",       LEAD + 1.12);

  draw(".platform-s6__link--right", LEAD + 1.32, 0.36);
  pop (".platform-s6__sq--3",       LEAD + 1.66);
})();

/* ---- platform-s5 composition — footage card, trip card, then the ring; the copy stays put ----
   Lifted out of the SECTIONS driver with its sequence intact. What went with it was the copy intro:
   the subtitle, the heading, the paragraph and the two-column icon list all sit still now. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  var root = document.querySelector(".platform-s5");
  if (!root) return;
  gsap.registerPlugin(ScrollTrigger);

  var media = root.querySelector(".platform-split__media");
  if (!media) return;
  var ring  = root.querySelector(".platform-s5__ring");
  var video = root.querySelector(".platform-s5__video");
  var map   = root.querySelector(".platform-s5__map");
  var stage = root.querySelector(".platform-s5__stage");

  var LEAD = 0.3;   // an empty first beat — see the note on the s4 module below

  var tl = gsap.timeline({
    defaults: { ease: "power4.out" },
    scrollTrigger: { trigger: media, start: "top 80%", toggleActions: "play none none none" }
  });

  // pieces follow 0.22s apart, same rhythm as the s4 composition
  if (video) tl.fromTo(video,
    { autoAlpha: 0, y: 34, scale: 0.94 },
    { autoAlpha: 1, y: 0, scale: 1, duration: 0.7 }, LEAD);

  if (map) tl.fromTo(map,
    { autoAlpha: 0, y: 46 },
    { autoAlpha: 1, y: 0, duration: 0.75 }, LEAD + 0.22);

  if (ring) tl.fromTo(ring,
    { scale: 0.6, autoAlpha: 0 },
    { scale: 1, autoAlpha: 1, duration: 0.8, ease: "back.out(1.4)" }, LEAD + 0.44);

  // release the orbiting badge exactly when the ring lands. Until then CSS holds it parked at
  // the ring's bottom-left, so it is on screen from the first frame rather than arriving later.
  if (stage) tl.call(function () { stage.classList.add("is-orbiting"); }, null, LEAD + 0.44);
})();

/* ---- platform-s4 composition — the picture assembles itself; the copy stays put ----
   Photo, then the legend, then the score card, then the arc, and the ring counts up under the tail
   of it. Lifted out of the SECTIONS driver with its sequence intact: what was dropped is the copy
   intro that used to run alongside it (subtitle, heading, paragraph). */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  var root = document.querySelector(".platform-s4");
  if (!root) return;
  gsap.registerPlugin(ScrollTrigger);

  var q = function (sel) { return root.querySelector(sel); };
  var media  = q(".platform-split__media");
  if (!media) return;
  var photo  = q(".platform-s4__photo-wrap");
  var legend = q(".score-legend");
  var card   = q(".driver-score");
  var shape  = q(".platform-s4__shape");

  // The pause is spent INSIDE the timeline rather than declared as a `delay`. On a timeline that
  // ScrollTrigger holds paused, `delay` describes when it starts within its parent, which is not the
  // same question; an empty first beat says "wait, then assemble" without depending on that reading.
  var LEAD = 0.3;

  var tl = gsap.timeline({
    defaults: { ease: "power4.out" },
    // The media, not the copy. The driver triggered off the copy block and flipped to the section
    // root below 900 (where the picture stacks on top); triggering off the picture itself is the same
    // answer at both widths now that the copy has nothing to wait for.
    scrollTrigger: { trigger: media, start: "top 80%", toggleActions: "play none none none" }
  });

  // the four pieces follow each other closely (0.22s apart) so the composition reads as one
  // object assembling itself rather than four separate entrances
  if (photo) tl.from(photo, { autoAlpha: 0, y: 46, duration: 0.8 }, LEAD);

  // .score-legend and .platform-s4__shape are centred with a CSS transform. GSAP writes the
  // whole transform once it animates them, so the centring has to be carried in the tween
  // itself (yPercent / xPercent) or the overlay jumps out of place mid-animation.
  if (legend) tl.fromTo(legend,
    { yPercent: -50, scale: 0.86, x: -70, autoAlpha: 0, transformOrigin: "left center" },
    { yPercent: -50, scale: 1, x: 0, autoAlpha: 1, duration: 0.75 }, LEAD + 0.22);

  if (card) tl.fromTo(card,
    { scale: 0.78, autoAlpha: 0 },
    { scale: 1, autoAlpha: 1, duration: 0.65, ease: "back.out(1.5)" }, LEAD + 0.44);

  if (shape) tl.fromTo(shape,
    { xPercent: -50, scale: 0.72, autoAlpha: 0 },
    { xPercent: -50, scale: 1, autoAlpha: 1, duration: 0.7 }, LEAD + 0.66);

  // The ring counts on this schedule rather than on its own scroll observer, starting while the arc
  // is still settling — waiting for the timeline to actually end read as a long pause.
  var deferred = [].slice.call(root.querySelectorAll("[data-score-defer]"));
  if (deferred.length) {
    tl.call(function () {
      deferred.forEach(function (r) { if (r._runScore) r._runScore(); });
    }, null, LEAD + 0.8);
  }
})();

/* ---- platform-s3 media — the slider fades up, and nothing else in the section moves ----
   The copy beside it (subtitle, heading, paragraph, checklist) is intentionally static: this section
   is read, not performed. Kept out of the SECTIONS driver for that reason — see the note where its
   entry used to be. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  var media = document.querySelector(".platform-s3 .platform-split__media");
  if (!media) return;
  gsap.registerPlugin(ScrollTrigger);

  // fromTo, not from: the "from" state is applied the moment this runs, so the slider is hidden well
  // before its trigger rather than sitting there waiting to be animated.
  gsap.fromTo(media, { autoAlpha: 0, y: 40 }, {
    autoAlpha: 1, y: 0, duration: 0.8, ease: "power4.out",
    // Holds off 0.3s after the trigger fires. With the copy beside it no longer animating there is
    // nothing for the slider to follow, so the beat has to be stated here rather than fall out of a
    // shared timeline. `delay` and not a later `start`: the trigger point is where the section is
    // judged to be on screen, and that is still the right place — only the entrance waits.
    delay: 0.3,
    scrollTrigger: { trigger: media, start: "top 80%", toggleActions: "play none none none" }
  });
})();

/* ---- Platform · behaviour slider (platform-s3) — the card on screen plays its own clip ----
   It used to take a hover, which left the clips undiscovered on touch and invisible to anyone who
   never happened to point at the card. Now the ACTIVE slide plays by itself.

   Two things still gate it, so this does not become five clips downloading at once behind the fold:
   only the active card ever plays (the rest are stopped and rewound), and nothing plays until the
   slider is actually on screen. With preload="none" in the markup, a clip is fetched the first time
   it is asked to play and not a moment sooner. */
(function () {
  var slider = document.querySelector(".behaviour-swiper");
  var cards = document.querySelectorAll(".behaviour-card");
  if (!slider || !cards.length) return;

  // A clip looping on its own is motion for its own sake, which is the case this setting exists for.
  // The photo underneath is the card's real content, so stopping here leaves it complete, just still.
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  var onScreen = false;

  var stop = function (card) {
    var video = card && card.querySelector(".behaviour-card__video");
    if (!video) return;
    card.classList.remove("is-playing"); // CSS fades the clip back out to the photo
    video.pause();
    try { video.currentTime = 0; } catch (e) {}
  };

  var play = function (card) {
    var video = card && card.querySelector(".behaviour-card__video");
    if (!video) return;
    card.classList.add("is-playing");
    video.muted = true;                 // an autoplay with sound is refused outright
    var p = video.play();
    // Refusal is a normal outcome, not an error — iOS low power mode declines every autoplay. The
    // card simply keeps showing its photo, which is why `is-playing` is the only thing CSS needs.
    if (p && p.catch) p.catch(function () { card.classList.remove("is-playing"); });
  };

  // Which card is active is read from the DOM, not from a Swiper index: loop mode REORDERS the real
  // slides, so the element actually carrying the class is the only answer that cannot drift.
  var sync = function () {
    var card = slider.querySelector(".swiper-slide-active") || cards[0];
    Array.prototype.forEach.call(cards, function (other) { if (other !== card) stop(other); });
    if (onScreen) play(card); else stop(card);
  };

  window.rooyaBehaviourSync = sync; // called by the slider on every slide change (see its init above)

  // A finished clip hands over to the next card. The `loop` attribute is gone from the markup for
  // this reason: a clip that repeats itself never fires `ended`, so the carousel is what repeats now.
  //
  // Bound once per card at setup rather than per play, and guarded twice over — an `ended` from a
  // card that is off screen, or that is no longer the one being looked at, must not move the slider
  // under the reader. In practice only the active, visible card can ever be running, but the guards
  // cost nothing and this is the one place where a stray event would visibly hijack the page.
  Array.prototype.forEach.call(cards, function (card) {
    var video = card.querySelector(".behaviour-card__video");
    if (!video) return;
    video.addEventListener("ended", function () {
      if (!onScreen) return;
      if (card !== (slider.querySelector(".swiper-slide-active") || cards[0])) return;
      if (window.rooyaBehaviourNext) window.rooyaBehaviourNext();
    });
  });

  if (window.IntersectionObserver) {
    new IntersectionObserver(function (entries) {
      onScreen = entries[0].isIntersecting;
      sync();
    }, { threshold: 0.25 }).observe(slider);
  } else {
    onScreen = true;
    sync();
  }
})();

/* ---- Driver-score rings — count the score up and fill the ring on scroll ----
   Drives any element marked [data-score-ring] (home-s2 card, platform hero card), whose
   conic-gradient fill is bound to a --score custom property and whose number is [data-score-num]. */
(function () {
  var rings = document.querySelectorAll("[data-score-ring]");
  if (!rings.length) return;
  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var hasIO = "IntersectionObserver" in window;

  var setup = function (ring) {
    var num = ring.querySelector("[data-score-num]");
    var target = parseInt((num && num.textContent) || ring.style.getPropertyValue("--score"), 10) || 0;

    // start empty (ring at 0%, number at 0) until it's revealed
    ring.style.setProperty("--score", "0");
    if (num) num.textContent = "0";

    var done = false;
    var run = function () {
      if (done) return;   // one count-up only, whichever trigger gets here first
      done = true;
      if (reduce) {
        ring.style.setProperty("--score", target);
        if (num) num.textContent = target;
        return;
      }
      var start = null, dur = 1300;
      var step = function (ts) {
        if (!start) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        var val = Math.round((1 - Math.pow(1 - p, 3)) * target); // ease-out
        ring.style.setProperty("--score", val);                  // conic-gradient fills as this grows
        if (num) num.textContent = val;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    // deferred rings (e.g. the platform masthead) are triggered by their entrance animation instead
    // of a scroll observer, so the count starts exactly when the stats become visible
    if (ring.hasAttribute("data-score-defer")) {
      ring._runScore = run;
      // nothing would ever call that hook if the entrance animations are not running at all, and the
      // ring would sit at zero for good — so fill it straight away in that case
      if (typeof gsap === "undefined" || !window.ScrollTrigger || reduce) run();
      return;
    }
    if (!hasIO) { run(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        run();
      });
    }, { threshold: 0.45 });
    io.observe(ring);
  };

  for (var i = 0; i < rings.length; i++) setup(rings[i]);
})();

/* ---- Platform masthead (platform-hero) entrance — homepage-style text (h1 chars, p masked lines)
   + button first, then laptop / event / stats, then the big circle last. The driver-score counter
   is deferred (data-score-defer) and fired the moment the stats card finishes animating in. */
(function () {
  var root = document.querySelector(".platform-hero");
  if (!root) return;
  // CSS holds this hidden through the first paint; nothing below may return without releasing it
  var wrap = root.querySelector(".platform-hero__inner");

  var copy = root.querySelector(".platform-hero__copy");
  var h1 = copy && copy.querySelector("h1");
  var p = copy && copy.querySelector("p");
  var btn = copy && copy.querySelector(".btn");
  var laptop = root.querySelector(".platform-hero__laptop");
  var evt = root.querySelector(".platform-hero__event");
  var stats = root.querySelector(".platform-hero__stats");
  var circle = root.querySelector(".platform-hero__circle");
  var ring = root.querySelector("[data-score-ring]");
  var runScore = function () { if (ring && ring._runScore) ring._runScore(); };

  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (typeof gsap === "undefined" || reduce) { rooyaRelease(wrap); runScore(); return; } // no motion → just count

  // Not on a translated page: a split heading is what the translator turns into nonsense, and the
  // plain-fade branch below is a perfectly good intro.
  var hasSplit = window.rooyaCanSplit();
  if (hasSplit) gsap.registerPlugin(SplitText);

  var media = [laptop, evt, stats, circle].filter(Boolean);
  gsap.set(copy, { autoAlpha: 0 });            // hide copy + media until the intro plays (no flash)
  if (media.length) gsap.set(media, { autoAlpha: 0 });
  rooyaRelease(wrap);                          // every piece carries its own visibility now

  var run = function () {
    gsap.set(copy, { autoAlpha: 1 });          // reveal container; pieces start hidden via .from
    if (btn) gsap.set(btn, { transition: "none" }); // silence the .btn CSS transition vs GSAP

    var tl = gsap.timeline({
      defaults: { ease: "power4.out" },
      onComplete: function () { if (btn) gsap.set(btn, { clearProps: "transition" }); }
    });

    // 1) text (h1 char-by-char, paragraph masked lines) + button — same as the homepage masthead
    if (hasSplit && h1 && p) {
      var words = new SplitText(h1, { type: "words", wordsClass: "s1-word" });
      var chars = rooyaSplitChars(words);
      var lines = new SplitText(p, { type: "lines", linesClass: "s1-line" });
      gsap.set(words.words, { display: "inline-block" });
      gsap.set(lines.lines, { overflow: "hidden" });

      tl.from(chars.chars, { autoAlpha: 0, y: 50, duration: 1.1, stagger: chars.stagger }, 0)
        .from(lines.lines, { yPercent: 120, autoAlpha: 0, duration: 1, stagger: 0.08 }, 0.45);

      rooyaUnsplit(tl, [words, chars, lines]);
    } else {
      if (h1) tl.from(h1, { autoAlpha: 0, y: 40, duration: 1.1 }, 0);
      if (p)  tl.from(p,  { autoAlpha: 0, y: 30, duration: 1 }, 0.45);
    }
    if (btn) tl.from(btn, { autoAlpha: 0, y: 40, duration: 0.8 }, 0.75); // closer after the paragraph

    // 2) laptop, then the event card, then the stats card — bigger gap after the button
    if (laptop) tl.fromTo(laptop, { autoAlpha: 0, y: 44 }, { autoAlpha: 1, y: 0, duration: 0.85, ease: "power3.out" }, 1.25);
    if (evt)    tl.fromTo(evt,    { autoAlpha: 0, x: 40, y: -10 }, { autoAlpha: 1, x: 0, y: 0, duration: 0.7, ease: "power3.out" }, 1.5);
    if (stats)  tl.fromTo(stats,  { autoAlpha: 0, y: 30 },
      { autoAlpha: 1, y: 0, duration: 0.7, ease: "power3.out",
        onComplete: function () { gsap.delayedCall(0.15, runScore); } }, 1.7); // count starts a beat after the stats are in
    // 3) big green circle scales in last
    if (circle) tl.fromTo(circle, { autoAlpha: 0, scale: 0.55, transformOrigin: "50% 50%" },
      { autoAlpha: 1, scale: 1, duration: 0.62, ease: "back.out(1.5)" }, 1.9);
  };

  rooyaFontsReady(run);
})();

/* ---- Industries masthead (ind-hero) entrance — same text intro as the other mastheads (h1 chars,
   paragraph masked lines, then the button), then the fleet DRIVES IN from the right: van, bus, car.
   The status pills follow, and the telemetry cards land last. */
(function () {
  var root = document.querySelector(".ind-hero");
  if (!root) return;
  // CSS holds this hidden through the first paint; nothing below may return without releasing it
  var wrap = root.querySelector(".ind-hero__inner");

  var copy = root.querySelector(".ind-hero__copy");
  var h1 = copy && copy.querySelector("h1");
  var p = copy && copy.querySelector("p");
  var btn = copy && copy.querySelector(".btn");
  var shape = root.querySelector(".ind-hero__shape");
  var van = root.querySelector(".ind-hero__van");
  var bus = root.querySelector(".ind-hero__bus");
  var car = root.querySelector(".ind-hero__car");
  var labels = root.querySelectorAll(".ind-hero__label");
  var infos = root.querySelectorAll(".ind-hero__info");
  // each status pill belongs to the vehicle it sits above, and follows it in
  var labelVan = root.querySelector(".ind-hero__label--parked");
  var labelBus = root.querySelector(".ind-hero__label--trip");
  var labelCar = root.querySelector(".ind-hero__label--offline");

  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (typeof gsap === "undefined" || reduce) { rooyaRelease(wrap); return; } // no motion → as authored

  // Not on a translated page: a split heading is what the translator turns into nonsense, and the
  // plain-fade branch below is a perfectly good intro.
  var hasSplit = window.rooyaCanSplit();
  if (hasSplit) gsap.registerPlugin(SplitText);

  // hide copy + every stage piece until the intro plays (nothing flashes in unanimated)
  var pieces = [shape, van, bus, car].filter(Boolean)
    .concat(Array.prototype.slice.call(labels), Array.prototype.slice.call(infos));
  if (copy) gsap.set(copy, { autoAlpha: 0 });
  if (pieces.length) gsap.set(pieces, { autoAlpha: 0 });

  /* Telemetry counters. The MARKUP holds the final, formatted figure — that is the source of truth,
     so a page with no JS, or one that took the reduced-motion exit above, shows the cards as authored
     and this module never gets to touch them. Only once we know the intro is going to run do the
     figures get wound back to zero, and that happens while .has-js still has the hero hidden, so the
     real numbers are never briefly on screen. */
  var values = root.querySelectorAll(".ind-hero__info-value");
  var format = function (el, n) {
    var dp = +(el.getAttribute("data-decimals") || 0);
    // en-US, not the visitor's locale: the figure belongs to an English design, and a locale that
    // groups differently (or uses a decimal comma) would stop matching the label beside it.
    return n.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
  };
  var countUp = function (el, dur) {
    var target = parseFloat(el.getAttribute("data-count"));
    if (!isFinite(target)) return;
    var t0 = null;
    var step = function (ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      // cubic ease-out, the same shape the home-s8 counters run on
      el.textContent = format(el, target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = format(el, target); // land on the authored figure, not on 6,343.79
    };
    requestAnimationFrame(step);
  };
  Array.prototype.forEach.call(values, function (el) { el.textContent = format(el, 0); });

  rooyaRelease(wrap);   // every piece carries its own visibility now

  var run = function () {
    if (copy) gsap.set(copy, { autoAlpha: 1 }); // reveal container; pieces start hidden via .from
    if (btn) gsap.set(btn, { transition: "none" }); // silence the .btn CSS transition vs GSAP

    var tl = gsap.timeline({
      defaults: { ease: "power4.out" },
      onComplete: function () { if (btn) gsap.set(btn, { clearProps: "transition" }); }
    });

    // 1) text — h1 char-by-char, paragraph masked lines, button just after
    if (hasSplit && h1 && p) {
      var words = new SplitText(h1, { type: "words", wordsClass: "s1-word" });
      var chars = rooyaSplitChars(words);
      var lines = new SplitText(p, { type: "lines", linesClass: "s1-line" });
      gsap.set(words.words, { display: "inline-block" });
      gsap.set(lines.lines, { overflow: "hidden" });

      tl.from(chars.chars, { autoAlpha: 0, y: 50, duration: 1.1, stagger: chars.stagger }, 0)
        .from(lines.lines, { yPercent: 120, autoAlpha: 0, duration: 1, stagger: 0.08 }, 0.45);

      rooyaUnsplit(tl, [words, chars, lines]);
    } else {
      if (h1) tl.from(h1, { autoAlpha: 0, y: 40, duration: 1.1 }, 0);
      if (p)  tl.from(p,  { autoAlpha: 0, y: 30, duration: 1 }, 0.45);
    }
    if (btn) tl.from(btn, { autoAlpha: 0, y: 40, duration: 0.8 }, 0.75);

    // 2) the fleet arrives one vehicle at a time, each followed by its own status pill: the vehicle
    //    drives in from off-stage, and its label pops above it just as it settles.
    //
    //    Which side it comes from has to follow the page. The offsets below are distances, not
    //    directions; this sign is the direction. On an Arabic page the artwork is the mirrored set
    //    of exports, so the vehicles face the other way — sliding them in from the right as before
    //    drove them in backwards, tail first.
    var driveFrom = (window.rooyaRTL && window.rooyaRTL()) ? -1 : 1;

    var driveIn = function (el, from, dur, at) {
      if (!el) return;
      tl.fromTo(el, { autoAlpha: 0, x: from * driveFrom },
        { autoAlpha: 1, x: 0, duration: dur, ease: "power2.out" }, at);
    };
    // the pill is quick and fires while its vehicle is still easing to a stop — with a power2.out
    // slide the vehicle reads as "arrived" well before its tween ends, so this lands right on it
    var pop = function (el, at) {
      if (!el) return;
      tl.fromTo(el, { autoAlpha: 0, y: 12, scale: 0.93, transformOrigin: "50% 100%" },
        { autoAlpha: 1, y: 0, scale: 1, duration: 0.24, ease: "back.out(2.2)" }, at);
    };

    // 1s drive-ins still starting 0.5s apart, so each vehicle is halfway in as the next one sets off
    driveIn(van, 520, 1, 1.15);      // van…
    pop(labelVan, 1.65);             // …then "Parked"
    driveIn(bus, 620, 1, 1.65);      // bus…
    pop(labelBus, 2.15);             // …then "On trip"
    driveIn(car, 460, 1, 2.15);      // car…
    pop(labelCar, 2.85);             // …then "Offline", held back a beat longer than the other two

    // 3) the telemetry cards land one by one, 4) and the arc scales in last, swinging upright
    if (infos.length) tl.fromTo(infos, { autoAlpha: 0, y: 26 },
      { autoAlpha: 1, y: 0, duration: 0.5, ease: "power3.out", stagger: 0.15 }, 3.15);

    // Each figure starts a beat into its own card's fade, so the card is legible before its number
    // begins to move — following the 0.15 stagger above rather than all three running at once.
    Array.prototype.forEach.call(values, function (el, i) {
      tl.call(function () { countUp(el, 1100); }, null, 3.15 + i * 0.15 + 0.15);
    });

    if (shape) tl.fromTo(shape, { autoAlpha: 0, scale: 0.8, rotation: -7, transformOrigin: "50% 50%" },
      { autoAlpha: 1, scale: 1, rotation: 0, duration: 0.9, ease: "back.out(1.4)" }, 3.7);
  };

  rooyaFontsReady(run);
})();

/* ---- Solutions masthead (solutions-hero) entrance — same text intro as the other mastheads (h1
   chars, paragraph masked lines, then the button), then the composition assembles back to front:
   the arc sweeps out, the circle and the driver land in it, the left card stack slides in, and the
   right-hand alerts follow. The driver-score counter is deferred and fired by the stats card. */
(function () {
  var root = document.querySelector(".solutions-hero");
  if (!root) return;
  // CSS holds this hidden through the first paint; nothing below may return without releasing it
  var wrap = root.querySelector(".solutions-hero__inner");

  var copy = root.querySelector(".solutions-hero__copy");
  var h1 = copy && copy.querySelector("h1");
  var p = copy && copy.querySelector("p");
  var btn = copy && copy.querySelector(".btn");

  var q = function (sel) { return root.querySelector(sel); };
  var arc     = q(".solutions-hero__arc");
  var photo   = q(".solutions-hero__photo");
  var cutout  = q(".solutions-hero__cutout");
  var arc2    = q(".solutions-hero__arc2");
  var evt     = q(".solutions-hero__event");
  var tail    = q(".solutions-hero__event-tail");
  var badge   = q(".solutions-hero__alert");
  var stats   = q(".platform-hero__stats");
  // left column of UI cards, top to bottom — they come in as one stack
  var stack = [q(".solutions-hero__trips"), q(".solutions-hero__map"), q(".solutions-hero__details")]
    .filter(Boolean);

  var ring = root.querySelector("[data-score-ring]");
  var runScore = function () { if (ring && ring._runScore) ring._runScore(); };

  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (typeof gsap === "undefined" || reduce) { rooyaRelease(wrap); runScore(); return; } // no motion → just count

  // Not on a translated page: a split heading is what the translator turns into nonsense, and the
  // plain-fade branch below is a perfectly good intro.
  var hasSplit = window.rooyaCanSplit();
  if (hasSplit) gsap.registerPlugin(SplitText);

  // hide copy + every stage piece until the intro plays (nothing flashes in unanimated)
  var pieces = [arc, photo, cutout, arc2, evt, tail, badge, stats].filter(Boolean).concat(stack);
  if (copy) gsap.set(copy, { autoAlpha: 0 });
  if (pieces.length) gsap.set(pieces, { autoAlpha: 0 });
  rooyaRelease(wrap);   // every piece carries its own visibility now

  var run = function () {
    if (copy) gsap.set(copy, { autoAlpha: 1 }); // reveal container; pieces start hidden via .from
    if (btn) gsap.set(btn, { transition: "none" }); // silence the .btn CSS transition vs GSAP

    var tl = gsap.timeline({
      defaults: { ease: "power4.out" },
      onComplete: function () { if (btn) gsap.set(btn, { clearProps: "transition" }); }
    });

    // 1) text — h1 char-by-char, paragraph masked lines, button just after
    if (hasSplit && h1 && p) {
      var words = new SplitText(h1, { type: "words", wordsClass: "s1-word" });
      var chars = rooyaSplitChars(words);
      var lines = new SplitText(p, { type: "lines", linesClass: "s1-line" });
      gsap.set(words.words, { display: "inline-block" });
      gsap.set(lines.lines, { overflow: "hidden" });

      tl.from(chars.chars, { autoAlpha: 0, y: 50, duration: 1.1, stagger: chars.stagger }, 0)
        .from(lines.lines, { yPercent: 120, autoAlpha: 0, duration: 1, stagger: 0.08 }, 0.45);

      rooyaUnsplit(tl, [words, chars, lines]);
    } else {
      if (h1) tl.from(h1, { autoAlpha: 0, y: 40, duration: 1.1 }, 0);
      if (p)  tl.from(p,  { autoAlpha: 0, y: 30, duration: 1 }, 0.45);
    }
    if (btn) tl.from(btn, { autoAlpha: 0, y: 40, duration: 0.8 }, 0.75);

    // 2) the backdrop first: the swoosh unfurls from its low left end, so it sweeps up and to the
    //    right rather than just appearing at size
    if (arc) tl.fromTo(arc,
      { autoAlpha: 0, scale: 0.82, rotation: -8, transformOrigin: "0% 100%" },
      { autoAlpha: 1, scale: 1, rotation: 0, duration: 0.85, ease: "back.out(1.3)" }, 1.05);

    // 3) the circle opens up. The cut-out driver is a second layer over the very same 567 box, so it
    //    rides the identical tween at the identical time — anything else would slide the two halves
    //    of one picture apart.
    var circle = [photo, cutout].filter(Boolean);
    if (circle.length) tl.fromTo(circle,
      { autoAlpha: 0, scale: 0.72, transformOrigin: "50% 50%" },
      { autoAlpha: 1, scale: 1, duration: 0.7, ease: "back.out(1.3)" }, 1.22);

    // 4) the left-hand data cards slide in as one stack, top card first
    if (stack.length) tl.fromTo(stack,
      { autoAlpha: 0, x: -44 },
      { autoAlpha: 1, x: 0, duration: 0.6, ease: "power3.out", stagger: 0.12 }, 1.55);

    // 5) the alert comes from the right. The tail is a separate element pinned under the card, so it
    //    has to travel on the same tween — animating only the card would tear the two apart in flight
    if (evt) tl.fromTo([evt, tail].filter(Boolean),
      { autoAlpha: 0, x: 44, y: -12 },
      { autoAlpha: 1, x: 0, y: 0, duration: 0.65, ease: "power3.out" }, 1.85);

    // …and the fatigue badge snaps onto its corner
    if (badge) tl.fromTo(badge,
      { autoAlpha: 0, scale: 0.5, rotation: -14, transformOrigin: "50% 50%" },
      { autoAlpha: 1, scale: 1, rotation: 0, duration: 0.45, ease: "back.out(2)" }, 2.05);

    // 6) the small counter-curve, unfurling from its right end like the big arc did from its left
    if (arc2) tl.fromTo(arc2,
      { autoAlpha: 0, scale: 0.6, transformOrigin: "100% 50%" },
      { autoAlpha: 1, scale: 1, duration: 0.5, ease: "back.out(1.6)" }, 2.18);

    // 7) the behaviour card lands last, and the score starts counting a beat after it settles
    if (stats) tl.fromTo(stats,
      { autoAlpha: 0, y: 32 },
      { autoAlpha: 1, y: 0, duration: 0.7, ease: "power3.out",
        onComplete: function () { gsap.delayedCall(0.15, runScore); } }, 2.28);
  };

  rooyaFontsReady(run);
})();

/* ---- company-s2 composition — the photo, then the connector lines drawn around it ----
   Lifted out of the SECTIONS driver with its sequence intact; the copy column beside it is static. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  var root = document.querySelector(".company-s2");
  if (!root) return;
  gsap.registerPlugin(ScrollTrigger);

  var figure = root.querySelector(".company-s2__figure");
  if (!figure) return;

  var LEAD = 0.3;   // an empty first beat, as on the other stilled sections

  var tl = gsap.timeline({
    defaults: { ease: "power4.out" },
    scrollTrigger: { trigger: figure, start: "top 80%", toggleActions: "play none none none" }
  });

  var photo = root.querySelector(".company-s2__figure img");
  var lines = root.querySelector(".company-s2__lines");

  if (photo) tl.fromTo(photo,
    { autoAlpha: 0, y: 44 },
    { autoAlpha: 1, y: 0, duration: 0.85, ease: "power3.out" }, LEAD);

  // the connector lines (and the badges riding them, which live inside this layer) only come
  // up once the photo is there — they read as drawn around it, not as a backdrop it lands on.
  // Plain opacity, no movement: they are anchored to the composition and must not slide.
  if (lines) {
    tl.fromTo(lines,
      { autoAlpha: 0 },
      { autoAlpha: 1, duration: 0.9, ease: "power2.out" }, LEAD + 0.5);
    // …and only now do the badges set off, from the start of their paths — looked up at fire
    // time, since the rider module publishes this hook after this timeline was built
    tl.call(function () {
      if (lines._startRiders) lines._startRiders();
    }, null, LEAD + 0.5);
  }
})();

/* ---- solutions-s5 composition — the map, then the two panels overhanging its left edge ----
   Lifted out of the SECTIONS driver with its sequence intact; the copy column beside it is static. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  var root = document.querySelector(".solutions-s5");
  if (!root) return;
  gsap.registerPlugin(ScrollTrigger);

  var media = root.querySelector(".solutions-s5__media");
  if (!media) return;

  var LEAD = 0.3;   // an empty first beat, as on the other stilled sections

  var tl = gsap.timeline({
    defaults: { ease: "power4.out" },
    scrollTrigger: { trigger: media, start: "top 80%", toggleActions: "play none none none" }
  });

  var map = root.querySelector(".solutions-s5__map");
  var panels = root.querySelectorAll(".solutions-s5__info");

  // the map is the only piece in flow — it sets the column's height and both panels are
  // positioned against it, so it has to land before them
  if (map) tl.fromTo(map,
    { autoAlpha: 0, y: 44 },
    { autoAlpha: 1, y: 0, duration: 0.85, ease: "power3.out" }, LEAD);

  // both panels overhang the map's left edge, so they slide out from that side, top one first
  // (querySelectorAll hands them back in document order, which here is also top to bottom)
  if (panels.length) tl.fromTo(panels,
    { autoAlpha: 0, x: -40 },
    { autoAlpha: 1, x: 0, duration: 0.6, ease: "power3.out", stagger: 0.16 }, LEAD + 0.28);
})();

/* ---- solutions-s3 media — the photo fades up; the copy beside it stays put ----
   All that survives of this section's intro: the subtitle, heading and both paragraphs are static. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  var media = document.querySelector(".solutions-s3__media");
  if (!media) return;
  gsap.registerPlugin(ScrollTrigger);

  // fromTo, not from: the "from" state lands the moment this runs, so the photo is hidden well before
  // its trigger rather than sitting there waiting to be animated.
  gsap.fromTo(media, { autoAlpha: 0, y: 40 }, {
    autoAlpha: 1, y: 0, duration: 0.8, ease: "power4.out",
    delay: 0.3,   // the same beat the other stilled sections wait out
    scrollTrigger: { trigger: media, start: "top 80%", toggleActions: "play none none none" }
  });
})();

/* ---- solutions-s1 composition — the photo, then the two cards hanging off it ----
   Lifted out of the SECTIONS driver with its sequence intact; the whole copy column beside it is
   deliberately still. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  var root = document.querySelector(".solutions-s1");
  if (!root) return;
  gsap.registerPlugin(ScrollTrigger);

  var media = root.querySelector(".solutions-s1__media");
  if (!media) return;

  var LEAD = 0.3;   // an empty first beat, as on the platform compositions

  var tl = gsap.timeline({
    defaults: { ease: "power4.out" },
    scrollTrigger: { trigger: media, start: "top 80%", toggleActions: "play none none none" }
  });

  var q = function (sel) { return root.querySelector(sel); };
  var photo  = q(".solutions-s1__photo");
  var stats  = q(".solutions-s1__stats");
  var events = q(".solutions-s1__events");

  // the photo is the only piece in flow — it sets the column's height and both cards hang off
  // it, so it has to land first or they would arrive against nothing
  if (photo) tl.fromTo(photo,
    { autoAlpha: 0, y: 44 },
    { autoAlpha: 1, y: 0, duration: 0.85, ease: "power3.out" }, LEAD);

  // the counts card overhangs the photo's top-right corner, so it comes in from that corner
  if (stats) tl.fromTo(stats,
    { autoAlpha: 0, x: 40, y: -18 },
    { autoAlpha: 1, x: 0, y: 0, duration: 0.65, ease: "power3.out" }, LEAD + 0.28);

  // …and the behaviours strip rises from under the photo's bottom edge, where it sits
  if (events) tl.fromTo(events,
    { autoAlpha: 0, y: 34 },
    { autoAlpha: 1, y: 0, duration: 0.65, ease: "power3.out" }, LEAD + 0.5);
})();

/* ---- Solutions FNOL card (solutions-s4) — tablet in, then the two green shapes ----
   The copy on this card is deliberately left alone: it sits on a dark panel that is already fully
   painted by the time you reach it, and animating it too made the whole card feel like it was still
   loading. So this is not a SECTIONS entry — that driver always animates the heading.

   The shapes are ::before / ::after on the card. GSAP cannot be handed a pseudo-element, so each one
   is fed by a custom property on the card itself (--s4-shape1 / --s4-shape2, 0 → 1); the CSS turns
   that into opacity + scale. See the note above the pseudo-elements in solutions.scss. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  var card = document.querySelector(".solutions-s4__card");
  if (!card) return;
  var tablet = card.querySelector(".solutions-s4__media");
  if (!tablet) return;

  gsap.registerPlugin(ScrollTrigger);

  gsap.set(tablet, { autoAlpha: 0 });
  gsap.set(card, { "--s4-shape1": 0, "--s4-shape2": 0 });

  var tl = gsap.timeline({
    // triggered off the tablet, not the card: the card is ~700px tall and the tablet is pinned to
    // its bottom edge, so a card-top trigger would play the whole thing below the fold
    scrollTrigger: { trigger: tablet, start: "top 85%", toggleActions: "play none none none" }
  });

  tl.fromTo(tablet, { autoAlpha: 0, y: 56 },
    { autoAlpha: 1, y: 0, duration: 0.9, ease: "power3.out" }, 0);

  // the ring in the top-left first, then the one in the bottom-right — reading order across the card
  tl.to(card, { "--s4-shape1": 1, duration: 0.7, ease: "power2.out" }, 0.5);
  tl.to(card, { "--s4-shape2": 1, duration: 0.7, ease: "power2.out" }, 0.68);
})();

/* ---- Company + Contact mastheads — same text intro as the other mastheads (h1 chars, paragraphs as
   masked lines, then the button), and the thing on the right last. Both run to TWO paragraphs rather
   than the one the platform/industries/solutions mastheads carry, so both get masked lines and the
   button is held back to sit after them. Both sit on the same map artwork, which is a CSS background
   — there is nothing else on either stage to bring in. */
(function () {
  var HEROES = [
    { root: ".company-hero", copy: ".company-hero__copy", wrap: ".company-hero__inner",
      lead: ".company-hero__lead", sub: ".company-hero__sub",
      // a big photo reads as settling into place rather than switching on if it eases up to size too
      media: ".company-hero__media img", mediaScale: 0.96 },
    // the contact card is not scaled: text inside a form going through a transform resample looks
    // soft on the way in, and the card should arrive as one solid object anyway
    { root: ".contact-hero", copy: ".contact-hero__copy", wrap: ".contact-hero__inner",
      lead: ".contact-hero__lead", sub: ".contact-hero__sub",
      media: ".contact-hero__form" },
    // this one's stage is three pieces rather than one, so it brings its own `build` instead of the
    // single `media` tween. Same choreography as the home-s4 packshots: each camera scales in with a
    // slight rotation, tilting opposite ways, and the circle scales in LAST — the ring reads as being
    // drawn behind the pair once they are in place, rather than as a target they drop onto.
    { root: ".hardware-hero", copy: ".hardware-hero__copy", wrap: ".hardware-hero__inner",
      lead: ".hardware-hero__lead", sub: ".hardware-hero__sub",
      pieces: ".hardware-hero__stage img",
      build: function (tl, root) {
        var ring = root.querySelector(".hardware-hero__ring");
        var k245 = root.querySelector(".hardware-hero__device--k245");
        var k220 = root.querySelector(".hardware-hero__device--k220");

        // fromTo, not from: the module has already hidden every piece, so a `from` would take that
        // hidden state as its DESTINATION. home-s4 can use `from` because nothing pre-hides it there.
        if (k245) tl.fromTo(k245,
          { autoAlpha: 0, scale: 0.6, rotation: -10, transformOrigin: "50% 50%" },
          { autoAlpha: 1, scale: 1, rotation: 0, duration: 0.8, ease: "back.out(1.5)" }, 1.05);

        if (k220) tl.fromTo(k220,
          { autoAlpha: 0, scale: 0.6, rotation: 9, transformOrigin: "50% 50%" },
          { autoAlpha: 1, scale: 1, rotation: 0, duration: 0.8, ease: "back.out(1.5)" }, 1.4);

        if (ring) tl.fromTo(ring,
          { autoAlpha: 0, scale: 0.4, transformOrigin: "50% 50%" },
          { autoAlpha: 1, scale: 1, duration: 0.7, ease: "back.out(1.4)" }, 1.75);
      } }
  ];

  // CSS holds each `wrap` hidden through the first paint, so every exit below has to release it
  var wrapOf = function (cfg) {
    var root = document.querySelector(cfg.root);
    return root && root.querySelector(cfg.wrap);
  };

  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (typeof gsap === "undefined" || reduce) {   // no motion → the masthead just sits there, as authored
    HEROES.forEach(function (cfg) { rooyaRelease(wrapOf(cfg)); });
    return;
  }

  // Not on a translated page: a split heading is what the translator turns into nonsense, and the
  // plain-fade branch below is a perfectly good intro.
  var hasSplit = window.rooyaCanSplit();
  if (hasSplit) gsap.registerPlugin(SplitText);

  HEROES.forEach(function (cfg) {
    var root = document.querySelector(cfg.root);
    if (!root) return;
    var wrap = root.querySelector(cfg.wrap);

    var copy = root.querySelector(cfg.copy);
    var h1 = copy && copy.querySelector("h1");
    var lead = copy && copy.querySelector(cfg.lead);
    var sub = copy && copy.querySelector(cfg.sub);
    var btn = copy && copy.querySelector(".btn");
    // a hero's stage is either one element (`media`) or several (`pieces` + its own `build`)
    var media = cfg.media ? root.querySelector(cfg.media) : null;
    var pieces = cfg.pieces ? [].slice.call(root.querySelectorAll(cfg.pieces)) : [];
    if (!copy || !h1) { rooyaRelease(wrap); return; }

    gsap.set(copy, { autoAlpha: 0 });          // hide copy + stage until the intro plays (no flash)
    if (media) gsap.set(media, { autoAlpha: 0 });
    if (pieces.length) gsap.set(pieces, { autoAlpha: 0 });
    rooyaRelease(wrap);                        // every piece carries its own visibility now

    rooyaFontsReady(function () {
      gsap.set(copy, { autoAlpha: 1 });        // reveal container; pieces start hidden via .from
      if (btn) gsap.set(btn, { transition: "none" }); // silence the .btn CSS transition vs GSAP

      var tl = gsap.timeline({
        defaults: { ease: "power4.out" },
        onComplete: function () { if (btn) gsap.set(btn, { clearProps: "transition" }); }
      });

      // masked lines: the wrapper clips while the line slides up out of it
      var lineSplits = [];
      var maskLines = function (el, dur, at) {
        var split = new SplitText(el, { type: "lines", linesClass: "s1-line" });
        lineSplits.push(split);
        gsap.set(split.lines, { overflow: "hidden" });
        tl.from(split.lines, { yPercent: 120, autoAlpha: 0, duration: dur, stagger: 0.08 }, at);
      };

      if (hasSplit) {
        var words = new SplitText(h1, { type: "words", wordsClass: "s1-word" });
        var chars = rooyaSplitChars(words);
        gsap.set(words.words, { display: "inline-block" });
        tl.from(chars.chars, { autoAlpha: 0, y: 50, duration: 1.1, stagger: chars.stagger }, 0);

        if (lead) maskLines(lead, 1, 0.45);
        if (sub)  maskLines(sub, 0.9, 0.78);

        rooyaUnsplit(tl, lineSplits.concat([words, chars]));
      } else {
        // graceful fallback if SplitText didn't load
        tl.from(h1, { autoAlpha: 0, y: 40, duration: 1.1 }, 0);
        if (lead) tl.from(lead, { autoAlpha: 0, y: 30, duration: 1 }, 0.45);
        if (sub)  tl.from(sub,  { autoAlpha: 0, y: 24, duration: 0.9 }, 0.78);
      }

      if (btn) tl.from(btn, { autoAlpha: 0, y: 40, duration: 0.8 }, 1);

      if (media) tl.fromTo(media,
        { autoAlpha: 0, y: 40, scale: cfg.mediaScale || 1 },
        { autoAlpha: 1, y: 0, scale: 1, duration: 0.95, ease: "power3.out" }, 1.15);

      if (typeof cfg.build === "function") cfg.build(tl, root);
    });
  });
})();

/* ---- Scaled connector-line stages — fit the fixed 1920 stage to the viewport ----
   The same scaler as the home-s2 diagram, and for the same reason: a stage carries the connector
   lines AND the copy, so scaling it as one unit is what keeps the lines meeting the photos and the
   text at every width. The stage is taken out of flow by the transform, so the viewport around it
   has to be given the SCALED height back or the section would collapse to nothing.
   Runs for every .lines-viewport on the page — see the block at the end of default.scss. */
(function () {
  var vps = document.querySelectorAll(".lines-viewport");
  if (!vps.length) return;

  var DESIGN = 1920; // width these stages are designed at

  Array.prototype.forEach.call(vps, function (vp) {
    var stage = vp.querySelector(".lines-stage");
    if (!stage) return;

    // Setting the height shifts everything below the section, so ScrollTrigger has to recompute —
    // this lands after first layout and after images, well past its own initial measurement.
    var lastH = null;
    function setH(h) {
      if (h === lastH) return;
      lastH = h;
      vp.style.height = h;
      if (window.rooyaRefreshST) window.rooyaRefreshST(); // debounced — recompute pins once settled
    }

    function fit() {
      // stack point must fire with the CSS @media (max-width: 900px) — matchMedia mirrors it exactly
      if (window.matchMedia && window.matchMedia("(max-width: 900px)").matches) {
        stage.style.transform = "";
        setH("");
        return;
      }
      // clientWidth, not innerWidth: the section fills the content area, scrollbar excluded
      var s = document.documentElement.clientWidth / DESIGN;
      stage.style.transform = "scale(" + s + ")";
      // offsetHeight is the unscaled (1920-layout) height; reserve the scaled one
      setH((stage.offsetHeight * s) + "px");
    }

    fit();
    window.addEventListener("resize", fit);
    window.addEventListener("load", fit);   // images settle -> the stage gets its final height
    if ("ResizeObserver" in window) new ResizeObserver(fit).observe(stage);
  });
})();

/* ---- Connector lines — arrows riding them ----
   Same idea as the home-s2 connectors: one badge per line, looping forever, staggered so they never
   move in unison. Two things are simpler here.

   Direction is NOT normalised. home-s2 resolves each path's travel sense so every arrow runs left to
   right; these three are authored in the direction they are meant to travel (1 rightwards, 2 and 3
   leftwards, which is what the design shows), so following the path as written is the whole of it.

   Everything below works in the stage's own 1920-design px, never in screen px: the badges live
   inside the stage, so the same CSS transform that scales the lines carries them along. getCTM (not
   getScreenCTM) is what keeps that true — it stops at the SVG viewport, which every engine agrees on,
   whereas WebKit does not fold an HTML ancestor's CSS transform into getScreenCTM and the badges
   would land hundreds of px off the stage. */
(function () {
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  var layers = document.querySelectorAll(".lines-layer");
  if (!layers.length) return;

  // arrow badge (ids stripped so the copies don't clash); sized by CSS .line-arrow
  var ARROW =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 31.111 31.114">' +
    '<g transform="translate(-187.766 -1497.795)">' +
    '<path d="M47.25,32.7A14.555,14.555,0,1,1,32.693,18.145,14.556,14.556,0,0,1,47.25,32.7" transform="translate(170.627 1480.65)" fill="#00995d"/>' +
    '<path d="M47.25,32.7A14.555,14.555,0,1,1,32.693,18.145,14.556,14.556,0,0,1,47.25,32.7Z" transform="translate(170.627 1480.65)" fill="none" stroke="#fff" stroke-miterlimit="10" stroke-width="2"/>' +
    '<path d="M32.965,26.882,21.888,21.557l2.654,5.095-2.928,5Z" transform="translate(176.879 1486.79)" fill="#fff"/>' +
    '</g></svg>';

  // TRAVEL is the time a badge spends actually MOVING, and it is what sets the pace. Holding the
  // whole cycle constant instead — as this did briefly — made the speed depend on how many lines a
  // layer has: the duty cycle below eats into the cycle, so four lines travelled a third faster than
  // three. Fixing travel and letting the cycle stretch keeps every badge on the site moving at one
  // speed; only the resting time between passes grows.
  var TRAVEL = 22000;   // ms a badge spends crossing its line
  var MAX_ON = 3;       // most badges allowed on screen at once, however many lines a layer has
  var HALF = 15.5;      // half the badge, to centre it on the path point
  var EXT = 140;        // arc px of runway past an end that leaves the layer, so it slides off screen
  var EDGE = 60;        // badge radius + glow: hide only once it is fully past the layer's edge
  var FADE = 0.07;      // share of the pass spent fading, at an end that stops INSIDE the layer

  Array.prototype.forEach.call(layers, function (lines) {
  // the stage is the coordinate space (its box is what ox/oy and the scale are measured against)…
  var layer = lines.closest(".lines-stage");
  if (!layer) return;

  // …but the badges belong INSIDE the lines layer, which buys two things: they sit in the same
  // stacking context as the lines and so pass behind the photos and the copy rather than over them,
  // and the intro can fade lines and badges in together as one thing. The two boxes coincide anyway
  // (the layer is inset:0 on the stage), so the coordinates below are unaffected.
  var arrows = document.createElement("div");
  arrows.className = "lines-arrows";
  lines.appendChild(arrows);

  var riders = [];
  Array.prototype.forEach.call(lines.querySelectorAll(".lines-layer__line"), function (span, i, all) {
    var geo = span.querySelector("path");
    if (!geo || !geo.getTotalLength) return;
    var el = document.createElement("div");
    el.className = "line-arrow";
    el.innerHTML = ARROW;
    arrows.appendChild(el);
    riders.push({
      geo: geo,
      svg: geo.ownerSVGElement,
      el: el,
      len: geo.getTotalLength(),   // user units — unaffected by how wide the svg is drawn
      idx: i,
      phase: 0,         // set once the rider count is known, just below
      ox: null, oy: null,
      extIn: null, extOut: null,   // runway per END — resolved on the first frame, see tick()
      from: 0, to: 0,              // the travel range in path units, derived from those
      pt: geo.ownerSVGElement.createSVGPoint()
    });
  });
  if (!riders.length) return;

  // Fraction of a cycle each badge spends travelling; the rest it waits off-stage. Evenly spread
  // starts x this fraction is the number visible at any moment, so the cap is expressed as a duty
  // cycle. Three lines or fewer and every badge simply runs continuously.
  var VISIBLE = Math.min(1, MAX_ON / riders.length);
  var CYCLE = TRAVEL / VISIBLE;   // travel is fixed, so the cycle is what stretches to make room

  // Phase offsets, one even share of the cycle each. That even spread x the visible share above is
  // what holds the cap: the count on screen is the same at every moment instead of drifting.
  //
  // These were head STARTS until the ramp gave it away: a delayed start means the spacing has to be
  // waited out before the layer is populated, and at four or five lines that is most of a minute of
  // arrows trickling in. As phases the same spacing is there from the first frame — the layer opens
  // already in its steady state, at the cost of the badges behind the first one being part way along
  // their lines when it does. There is no arrangement that is both evenly spread and all-at-the-start.
  //
  // Dealt in the order the lines SIT ON THE PAGE, not the order they appear in the markup. The two
  // are nothing like each other — a layer's paths are authored in whatever order the artwork exported
  // them, so line 1 can easily be the lowest on screen. Measured at start(), by which point the
  // section is on screen and its layout has settled.
  var dealPhases = function () {
    riders.slice()
      .sort(function (a, b) {
        return a.svg.getBoundingClientRect().top - b.svg.getBoundingClientRect().top;
      })
      .forEach(function (r, i) { r.phase = i / riders.length; });
  };

  // Each line's <svg> offset inside the stage, in the stage's own UNSCALED px — hence dividing the
  // rect deltas back down by the stage's current scale. Cached rather than read per frame: reading
  // three rects every frame would be three forced layouts, and only a reflow can change these.
  // Mirrored layer? rtl.scss flips company-s2's whole line layer so the badges travel with their
  // paths. Everything else here works in local space and comes along for free; these offsets are the
  // one screen-space read, and a mirror turns screen-left into local-right.
  //
  // It only shows on a line that does not begin at local x=0. Two of the three span the full stage,
  // so their offset measures 0 either way and they look correct; company-s2__line--1 is pinned to
  // the far edge, and its offset came back as 0 instead of its real inset — putting that badge about
  // a thousand design px from its own path. One arrow in open space, two of them fine.
  // The flip lives on the LINES layer, not on the stage — `layer` above is the stage, which only ever
  // carries the JS scale. Asking the stage whether it is mirrored always answered no, which is why
  // the correction below did nothing the first time it was written.
  //
  // The stage box is still the right thing to measure against: scaleX(-1) about a box's own centre
  // maps that box onto itself, so the layer's screen rect is unchanged by the flip and the two
  // coincide exactly as the note above says. What the flip does move is the svg INSIDE it.
  function mirrored() {
    var t = getComputedStyle(lines).transform;
    return t.indexOf("matrix(-") === 0;
  }

  function measureOffsets() {
    if (!layer.offsetWidth) return;
    var rect = layer.getBoundingClientRect();
    var s = rect.width / layer.offsetWidth || 1;
    var mir = mirrored();
    riders.forEach(function (r) {
      var sr = r.svg.getBoundingClientRect();
      // Distance from the LEADING edge to the line's own — the right-hand one once flipped. The
      // badge is drawn inside the flipped layer too, so a correct local offset lands it back on the
      // path: both are mirrored by the same transform, once.
      r.ox = mir ? (rect.right - sr.right) / s : (sr.left - rect.left) / s;
      r.oy = (sr.top - rect.top) / s;
      r.extIn = r.extOut = null;   // a reflow can move an end in or out; re-resolve it
    });
  }
  window.addEventListener("resize", measureOffsets);
  window.addEventListener("load", measureOffsets);

  // Point at a "virtual" length: inside [0,len] the real path point, beyond either end extrapolated
  // straight along the tangent — that is the runway the badge slides in and out along.
  function ptv(geo, vl, len) {
    if (vl < 0) {
      var p0 = geo.getPointAtLength(0), p1 = geo.getPointAtLength(Math.min(1, len));
      var dx = p0.x - p1.x, dy = p0.y - p1.y, d = Math.hypot(dx, dy) || 1;
      return { x: p0.x + dx / d * (-vl), y: p0.y + dy / d * (-vl) };
    }
    if (vl > len) {
      var pn = geo.getPointAtLength(len), pm = geo.getPointAtLength(Math.max(0, len - 1));
      var ex = pn.x - pm.x, ey = pn.y - pm.y, e = Math.hypot(ex, ey) || 1;
      return { x: pn.x + ex / e * (vl - len), y: pn.y + ey / e * (vl - len) };
    }
    return geo.getPointAtLength(vl);
  }

  // The badges wait for the lines. Their layer fades in partway through the section's intro (see the
  // company-s2 entry in SECTIONS), and starting the clock at page load meant that by the time anyone
  // scrolled down here the three were already mid-flight when the layer appeared. So the intro calls
  // this, and the clock starts from zero at that moment: every badge sets off from the START of its
  // own path, one after another, LEAD apart. That is also why they carry a head start rather than a
  // phase — a phase would place two of them mid-path on the very first frame, which is the thing
  // being fixed. With no intro running at all (no GSAP), nothing would ever call this, so start now.
  var started = false;
  var t0 = null;
  var start = function () { if (!started) { started = true; t0 = null; dealPhases(); } };
  lines._startRiders = start;
  if (typeof gsap === "undefined" || !window.ScrollTrigger) start();

  function tick(ts) {
    if (!started) { requestAnimationFrame(tick); return; }
    if (t0 === null) t0 = ts;
    if (layer.offsetWidth) {          // hidden below 900 — getCTM would be null there
      var elapsed = ts - t0;

      riders.forEach(function (r) {
        var ctm = r.geo.getCTM();
        if (!ctm) return;
        if (r.ox === null) { measureOffsets(); if (r.ox === null) return; }

        // Runway, decided per END rather than once per rider. EXT exists so a badge slides in and
        // out of view along the tangent, and that only means anything where the path actually leaves
        // the layer. Give it to an end that stops INSIDE the stage — company-s2's short horizontal
        // finishes around x=972, mid-section — and the badge gets drawn 140px on past the last of the
        // line, riding empty space. Such an end gets no runway and a brief fade instead, so the badge
        // arrives and leaves ON the line rather than beyond it.
        if (r.extIn === null) {
          var e0 = r.geo.getPointAtLength(0), e1 = r.geo.getPointAtLength(r.len);
          r.pt.x = e0.x; r.pt.y = e0.y; var s0 = r.pt.matrixTransform(ctm);
          r.pt.x = e1.x; r.pt.y = e1.y; var s1 = r.pt.matrixTransform(ctm);
          var inside = function (px) { return px > 2 && px < layer.offsetWidth - 2; };
          r.extIn  = inside(r.ox + s0.x) ? 0 : EXT;
          r.extOut = inside(r.ox + s1.x) ? 0 : EXT;

          // Stopping ON the end point is not the same as stopping on the line: the badge is centred
          // on it, so half of it — plus its glow — still hangs past the last of the stroke. Where an
          // end is inside the stage, back the travel off by that much so the whole badge stays over
          // the line. In PATH units, hence measuring how many px one unit is worth: a path's units
          // are its own, and only for this flat 948-wide one do they happen to equal px.
          var q1 = r.geo.getPointAtLength(Math.min(1, r.len));
          r.pt.x = q1.x; r.pt.y = q1.y; var sq = r.pt.matrixTransform(ctm);
          var perUnit = Math.hypot(sq.x - s0.x, sq.y - s0.y) || 1;
          var inset = (HALF + 8) / perUnit;

          r.from = -r.extIn + (r.extIn ? 0 : inset);
          r.to = r.len + r.extOut - (r.extOut ? 0 : inset);
        }

        var prog = (elapsed / CYCLE + r.phase) % 1;               // round and round, one per cycle
        if (prog >= VISIBLE) { r.el.style.opacity = 0; return; }  // resting off-stage this stretch
        prog = prog / VISIBLE;                                    // travel across the visible share

        var vl = r.from + prog * (r.to - r.from);
        var a = ptv(r.geo, vl, r.len);
        var b = ptv(r.geo, vl + 1, r.len);       // a step further along, to aim the badge
        r.pt.x = a.x; r.pt.y = a.y;
        var pa = r.pt.matrixTransform(ctm);
        r.pt.x = b.x; r.pt.y = b.y;
        var pb = r.pt.matrixTransform(ctm);
        var ang = Math.atan2(pb.y - pa.y, pb.x - pa.x) * 180 / Math.PI;

        var lx = r.ox + pa.x;
        var ly = r.oy + pa.y;

        // Past the layer's edge the badge is simply cut — the section's overflow already hides it
        // there, so no fade is needed and none is spent. A fade is only used at an end that stops
        // inside the stage, where there is nothing to disappear behind.
        var vis = 1;
        if (r.extIn === 0 && prog < FADE) vis = prog / FADE;
        if (r.extOut === 0 && prog > 1 - FADE) vis = Math.min(vis, (1 - prog) / FADE);
        r.el.style.opacity = (lx < -EDGE || lx > layer.offsetWidth + EDGE) ? 0 : vis;
        r.el.style.transform = "translate(" + (lx - HALF) + "px," + (ly - HALF) + "px) rotate(" + ang + "deg)";
      });
    }
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
  });
})();

/* ---- Hardware capabilities (hardware-s2) — the camera photo, then the six feature cards ----
   Not a SECTIONS entry: that driver builds its intro around a heading and a lead paragraph, and this
   section has neither. Its only <p>s belong to the feature cards themselves, so pointing the driver
   at it would drag one card's copy into the heading treatment.

   The cards go through ScrollTrigger.batch rather than one timeline. The grid runs three rows deep
   either side of the photo, so a single trigger would play the bottom row well below the fold; batch
   groups whatever comes into view together and staggers each group as it lands, which also means the
   column count sorts itself out — three columns on desktop, stacked further down.

   Nothing here is wired up until the intro band above (hardware-s1) has had its say. The two are
   separate sections with separate triggers, but they sit close enough together that on a tall screen
   both come into view at once and the grid would otherwise race the paragraph above it. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  var root = document.querySelector(".hardware-s2");
  if (!root) return;
  var figure = root.querySelector(".hardware-s2__figure");
  var features = root.querySelectorAll(".hardware-s2__feature");
  var layer = root.querySelector(".hardware-s2__lines");
  if (!figure && !features.length) return;

  gsap.registerPlugin(ScrollTrigger);

  // hidden straight away, not inside the handover below — otherwise everything would sit there in
  // plain sight until the band above finished and then blink out to animate
  if (figure) gsap.set(figure, { autoAlpha: 0 });
  if (features.length) gsap.set(features, { autoAlpha: 0, y: 34 });
  if (layer) gsap.set(layer, { autoAlpha: 0 });

  rooyaFontsReady(function () {
    rooyaAfterIntro(document.querySelector(".hardware-s1"), function () {
      // The cards wait for the photo, they are not merely triggered later. Two independent triggers
      // had them arriving the wrong way round: the cards' "top 88%" is LOWER on the screen than the
      // photo's "top 80%", so scrolling down crossed it first and the grid set off before the thing
      // it is arranged around. Hanging the batch off the photo's own completion makes the order a
      // fact rather than a coincidence of two numbers.
      // the connector lines (and the badges riding them, which live inside that layer) come up once
      // the cards are down — they read as drawn around the composition, not as a backdrop it lands
      // on. Plain opacity: they are registered to the grid and any movement would slide them off it.
      var startLines = function () {
        if (!layer) return;
        gsap.to(layer, { autoAlpha: 1, duration: 0.9, ease: "power2.out" });
        if (layer._startRiders) layer._startRiders();   // badges set off from the start of their paths
      };

      var startCards = function () {
        if (!features.length) { startLines(); return; }
        gsap.delayedCall(0.7, startLines);
        ScrollTrigger.batch(features, {
          start: "top 88%",
          once: true,
          onEnter: function (batch) {
            gsap.to(rooyaTopDown(batch), {
              autoAlpha: 1, y: 0, duration: 0.9, ease: "power3.out", stagger: 0.13, overwrite: true
            });
          }
        });
      };

      if (figure) {
        gsap.fromTo(figure,
          { autoAlpha: 0, y: 40, scale: 0.94 },
          { autoAlpha: 1, y: 0, scale: 1, duration: 1.15, ease: "power3.out",
            scrollTrigger: { trigger: figure, start: "top 80%", once: true },
            // Counted from the photo's START, not its onComplete. power3.out has it visually settled
            // by roughly a third of its 1.15s, so waiting for the tween to formally end left about a
            // second of nothing before the grid moved. 0.3 has the cards following it closely, while
            // still making the photo unmistakably first.
            onStart: function () { gsap.delayedCall(0.3, startCards); } });
      } else {
        startCards();   // no photo on this layout — nothing for the cards to wait on
      }
    });
  });
})();

/* ---- Hardware devices (hardware-s3) — the two device cards, one after the other ----
   Cards only: the heading above them and the closing line below are left exactly as authored. That
   is why this is not a SECTIONS entry — that driver always animates the head it is pointed at.

   Through ScrollTrigger.batch rather than one timeline, because the pair sits side by side on desktop
   and stacks further down: side by side they enter together and stagger as a pair, stacked they each
   get their own trigger instead of the second one playing out of sight. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  var cards = document.querySelectorAll(".hardware-s3__card");
  if (!cards.length) return;

  gsap.registerPlugin(ScrollTrigger);
  gsap.set(cards, { autoAlpha: 0, y: 44 });

  ScrollTrigger.batch(cards, {
    start: "top 85%",
    once: true,
    onEnter: function (batch) {
      // only two of them, and they are tall — a wider gap than the small-tile grids elsewhere, so
      // the second reads as following the first rather than arriving alongside it
      gsap.to(rooyaTopDown(batch), {
        autoAlpha: 1, y: 0, duration: 0.9, ease: "power3.out", stagger: 0.28, overwrite: true
      });
    }
  });
})();

/* ---- Company offices (company-s3) — the two office cards, on their own trigger ----
   The section's head and intro columns run through the SECTIONS driver; the cards sit far below them
   and would otherwise animate off the bottom of the screen on that same trigger. They cannot be a
   second SECTIONS entry either: this block carries no h2 and no paragraph — the addresses are spans —
   so the driver has nothing to hang its text intro on. Hence a module of its own.

   Two gates, not one. Scrolling the cards into view is the obvious trigger, but on a tall screen the
   whole section is visible at once and BOTH triggers fire together, which had the cards racing the
   intro columns above them. So the cards also wait on rooyaAfterIntro, and play only once both
   conditions are met — in normal scrolling that is the scroll gate, on a tall screen it is the intro. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  var section = document.querySelector(".company-s3");
  var wrap = section && section.querySelector(".company-s3__offices");
  if (!wrap) return;
  var offices = wrap.querySelectorAll(".company-s3__office");
  if (!offices.length) return;
  var tags = wrap.querySelectorAll(".company-s3__office-tag");
  // the connector lines belong to this half too: they are drawn around the office cards, so they
  // wait for them rather than coming up with the intro copy a screen earlier
  var layer = section.querySelector(".company-s3__lines");

  gsap.registerPlugin(ScrollTrigger);

  gsap.set(offices, { autoAlpha: 0 });
  if (tags.length) gsap.set(tags, { autoAlpha: 0 });   // held back inside their card until it lands
  // hidden only from here, which is past both guards above — so with no GSAP or reduced motion on,
  // the lines are simply never touched and stay as authored
  if (layer) gsap.set(layer, { autoAlpha: 0 });

  // Still gated on fonts-ready, which is also when the SECTIONS driver used to publish this
  // section's _introTl. That intro is gone now — the copy above these cards no longer animates — so
  // rooyaAfterIntro below finds no timeline and releases immediately, leaving the scroll gate as the
  // only condition. Kept rather than removed: it costs nothing, and it is what makes the handshake
  // reappear on its own if the copy is ever given its intro back.
  rooyaFontsReady(function () {
    var inView = false, introDone = false;

    var tl = gsap.timeline({ paused: true });

    // London then Riyadh. The second card already rides 105px lower by design, so the stagger reads
    // as the step continuing rather than as two unrelated entrances.
    tl.fromTo(offices, { autoAlpha: 0, y: 48 },
      { autoAlpha: 1, y: 0, duration: 0.8, ease: "power3.out", stagger: 0.18 }, 0);

    // …and each city label slides out from the photo's left edge once its card is there
    if (tags.length) tl.fromTo(tags, { autoAlpha: 0, x: -18 },
      { autoAlpha: 1, x: 0, duration: 0.5, ease: "power3.out", stagger: 0.18 }, 0.45);

    // then the connector lines, once both photos are down. Plain opacity: they are anchored to the
    // composition and any movement would slide them off it.
    if (layer) {
      tl.fromTo(layer, { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.9, ease: "power2.out" }, 0.85);
      // …and the badges set off from the start of their paths at that same moment. Looked up at fire
      // time: the rider module publishes this hook after this timeline was built.
      tl.call(function () {
        if (layer._startRiders) layer._startRiders();
      }, null, 0.85);
    }

    var play = function () { if (inView && introDone) tl.play(0); };

    rooyaAfterIntro(section, function () { introDone = true; play(); });

    ScrollTrigger.create({
      trigger: wrap, start: "top 80%", once: true,
      onEnter: function () { inView = true; play(); }
    });
  });
})();

/* ---- Leadership grid (company-s4) — each row of faces animates as it arrives ----
   Nine cards over three rows, roughly 1300px of grid. On the intro's trigger the bottom two rows
   would play their entrance a screen and a half below the fold and be over before anyone saw them,
   so the grid is not a `cards` list on the section entry. ScrollTrigger.batch instead: it groups the
   cards that come into view together and staggers each group as it lands, which also means the row
   width sorts itself out — three across on desktop, two or one further down, no column maths here.

   The batch used to be wired up at 1.1 on the intro's timeline, so it started under the paragraph
   rather than after it. That copy no longer animates, so rooyaAfterIntro finds nothing to wait for
   and releases straight away — the 1.1 is now inert, and kept only so the pacing returns by itself if
   the intro is ever restored. What actually governs the grid is the batch: it checks what is already
   on screen the moment it is created, so the first row animates right then and each later row as it
   arrives. */
(function () {
  if (typeof gsap === "undefined" || !window.ScrollTrigger) return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  var section = document.querySelector(".company-s4");
  if (!section) return;
  var members = section.querySelectorAll(".company-s4__member");
  if (!members.length) return;

  gsap.registerPlugin(ScrollTrigger);
  gsap.set(members, { autoAlpha: 0, y: 40 });

  rooyaFontsReady(function () {
    rooyaAfterIntro(section, function () {
      ScrollTrigger.batch(members, {
        start: "top 88%",
        once: true,
        onEnter: function (batch) {
          gsap.to(rooyaTopDown(batch), {
            autoAlpha: 1, y: 0, duration: 0.65, ease: "power3.out", stagger: 0.1, overwrite: true
          });
        }
      });
    }, 1.1);
  });
})();

/* ---- Industries timeline (ind-s2) — the arrow rides the spine on scroll ----
   The travel is scrubbed, and between the hot points sits a same-value tween: the arrow simply holds
   still on a dot while you keep scrolling through that segment, then moves on to the next row. */
(function () {
  var section = document.querySelector(".ind-s2");
  if (!section) return;
  var rows = section.querySelector(".ind-s2__rows");
  var marker = section.querySelector(".ind-s2__marker");
  var spine = section.querySelector(".ind-s2__spine");
  var fill = section.querySelector(".ind-s2__spine-fill");
  var dots = [].slice.call(section.querySelectorAll(".ind-s2__dot"));
  if (!rows || !marker || dots.length < 2) return;

  // The rail runs from the FIRST hot point to the LAST one — never past either end of the content.
  // Pinned to the dots (not to the section box) so section padding, breakpoints or a changed row
  // simply move the whole rail with them; CSS can't know where those dots land.
  var fitSpine = function () {
    if (!spine) return;
    var secTop = section.getBoundingClientRect().top;
    var first = dots[0].getBoundingClientRect();
    var last = dots[dots.length - 1].getBoundingClientRect();
    spine.style.top = (first.top + first.height / 2 - secTop) + "px";
    spine.style.bottom = "auto";
    spine.style.height = Math.max(0, last.top - first.top) + "px"; // dot centre to dot centre
  };
  fitSpine();
  window.addEventListener("resize", fitSpine);
  window.addEventListener("load", fitSpine); // images settle → rows get their final heights

  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (typeof gsap === "undefined" || !window.ScrollTrigger || reduce) {
    if (marker) marker.style.display = "none"; // no scroll animation → no stranded arrow
    return;
  }
  gsap.registerPlugin(ScrollTrigger);

  // dot centres, as y offsets inside .ind-s2__rows (re-measured on every ScrollTrigger refresh, so
  // breakpoint reflows and late-loading images can't leave the arrow off its rail)
  var anchors = [];
  var pos = { y: 0 };  // where the timeline WANTS the arrow; what gets drawn is this, kept in view

  var measure = function () {
    fitSpine();
    var top = rows.getBoundingClientRect().top;
    var h = marker.offsetHeight;
    anchors = dots.map(function (d) {
      var r = d.getBoundingClientRect();
      return r.top - top + r.height / 2 - h / 2;
    });
    pos.y = anchors[0];
    draw();
  };

  var NAV = 150;  // clears the sticky 116px navbar, plus a little air
  var FOOT = 40;

  // The arrow's position is an offset into the CONTENT, so on a viewport shorter than a row the dot it
  // is resting on scrolls clean off the screen and takes the arrow with it. Hold it inside a band of
  // the viewport instead: on tall screens the dot is always within that band and this changes nothing,
  // on short ones the arrow waits at the edge of the screen rather than leaving it.
  var inView = function (y) {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var top = rows.getBoundingClientRect().top;
    var min = NAV - top;
    var max = vh - FOOT - marker.offsetHeight - top;
    if (max > min) y = Math.min(Math.max(y, min), max); // (a viewport too short for a band keeps y)
    // …but never past the rail's own ends, or the arrow would float off the line entirely
    return Math.min(Math.max(y, anchors[0]), anchors[anchors.length - 1]);
  };

  // place the arrow, trail the covered stretch of spine behind it, light up the dots it has reached.
  // All three read the DRAWN position, so the arrow, the green line and the dots can never disagree.
  var place = function (y) {
    gsap.set(marker, { y: y });
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle("is-active", y >= anchors[i] - 6);
    }
    if (fill && spine) {
      // both live inside the section, so their distance is the same at any scroll position
      var m = marker.getBoundingClientRect();
      var s = spine.getBoundingClientRect();
      fill.style.height = Math.max(0, Math.min(s.height, m.top + m.height / 2 - s.top)) + "px";
    }
  };

  // What actually gets painted trails the target instead of jumping to it, one frame at a time. That
  // is what smooths the two places where the motion would otherwise break: leaving/arriving at a dot,
  // and the moment the viewport band takes over from the dot (a hard clamp changes speed instantly).
  var SMOOTH = 0.12;   // approach per frame at 60fps — higher tracks tighter, lower glides longer
  var drawn = null;
  var painted = null;

  var draw = function () { if (anchors.length) { drawn = inView(pos.y); place(drawn); painted = drawn; } };

  gsap.ticker.add(function (time, deltaTime) {
    if (!anchors.length) return;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var r = section.getBoundingClientRect();
    if (r.bottom < -vh || r.top > vh * 2) return;   // nowhere near the screen — nothing to ease
    var target = inView(pos.y);
    if (drawn === null) drawn = target;
    // frame-rate independent, so the glide feels the same on a 60Hz and a 144Hz screen
    var f = 1 - Math.pow(1 - SMOOTH, (deltaTime || 16.67) / 16.67);
    drawn += (target - drawn) * f;
    if (Math.abs(target - drawn) < 0.05) drawn = target;
    if (drawn === painted) return;                  // settled, and the scroll hasn't moved the band
    place(drawn);
    painted = drawn;
  });

  measure();
  ScrollTrigger.addEventListener("refreshInit", measure);

  // Share of the scroll spent moving between two hot points, and parked on one. These two set the
  // arrow's SPEED, and not in an obvious way: the rail length and the scroll length are both fixed by
  // the layout, so every unit of scroll spent dwelling has to be made up by travelling faster. At the
  // old 1 : 0.75 the arrow only moved for 57% of the scroll and therefore ran at ~1.75x the speed of
  // the page — nearer 2.7x mid-segment, where sine.inOut peaks. Trimming the dwell is what slows it.
  var TRAVEL = 1;
  var DWELL = 0.4;   // travel is now ~71% of the scroll, so the arrow runs at ~1.4x

  // The scroll range is pinned DOT TO DOT, and that is what keeps the arrow at a steady height on
  // screen. Its viewport position moves by (arrow travel − page scroll), so it only holds still when
  // those two are equal over the section. Running the range across the whole of .ind-s2__rows made
  // them unequal: the rail is shorter than the block that contains it (the first and last dots sit
  // inside it, not on its edges), so the arrow covered ~94% of a scroll it was measured against and
  // crept upward the whole way down — mid-screen in the first row, up under the navbar by the last.
  // First dot to last dot is exactly the rail's own length, so the ratio is 1 and there is no drift.
  //
  // HOLD is the height it then keeps: the range starts when the first dot reaches that line and ends
  // when the last dot does. Within each row it still rides up while parked on a dot and comes back
  // down as it travels to the next — that swing is the effect; only the creep was the bug.
  var HOLD = "top 45%";

  // No onUpdate here, and no scroll/resize listeners either: the ticker above already repaints every
  // frame, and anything writing the position straight from a scroll event would step over the easing.
  var tl = gsap.timeline({
    scrollTrigger: {
      trigger: dots[0],
      start: HOLD,
      endTrigger: dots[dots.length - 1],
      end: HOLD,
      scrub: 0.7,            // the per-frame easing carries the rest of the smoothing now
      invalidateOnRefresh: true
    }
  });

  // …and the other half of the "sometimes too fast": the gaps between dots are nothing like equal — a
  // row with four feature cards stands far taller than one with two — but every segment used to get
  // the same duration regardless. The arrow therefore sprinted down the long rows and crawled the
  // short ones. Weighting each travel by its own distance against the average holds one speed the
  // whole way down. (Measured once, at build; a later reflow moves the anchors via invalidateOnRefresh
  // and only the pacing goes slightly stale, never the positions.)
  var span = anchors[anchors.length - 1] - anchors[0];
  var avg = span / (dots.length - 1) || 1;

  dots.forEach(function (d, i) {
    if (!i) return; // the arrow starts on the first dot
    var to = function () { return anchors[i]; }; // function-based → re-read after invalidateOnRefresh
    var travel = TRAVEL * ((anchors[i] - anchors[i - 1]) / avg);
    // sine leaves and reaches a dot gentler than a quad ease, which is what the stop-and-go between
    // hot points needs — the arrow rolls away rather than snapping off the dot
    tl.to(pos, { y: to, duration: travel, ease: "sine.inOut" })
      .to(pos, { y: to, duration: DWELL });
  });

  draw();                                        // land on the first dot without gliding into it
  ScrollTrigger.addEventListener("refresh", draw); // layout changed → snap, don't animate the jump
})();

/* ---- Industries timeline (ind-s2) — the row media, at every width ----
   Each media block animates as it scrolls into view: the photo fades up, and where a row carries a
   `--additional` overlay (the event card, the score badge) that slides in from the right just behind
   it — travelling leftwards onto the photo it belongs to.

   This used to be gated to (max-width: 900), on the reasoning that the arrow riding the spine was
   movement enough on desktop; it left seven static photos down the desktop page instead. Ungated
   now, so the rows read the same at any width. The seven rows each get their own trigger — the
   section runs several screens deep, so one trigger would play most of them out of sight. */
(function () {
  var medias = document.querySelectorAll(".ind-s2__media");
  if (!medias.length) return;
  var imgs = document.querySelectorAll(".ind-s2__media img");

  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (typeof gsap === "undefined" || !window.ScrollTrigger || reduce) {
    // CSS holds these hidden through the first paint (see .has-js in header.php and industries.scss).
    // Nothing is going to animate them now, so hand them straight back.
    Array.prototype.forEach.call(imgs, function (el) { el.style.visibility = "visible"; });
    return;
  }
  gsap.registerPlugin(ScrollTrigger);

  // Hidden here as well as in CSS, and it has to be both. The CSS covers the gap before this file
  // runs — these sit below the fold, but a photo that pops in and then re-animates is visible even
  // there on a quick scroll. This inline set is what the tweens then animate away from, and it is
  // also what lets the CSS rule be lifted: GSAP reveals with `visibility: inherit`, which resolves
  // against .ind-s2__media and so beats the rule for good.
  gsap.set(imgs, { autoAlpha: 0 });

  var PHOTO = 0.9;
  var EXTRA = 0.5;
  var isExtra = function (el) { return el.className.indexOf("ind-s2__media-img-additional") > -1; };
  var isArc = function (el) { return el.className.indexOf("ind-s2__media-arc") > -1; };

  Array.prototype.forEach.call(medias, function (media) {
    var kids = Array.prototype.slice.call(media.children);
    var extras = kids.filter(isExtra);
    var arcs = kids.filter(isArc);
    var photos = kids.filter(function (el) { return !isExtra(el) && !isArc(el); });
    if (!kids.length) return;

    var tl = gsap.timeline({
      scrollTrigger: { trigger: media, start: "top 85%", toggleActions: "play none none none" }
    });

    // 0.9s between photos is the spacing for a picture and the card that lands on it, and every row
    // but one holds a single picture. The public-sector row holds three tiles of ONE composition, and
    // at that spacing they would arrive nearly three seconds apart — so a row with several photos
    // steps through them quickly instead.
    var step = photos.length > 2 ? 0.16 : PHOTO;

    if (photos.length) tl.fromTo(photos, { autoAlpha: 0, y: 50 },
      { autoAlpha: 1, y: 0, duration: PHOTO, ease: "power3.out", stagger: step });

    if (extras.length) tl.fromTo(extras, { autoAlpha: 0, x: 50 },
      { autoAlpha: 1, x: 0, duration: EXTRA, ease: "power3.out", stagger: EXTRA }, "-=0.3");

    // The arc is a flourish, not a picture, so it does not fade up with the tiles it sits above —
    // it swings. Pivoting on its own bottom centre, it starts small and tipped and rights itself
    // with an overshoot, which is the entrance the same brand shape already gets in the industries
    // masthead and the home-s6 carousel.
    //
    // xPercent: -50 is carried through BOTH states deliberately. CSS centres this element with
    // `left: 50%` + `translateX(-50%)`, and GSAP rewrites the whole transform the moment it animates
    // one part of it — leave the centring out and the arc jumps half its width to the right for the
    // length of the tween.
    if (arcs.length) tl.fromTo(arcs,
      { xPercent: -50, autoAlpha: 0, scale: 0.5, rotation: -14, transformOrigin: "50% 100%" },
      { xPercent: -50, autoAlpha: 1, scale: 1, rotation: 0, duration: 0.8, ease: "back.out(1.5)" },
      0.55);
  });
})();



/* ---- Company FAQ accordion ----
   Plain divs, so the whole disclosure is this script's job: the panel rests at height 0 in CSS and
   is tweened open and shut from here. The question carries role="button" + tabindex + aria-expanded,
   which is what buys back the keyboard and screen-reader behaviour a <summary> used to provide — so
   Enter and Space are handled explicitly alongside the click. Reduced motion still toggles, just
   without the tween. */
(function () {
  var grid = document.querySelector(".company-s5__grid");
  if (!grid) return;

  var items = grid.querySelectorAll(".company-s5__item");
  if (!items.length) return;

  var DUR = 260;
  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  Array.prototype.forEach.call(items, function (item) {
    var q = item.querySelector(".company-s5__q");
    var panel = item.querySelector(".company-s5__answer");
    if (!q || !panel) return;

    var busy = false;
    var timer;

    var settle = function () {
      clearTimeout(timer);
      panel.removeEventListener("transitionend", onEnd);
      panel.style.transition = "";
      // an open panel is left on `auto`, not on the pixel height it was tweened to, so re-wrapping
      // text (a resize, a font swap) can change its height freely afterwards
      panel.style.height = item.classList.contains("is-open") ? "auto" : "";
      busy = false;
    };

    function onEnd(e) {
      if (e.target !== panel || e.propertyName !== "height") return;
      settle();
    }

    var toggle = function () {
      if (busy) return;
      var opening = !item.classList.contains("is-open");

      item.classList.toggle("is-open", opening);
      q.setAttribute("aria-expanded", opening ? "true" : "false");

      if (reduce) {
        panel.style.height = opening ? "auto" : "";
        return;
      }

      busy = true;
      // The start height has to be COMMITTED with no transition attached, otherwise setting it is
      // itself an animation and the reflow below samples a value that is still in flight — which is
      // what made the first frames stutter. So: kill the transition, set `from`, flush the layout,
      // only then arm the transition and set `to`.
      var from = opening ? 0 : panel.scrollHeight;
      var to = opening ? panel.scrollHeight : 0;

      panel.style.transition = "none";
      panel.style.height = from + "px";
      void panel.offsetHeight;
      panel.style.transition = "height " + DUR + "ms cubic-bezier(.4,0,.2,1)";
      panel.style.height = to + "px";

      panel.addEventListener("transitionend", onEnd);
      timer = setTimeout(settle, DUR + 80);   // fallback in case the event is dropped
    };

    q.addEventListener("click", toggle);
    q.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
        e.preventDefault();   // Space would otherwise scroll the page
        toggle();
      }
    });
  });
})();

/* ---- Book a demo — the card fades up on load ----
   Straight fade-up, nothing staged: the card is the whole page here and there is no copy beside it
   to sequence against. The progress dots follow it rather than being left out — they sit right under
   the card and would otherwise be the one thing already on screen while it arrives.

   Safe against the wizard below, which grows the card between steps: that runs on a CSS height
   transition and never touches transform, so the two cannot write over each other. */
(function () {
  var card = document.querySelector(".demo__card");
  if (!card) return;
  var dots = document.querySelector(".demo__dots");

  var pieces = [card, dots].filter(Boolean);

  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (typeof gsap === "undefined" || reduce) {
    // CSS has these hidden for the first paint (see .has-js in header.php and book-a-demo.scss).
    // Nothing is going to animate them now, so hand them straight back rather than leaving the page
    // blank — this is also the escape hatch if GSAP's CDN never answers.
    pieces.forEach(function (el) { el.style.visibility = "visible"; });
    return;
  }

  gsap.set(pieces, { autoAlpha: 0 });   // matches the CSS, and gives the tweens something to run from

  rooyaFontsReady(function () {
    var tl = gsap.timeline({ defaults: { ease: "power3.out" } });
    tl.fromTo(card, { autoAlpha: 0, y: 44 }, { autoAlpha: 1, y: 0, duration: 0.9 }, 0);
    if (dots) tl.fromTo(dots, { autoAlpha: 0, y: 14 }, { autoAlpha: 1, y: 0, duration: 0.5 }, 0.35);
  });
})();

/* ---- Company "About Rooya" — two team photos in one frame ----
   `rewind` rather than `loop`: with only two slides Swiper's loop mode has to reorder them on every
   transition, and the one thing it buys — arrows that never dead-end — is exactly what rewind gives
   already, without touching the DOM. */
(function () {
  var el = document.querySelector(".company-s1__swiper");
  if (!el || typeof Swiper === "undefined") return;

  // Content that moves on its own is exactly what this setting asks to be spared, and the two photos
  // are equals — nobody misses anything by being left on the first.
  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  var sw = new Swiper(el, {
    speed: 500,
    rewind: true,
    navigation: { prevEl: ".company-s1__prev", nextEl: ".company-s1__next" },
    //pagination: { el: ".company-s1__dots", clickable: true },
    autoplay: reduce ? false : {
      delay: 5000,
      // Once someone works an arrow or a dot the carousel is theirs and stays put. Sliding a photo
      // out from under a person who just chose it is the whole reason auto-advance gets a bad name,
      // and it is also what WCAG's "pause, stop, hide" is after.
      disableOnInteraction: true,
      pauseOnMouseEnter: true
    }
  });

  // Hold the timer until the section is actually on screen. Started at page load it would have
  // advanced once or twice before anyone scrolled this far, so the slider would introduce itself
  // mid-sequence — arriving on the second photo with the second dot already lit.
  if (!reduce && sw.autoplay && window.IntersectionObserver) {
    sw.autoplay.stop();

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        sw.autoplay.start();
        io.disconnect();                          // first arrival is enough; it never needs re-arming
      });
    }, { threshold: 0.35 });

    io.observe(el);
  }
})();

/* ---- Closing CTA — hand the address to the wizard WITHOUT putting it in the URL ----
   The bar is a plain GET form aimed at /book-a-demo/, so it still works with JavaScript off. Where JS
   runs this upgrades it: the address goes into sessionStorage and the browser navigates to the clean
   URL instead. Three third-party trackers measure that page, and a query string reaches every one of
   them — as well as the server log, the browser history and the Referer header. sessionStorage goes
   nowhere and dies with the tab. */
(function () {
  var forms = document.querySelectorAll(".home-s10__form");
  if (!forms.length) return;

  Array.prototype.forEach.call(forms, function (form) {
    // `submit` only fires once the browser's own validation has passed, so the address is already
    // well-formed here and there is no second check to keep in step with the input's type.
    form.addEventListener("submit", function (e) {
      var field = form.querySelector("input[type='email']");
      var value = field ? field.value.trim() : "";
      if (!value) return;

      // Storage can be unavailable — a private window, or site data blocked. Fall through to the
      // plain GET rather than swallowing the click: a working form that puts the address in the URL
      // beats a button that does nothing.
      try {
        window.sessionStorage.setItem("rooya:demo-email", value);
      } catch (err) {
        return;
      }

      e.preventDefault();
      window.location.href = form.action;
    });
  });
})();

/* ---- Book a demo — five steps on one page ----
   Every step is already in the DOM; this only moves which one carries .is-active. Nothing navigates
   and nothing is re-rendered, so answers survive stepping backwards and forwards. Steps 2–4 are
   pick-one: the tile writes into the hidden input beside it and advances, which is also what lets a
   revisit show the earlier choice still selected. */
(function () {
  var form = document.getElementById("demo-form");
  if (!form) return;

  var steps = form.querySelectorAll("[data-demo-step]");
  if (!steps.length) return;

  // The closing CTA hands the address over before step 1 renders — asking for it a second time reads
  // as the form having lost it. Two sources, in this order: sessionStorage, written by the CTA when
  // JavaScript is available, and ?email=, the plain-GET fallback for when it is not.
  // Set as a property, never as markup, and only over an empty field so a value the browser restored
  // on a back-navigation is not clobbered.
  (function () {
    var field = form.querySelector("input[type='email']");
    if (!field || field.value) return;

    var value = "";
    try {
      value = window.sessionStorage.getItem("rooya:demo-email") || "";
    } catch (err) {
      value = "";                                 // private window, or site data blocked
    }

    if (!value) {
      var hit = /[?&]email=([^&]*)/.exec(window.location.search);
      if (hit) {
        try {
          value = decodeURIComponent(hit[1].replace(/\+/g, " "));
        } catch (err2) {
          value = "";                             // malformed %-escape in the query string
        }

        // Take it back out of the address bar. Analytics has already fired by this point, so on this
        // path it is damage limitation only — the JavaScript path never puts it in the URL at all.
        if (window.history && history.replaceState) {
          var rest = window.location.search
            .replace(/([?&])email=[^&]*(&|$)/, "$1")
            .replace(/[?&]$/, "");
          history.replaceState(null, "", window.location.pathname + rest + window.location.hash);
        }
      }
    }

    value = value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return;

    // Both properties, deliberately. Wherever a page-cache plugin is active Contact Form 7 ships
    // `cached: 1` and calls form.reset() on window.load, so anything written to `value` alone is
    // wiped a moment after it lands — which is exactly what happened on the Breeze-backed staging
    // server. reset() restores each control to its DEFAULT, so writing the default is what actually
    // survives, and it does not depend on out-ordering a listener CF7 registers later than ours.
    field.defaultValue = value;
    field.value = value;
  })();

  var done = form.querySelector("[data-demo-done]");
  var dots = document.querySelectorAll("[data-demo-dots] .demo__dot");
  var leave = document.querySelector("[data-demo-leave]");
  var index = 0;
  var busy = false;

  var OUT = 180;   // must match the .is-out fade in book-a-demo.scss
  var GROW = 300;  // ...and the height tween below

  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  var paint = function () {
    Array.prototype.forEach.call(dots, function (dot, n) {
      dot.classList.toggle("is-active", n === index);
    });
    // step 1 has nothing behind it, so the only Back on the page stays out of the way there
    if (leave) leave.classList.toggle("is-visible", index > 0);

    // Only pull the card back into view when its top has actually scrolled off — steps differ a lot
    // in height, and scrolling on every change would yank the page around for no reason.
    var box = form.getBoundingClientRect();
    if (box.top < 0) window.scrollTo({ top: window.pageYOffset + box.top - 100, behavior: "smooth" });
  };

  var show = function (i) {
    var to = Math.max(0, Math.min(i, steps.length - 1));
    if (busy || to === index) return;

    if (reduce) {
      steps[index].classList.remove("is-active");
      steps[to].classList.add("is-active");
      index = to;
      paint();
      return;
    }

    busy = true;
    var out = steps[index];
    out.classList.add("is-out");

    setTimeout(function () {
      // measured while the OLD step is still laid out — this is the height to tween away from
      var h0 = form.offsetHeight;

      out.classList.remove("is-active", "is-out");
      steps[to].classList.add("is-active", "is-in");

      // ...and this one with the new step in place but the card still free to size itself
      var h1 = form.scrollHeight;

      // Same discipline as any height tween: commit the start value with NO transition attached,
      // flush the layout, and only then arm the transition — otherwise the reflow samples a value
      // that is already in flight and the first frames stutter.
      form.style.transition = "none";
      form.style.height = h0 + "px";
      void form.offsetHeight;
      form.style.transition = "height " + GROW + "ms cubic-bezier(.4,0,.2,1)";
      form.style.height = h1 + "px";

      void steps[to].offsetHeight;          // commit the pre-enter state before releasing it
      steps[to].classList.remove("is-in");

      index = to;
      paint();

      setTimeout(function () {
        form.style.transition = "";
        form.style.height = "";             // back to auto, so the card can reflow freely afterwards
        busy = false;
      }, GROW + 20);
    }, OUT);
  };

  // only step 1 has anything to check; the rest are either a single choice or entirely optional
  var validate = function (step) {
    var ok = true;
    // CF7 marks its required controls with aria-required and validates server-side; it never emits
    // the `required` attribute (its forms carry novalidate). Match both so this gate keeps working.
    Array.prototype.forEach.call(step.querySelectorAll("input[required], input[aria-required='true']"), function (input) {
      var value = input.value.trim();
      var bad = !value || (input.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value));
      input.closest(".demo__field").classList.toggle("has-error", bad);
      if (bad && ok) input.focus();
      if (bad) ok = false;
    });
    return ok;
  };

  var next = function () {
    if (!validate(steps[index])) return;
    show(index + 1);
  };

  form.addEventListener("click", function (e) {
    var advance = e.target.closest("[data-demo-next]");
    if (advance) { next(); return; }

    var option = e.target.closest("[data-demo-option]");
    if (!option) return;

    var step = option.closest("[data-demo-step]");
    // CF7's [hidden] tag takes class: but not arbitrary data-* attributes, so the store is found by
    // either marker — data-demo-value (plain markup) or .demo-value (the CF7 tag's class).
    var store = step.querySelector("[data-demo-value], .demo-value");
    if (store) store.value = option.getAttribute("data-demo-option");

    Array.prototype.forEach.call(step.querySelectorAll("[data-demo-option]"), function (tile) {
      tile.classList.toggle("is-selected", tile === option);
    });

    show(index + 1);
  });

  // clearing the flag as soon as the field is touched, rather than waiting for the next attempt
  form.addEventListener("input", function (e) {
    var field = e.target.closest(".demo__field");
    if (field) field.classList.remove("has-error");
  });

  var finish = function () {
    Array.prototype.forEach.call(steps, function (step) { step.classList.remove("is-active"); });
    if (done) done.classList.add("is-active");
    if (leave) leave.classList.remove("is-visible");
    var wrap = document.querySelector("[data-demo-dots]");
    if (wrap) wrap.style.display = "none";
  };

  // Contact Form 7 binds its own submit listener to the form and posts over fetch. Ours has to run
  // FIRST so a mid-flow submit (Enter on step 1 means "continue", not "send") never reaches it — and
  // a second listener on the form itself could not guarantee that, because at the target element
  // both phases fire in registration order and CF7 registers on DOMContentLoaded. Capturing on
  // document runs a whole phase earlier, so stopPropagation() here is deterministic.
  document.addEventListener("submit", function (e) {
    if (e.target !== form) return;

    var last = index === steps.length - 1;
    if (last && validate(steps[index])) return; // hand it over to CF7

    e.preventDefault();
    e.stopPropagation();
    if (!last) next();
  }, true);

  // CF7 announces the outcome on the form. Only swap in the "thanks" panel once the mail really went,
  // so a failed send leaves the user on step 5 with CF7's own error message.
  form.addEventListener("wpcf7mailsent", finish);

  if (leave) leave.addEventListener("click", function () { show(index - 1); });

  paint();   // step 1: hides the Back, lights the first dot
})();

/* ---- Safety net for the below-the-fold intro holds in default.scss ----
   Those decorations are held `visibility: hidden` from the first paint so they cannot flash at full
   strength before GSAP turns up. Normally the SECTIONS driver takes them over within milliseconds and
   the intro reveals them. If GSAP never arrives — CDN blocked, offline, a script error upstream — no
   one would ever un-hide them and the sections would render permanently incomplete. So: if the
   library is not here by the time this file ends, hand them straight back. */
(function () {
  // Reduced motion counts as "no intro" here too. Every module that would reveal these pieces bails
  // on it, so without this the four selectors below stayed hidden for good on that setting — the CSS
  // hold is only ever safe while something is guaranteed to undo it.
  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!reduce && typeof gsap !== "undefined" && window.ScrollTrigger) return;
  var held = document.querySelectorAll(
    ".platform-s4__shape, .platform-s5__ring, .platform-s6__link, .company-s2__lines"
  );
  Array.prototype.forEach.call(held, function (el) { window.rooyaRelease(el); });
})();
